import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ModuleAuthModal from '../shared/components/ModuleAuthModal';
import TeachAIModal from '../components/TeachAIModal';
import AtlasValidationPreview, { ATLAS_FIELDS, VERIFICATION_REQUIRED_FIELDS, getATLASFields, useFieldLabel } from '../components/AtlasValidationPreview';
import AtlasStructuredForm from '../components/AtlasStructuredForm';
import { isJunkPrediction } from '../components/mlPredictionSanitize';
import { appendFieldError } from '../components/fieldErrorMerge';
import { GRPC_FIELD_PATH_ALIASES } from '../components/grpcFieldPathAliases';
import { applyPartyEori } from '../components/partyEori';
import { expandProcedureFanout } from '../components/procedureFanout';
import SubmissionPreviewDialog from '../components/SubmissionPreviewDialog';
import FieldValidator from '../components/FieldValidator';
import MLPredictionCell from '../components/MLPredictionCell';
import MLPredictionsSidebar, { MLPredictionsResult, ConfidenceFilter } from '../components/MLPredictionsSidebar';
import { mlToAtlasPath, atlasToMlField, HIGH_CONFIDENCE, MEDIUM_CONFIDENCE } from '../components/mlPredictionCategories';
import {
  startMlBlackout, isMlBlackoutActive,
  setApplyingPredictions, isApplyingPredictions,
} from '../components/mlCoordination';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
Paper,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Badge,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
  FormLabel,
  CircularProgress,
  useTheme,
  Fab,
  Collapse,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PlayArrow as ProcessIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  CheckCircle as SuccessIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  SelectAll as SelectAllIcon,
  DeselectOutlined as DeselectIcon,
  Logout,
  Home as HomeIcon,
  Security,
  Highlight as HighlightIcon,
  Shield as ShieldIcon,
  VerifiedUser as ComplianceIcon,
  TableChart as TableChartIcon,
  ViewModule as ViewModuleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ManageAccounts as ManageAccountsIcon,
  Psychology as PsychologyIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  History as HistoryIcon,
  School as SchoolIcon,
  Apps as AppsIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuthStore } from '../store/authStore';
import { useAppStore } from '../store/appStore';
import authService from '../shared/services/authService';
import api from '../shared/services/api';
import DocumentAnnotator from '../features/magic-marker/MagicMarker/DocumentAnnotator';
import complianceService from '../services/complianceService';
import codelistService from '../services/codelistService';
import ComplianceReport from '../features/compliance/Compliance/ComplianceReport';
import { ComplianceReportCard } from '../features/compliance/Compliance/ComplianceReportCard';
import { useCompliancePolling } from '../hooks/useCompliancePolling';
import { useTranslation } from '../store/languageStore';
import { translations } from '../i18n';
import { colors, brand } from '../theme';
import { stepperIcons, uiIcons, actionIcons } from '../assets/icons/nav';
import NavIcon from '../shared/components/NavIcon';
import { convertToDisplayValue, convertToBackendValue } from '../utils/backendValueConverter';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { AutoAwesome as AutoAwesomeIcon, Assessment as AssessmentIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import customsAuditService, { AuditReport, Finding as AuditFinding } from '../services/customsAuditService';
import AuditDashboard from '../components/AuditReport/AuditDashboard';
import AuditFindings from '../components/AuditReport/AuditFindings';
import AuditApplyDialog from '../components/AuditReport/AuditApplyDialog';
import AuditHistory from '../components/AuditReport/AuditHistory';
import { autoClassifyAllPositions, classifyBatch } from '../services/tariffClassifierService';
import {
  CorrelationValidator,
  CorrelationFeedbackPanel,
  ATLASValidationPanel,
  ComprehensiveCorrelationResponse,
} from '../components/Correlation';
import documentSetService from '../features/cross-document/documentSetService';
import MergeStatusBanner from '../features/cross-document/components/MergeStatusBanner';
import MergeConflicts from '../features/cross-document/components/MergeConflicts';
import { deriveFieldSources } from '../features/cross-document/mergeProvenance';
import type { MultiDocumentProcessingResult } from '../features/cross-document/documentSetService';
import StepBar, { WorkflowStep } from '../components/DocumentProcessor/StepBar';
import { useModuleDrawerStore } from '../store/moduleDrawerStore';
import { useNavGuardStore } from '../store/navGuardStore';

// Add keyframe animation for spinning
const spinKeyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes procSweep {
    from { transform: translateX(-100%); }
    to   { transform: translateX(100%); }
  }
  @keyframes procGlow {
    0%, 100% { opacity: 0.55; }
    50%      { opacity: 1; }
  }
`;

/**
 * Progress model for the extraction run.
 *
 * Two things have to be true at once, and the first version got only one of
 * them right:
 *
 *  1. It must be ANCHORED to reality. `processing.progress` is a synthetic
 *     timer (+1%/150ms, parked at 80%) that reaches 80% in 12s while real runs
 *     take 30-60s — cold PP-OCRv6 alone is ~30s/page. Unusable.
 *  2. It must never STALL. Deriving position from step labels alone fails here
 *     because the whole backend pipeline (upload + OCR + LLM + grounding) sits
 *     inside a single await with no intermediate step change. That parked the
 *     bar at one value for 30-60s and then fired every remaining milestone at
 *     once — "stuck at 25%, then instantly complete".
 *
 * So: step labels set the BAND (hard, real anchors), and elapsed time eases
 * within the band. The easing is asymptotic — it approaches the band ceiling
 * without reaching it — so a slower-than-expected run keeps creeping instead
 * of stopping, and only a real milestone can cross into the next band.
 *
 * EXTRACT owns 18-90 because it is where nearly all the wall-clock goes.
 */
const PROC_BANDS: { test: RegExp; from: number; to: number }[] = [
  { test: /authenticat/i,                     from: 0,  to: 8 },
  { test: /uploading/i,                       from: 8,  to: 18 },
  { test: /processing \d+ file|processing file/i, from: 18, to: 90 },
  { test: /extracted data/i,                  from: 90, to: 93 },
  { test: /merging/i,                         from: 93, to: 96 },
  { test: /validating/i,                      from: 96, to: 98 },
];

// Measured per-file wall clock: ~12-19s warm, 40-60s cold (first sight of a
// document, when PP-OCRv6 actually runs at ~30s/page). 30s is the middle of
// that range, so warm runs finish ahead of the curve and cold ones ride it.
const PROC_EXPECTED_MS_PER_FILE = 30_000;

/**
 * 0..1, decelerating, never reaching 1.
 *
 * k=1.2 was picked against the measured spread rather than by feel: it climbs
 * 26/33/42/50/58/68/75/82% at 3/6/10/15/20/30/40/55s, which reads as steady
 * movement for a warm run and still visibly advances through a cold one. A
 * sharper curve (the first attempt used k≈1.67) burned 33% in the first three
 * seconds and then crawled 85->88% for twenty — the same dead feel as being
 * stuck, just at a higher number.
 */
const easeAsymptotic = (elapsedMs: number, expectedMs: number): number =>
  1 - Math.exp(-1.2 * elapsedMs / Math.max(1, expectedMs));

const deriveProcessingProgress = (
  step: string,
  elapsedMs: number,
  fileCount: number,
): number | null => {
  const s = (step || '').toLowerCase();
  if (!s) return null;
  if (/success|complete[d]?$|processed successfully/.test(s)) return 100;

  const band = PROC_BANDS.find(b => b.test.test(s));
  if (!band) return null;
  const span = band.to - band.from;

  // Inside EXTRACT, a real "(N of M complete)" counter is the strongest signal
  // available: pin the floor to completed files, then ease across the file
  // currently in flight so the bar still moves between completions.
  const counter = s.match(/\((\s*\d+)\s+of\s+(\d+)/);
  if (counter && band.to === 90) {
    const done = Number(counter[1]);
    const total = Math.max(1, Number(counter[2]));
    const slice = span / total;
    const eased = easeAsymptotic(elapsedMs, PROC_EXPECTED_MS_PER_FILE) * slice;
    return Math.min(band.to, band.from + done * slice + eased);
  }

  const expected = band.to === 90
    ? PROC_EXPECTED_MS_PER_FILE * Math.max(1, fileCount)
    : 1_500;
  return band.from + easeAsymptotic(elapsedMs, expected) * span;
};

interface Module {
  id: string;
  name: string;
  description: string;
  endpoint_url: string;
  is_active: boolean;
  enabled?: boolean;
  data_structure: any;
  auth_status?: 'authenticated' | 'expired' | 'unauthenticated';
  // Access control fields
  user_has_access?: boolean;
  can_process?: boolean;
  can_read?: boolean;
  can_configure?: boolean;
  requires_admin_approval?: boolean;
}

interface UploadedFile {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  extractedData?: any;
  fileId?: string;
  transactionId?: string;
  complianceCheck?: any;
}

interface ProcessingState {
  isProcessing: boolean;
  currentStep: string;
  progress: number;
  extractedData: any;
  selectedModule: Module | null;  // Module used for document processing (e.g., BEO ATLAS)
  complianceModule: Module | null;  // Module used for compliance screening (e.g., BEO Sanctions)
  fieldSelections: Record<string, boolean>;
  complianceReport?: any;
  runComplianceCheck?: boolean;
}

interface AuthDialogData {
  open: boolean;
  module: Module | null;
  credentials: {
    username: string;
    password: string;
  };
}

const DocumentProcessing: React.FC = () => {
  const theme = useTheme();
  // Info notifications styled to the brand theme (orange) instead of MUI's
  // default blue, so they read as part of the app rather than a generic alert.
  const themedInfoAlertSx = {
    maxWidth: 800,
    width: '100%',
    color: theme.palette.primary.dark,
    backgroundColor: `${theme.palette.primary.main}14`,
    border: `1px solid ${theme.palette.primary.main}40`,
    '& .MuiAlert-icon': { color: theme.palette.primary.main },
    '& .MuiAlert-message': { fontWeight: 400 },
  };
  const { user, logout } = useAuthStore();
  const { setSelectedModule } = useAppStore();
  const setNavGuardReason = useNavGuardStore(s => s.setReason);
  const navigate = useNavigate();
  const location = useLocation();
  // Use translation-aware field label hook
  const getFieldLabel = useFieldLabel();
  const [modules, setModules] = useState<Module[]>([]);
  const [modulesLoaded, setModulesLoaded] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [processing, setProcessing] = useState<ProcessingState>({
    isProcessing: false,
    currentStep: '',
    progress: 0,
    extractedData: null,
    selectedModule: null,  // For document processing (BEO ATLAS)
    complianceModule: null,  // For sanctions screening (BEO Sanctions)
    fieldSelections: {},
    runComplianceCheck: false,
  });
  const [authDialog, setAuthDialog] = useState<AuthDialogData>({
    open: false,
    module: null,
    credentials: { username: '', password: '' },
  });
  // Switch User flow state
  const isSwitchUserRef = useRef(false);
  const originalExtractedDataRef = useRef<any>(null);
  const [switchUserConfirmDialog, setSwitchUserConfirmDialog] = useState<{
    open: boolean;
    pendingModule: Module | null;
    pendingUserDetails: any | null;
  }>({ open: false, pendingModule: null, pendingUserDetails: null });
  const [authRequiredDialog, setAuthRequiredDialog] = useState<{
    open: boolean;
    moduleId: string | null;
    moduleName: string;
    message: string;
  }>({
    open: false,
    moduleId: null,
    moduleName: '',
    message: ''
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showMagicMarker, setShowMagicMarker] = useState(false);
  const [selectedFileForAnnotation, setSelectedFileForAnnotation] = useState<UploadedFile | null>(null);
  // Upload panel: which file's preview is open below the slider (null = none)
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const [showTeachAI, setShowTeachAI] = useState(false);
  const [showComplianceReport, setShowComplianceReport] = useState(false);
  // Holds the specific compliance report currently shown in the detail dialog.
  // Decouples the dialog from `processing.complianceReport` so we can display
  // any report from `allComplianceReports` (multi-file flow).
  const [activeComplianceReport, setActiveComplianceReport] = useState<any>(null);
  // Floating "Compliance results" popup that lists every per-file report so
  // the user can still reach them after leaving the upload step.
  const [showAllComplianceDialog, setShowAllComplianceDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewSubmissionData, setPreviewSubmissionData] = useState<any>(null);
  const [lastTransaction, setLastTransaction] = useState<{
    transactionId: string;
    filesProcessed: number;
    moduleName: string;
    apiResponse?: any;
  } | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'form'>('form'); // Form View is default
  const [selectedProcessType, setSelectedProcessType] = useState<string>(() => {
    try {
      const stored = localStorage.getItem('selectedProcessType');
      if (stored === 'ausfuhr' || stored === 'einfuhr' || stored === 'eas') return stored;
    } catch { /* ignore */ }
    return 'ausfuhr';
  });
  // Variant under the active sub-process (e.g. 'ausfuhr.standard' under
  // 'ausfuhr'). ModuleDrawer owns the selection and the localStorage write;
  // we mirror per sub-item id so the form view and ATLAS submission payload
  // can branch on the variant without coupling to the drawer.
  const [selectedVariantByType, setSelectedVariantByType] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem('selectedSubItemChildIds');
      if (raw) return JSON.parse(raw) || {};
    } catch { /* ignore */ }
    return {};
  });
  const [showTariffClassifier, setShowTariffClassifier] = useState(false);
  const [classifierFieldPath, setClassifierFieldPath] = useState<string>('');
  const [classifyingAll, setClassifyingAll] = useState(false);
  // Tracks field paths auto-filled by AI HS code classifier (shown as label in form)
  const [aiClassifiedHSCodes, setAiClassifiedHSCodes] = useState<Set<string>>(new Set());
  const [sendPartialData, setSendPartialData] = useState<boolean>(false);  // Default to false so gRPC validation runs on form load
  const [moduleSelectionLocked, setModuleSelectionLocked] = useState(false);  // Prevent module switching mid-upload
  const [showOptionalPositionFields, setShowOptionalPositionFields] = useState<Record<number, boolean>>({});  // Track optional fields visibility per line item
  const [auditReport, setAuditReport] = useState<AuditReport | null>(null);
  const [isAuditing, setIsAuditing] = useState(false);
  const [showAuditResults, setShowAuditResults] = useState(false);
  const [appliedFixes, setAppliedFixes] = useState<Map<string, { originalValue: string; newValue: string }>>(new Map());
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [expandedAuditArea, setExpandedAuditArea] = useState<string | undefined>();
  const { t, tString, currentLanguage } = useTranslation();

  // Helper: translate a field path to a human-readable label using i18n atlasFieldPaths
  const translateFieldPath = (fieldPath: string): string => {
    // Access atlasFieldPaths directly (keys contain dots, so tString won't work)
    const fieldPaths = (translations[currentLanguage] as any)?.atlasFieldPaths;
    if (fieldPaths && fieldPaths[fieldPath]) {
      return fieldPaths[fieldPath];
    }
    // For position fields, extract base field and add item number
    const posMatch = fieldPath.match(/^position\[(\d+)\]\.(?:verpackung\[\d+\]\.)?(.+)$/);
    if (posMatch) {
      const posIndex = parseInt(posMatch[1], 10) + 1;
      const baseField = posMatch[2];
      if (fieldPaths && fieldPaths[baseField]) {
        return `${tString('atlasFields.item')} ${posIndex} - ${fieldPaths[baseField]}`;
      }
    }
    // Fallback: extract last part of the path
    const parts = fieldPath.split('.');
    return parts[parts.length - 1] || fieldPath;
  };

  // German → English translation map for ATLAS gRPC error message terms
  // IMPORTANT: longer/more specific phrases must come BEFORE shorter ones to avoid partial matches
  const germanErrorTerms: Record<string, string> = {
    // Section/tab labels (backend sends these as part of the field label prefix)
    'Beförderungsroute Der Sendung': 'Transport Route of the Shipment',
    'Beforderungsroute Der Sendung': 'Transport Route of the Shipment',
    'Beförderungsroute der Sendung': 'Transport Route of the Shipment',
    'Beforderungsroute der Sendung': 'Transport Route of the Shipment',
    // Field names (longest first to avoid partial matches)
    'Vorgesehene Ausgangszollstelle': 'Intended Exit Customs Office',
    'Inländischer Verkehrszweig': 'Domestic Transport Mode',
    'Verkehrszweig an der Grenze': 'Border Transport Mode',
    'Beteiligten-Konstellation': 'Participant Constellation',
    'Art der Ausfuhranmeldung': 'Export Declaration Type',
    'Gestellungszollstelle': 'Customs Office of Presentation',
    'Nachrichtenempfänger': 'Message Recipient',
    'Niederlassungsnummer': 'Branch Number',
    'Container-Indikator': 'Container Indicator',
    'Dienststellennummer': 'Office Number',
    'Art der Anmeldung': 'Type of Registration',
    'Maßgebliches Datum': 'Relevant Date',
    'Beförderungsmittel': 'Transport Means',
    'Beförderungsroute': 'Transport Route',
    'Beforderungsroute': 'Transport Route',
    'Staatszugehörigkeit': 'Nationality',
    'Warenbezeichnung': 'Product Description',
    'Ausfuhrzollstelle': 'Export Customs Office',
    'Rechnungswährung': 'Invoice Currency',
    'Referenznummer': 'Reference Number',
    'Bestimmungsland': 'Destination Country',
    'Lieferbedingung': 'Delivery Terms',
    'Gesamt-Rohmasse': 'Total Gross Mass',
    'Versandzeichen': 'Shipping Marks',
    'Subunternehmer': 'Subcontractor',
    'Warennummer': 'HS Code',
    // Before the bare 'Verpackung' below, which alone rendered the package-type
    // error as "Art der Packaging: ...".
    'Art der Verpackung': 'Package Type',
    'Verpackung': 'Packaging',
    'fehlen': 'are missing',
    'fehlt': 'is missing',
    'Packstücke': 'Packages',
    // BEO's own English string is itself half-German: "Supporting Document
    // Unterlagetyp benoetigt a Qualifikator". Translating the leftovers here is
    // the only place they can be reached. Unterlagetyp comes before Unterlage
    // so the compound wins (the whole-term rule below would protect it anyway).
    'Unterlagetyp': 'Document Type',
    'benötigt einen': 'requires a',
    'benoetigt einen': 'requires a',
    'benötigt eine': 'requires a',
    'benoetigt a': 'requires a',
    'benötigt a': 'requires a',
    'benötigt': 'requires',
    'benoetigt': 'requires',
    'Qualifikator': 'Qualifier',
    'Unterlage': 'Supporting Document',
    'Bewilligung': 'Authorization',
    'Ausfuhrland': 'Export Country',
    'Eigenmasse': 'Net Weight',
    'Sicherheit': 'Security',
    'Empfänger': 'Recipient',
    'Ausführer': 'Exporter',
    'Vertreter': 'Representative',
    'Anmelder': 'Declarant',
    'Rohmasse': 'Gross Weight',
    'Sendung': 'Shipment',
    'KOPF': 'HEADER',
    // Section path prefixes used in error messages
    'VERSENDUNG': 'DELIVERY',
    'POSITION': 'ITEM',
    // Common validation phrases — longer phrases first to prevent partial substitution
    // Transport Route C0248 error pattern
    'Für alle in den': 'For all in the',
    'Für alle': 'For all',
    'nur die Codierungen der Codeliste': 'only the codes of codelist',
    'Codierungen der Codeliste': 'codes of codelist',
    'sind in der': 'are in the',
    'sind nur': 'are only',
    'nur die': 'only the',
    'der Codeliste': 'of codelist',
    'genannten Ländern': 'listed countries',
    'genannten Länder': 'listed countries',
    // Before the bare 'Datenfeld' entries below: object key order is insertion
    // order, and translating only the suffix left "Pflicht-data field" on
    // screen — half German, half English, in the message that tells the user a
    // field is mandatory.
    'Pflicht-Datenfeld': 'Required data field',
    'Pflicht-Datengruppe': 'Required data group',
    'Pflicht-Datenfelder': 'Required data fields',
    'Datenfeldern': 'data fields',
    'Datenfelder': 'data fields',
    'Feldern': 'fields',
    'Felder': 'fields',
    'Ländern': 'countries',
    // Standalone words
    'Für': 'For',
    'bzw.': 'or',
    'genannten': 'listed',
    'Codierungen': 'codes',
    'zulässig': 'permitted',
    'sind': 'are',
    'nur': 'only',
    'Eines der angegebenen Länder': 'One of the specified countries',
    'Einer der angegebenen Länder': 'One of the specified countries',
    'Eines der angegebenen': 'One of the specified',
    'Einer der angegebenen': 'One of the specified',
    'angegebenen Länder': 'specified countries',
    'mit einem anderen Wert als': 'with a value other than',
    'mit einem anderen Wert': 'with a different value',
    'Zentrales Zollverfahren': 'Central Customs Processing',
    'muss den Wert': 'must contain the value',
    'muss die Angabe': 'must specify',
    'die Angabe der Werte': 'the specification of the values',
    'die Angabe des Wertes': 'the specification of the value',
    'darf nicht angegeben werden': 'must not be specified',
    'darf nicht leer sein': 'must not be empty',
    'muss angegeben werden': 'must be specified',
    'sind nicht übereinstimmend': 'do not match',
    'nicht übereinstimmend': 'do not match',
    'ist nicht zulässig': 'is not allowed',
    'falls das Datenfeld': 'if the data field',
    'wenn das Datenfeld': 'if the data field',
    'ist ein Pflichtfeld': 'is a required field',
    'nicht zulässig': 'not allowed',
    'nicht erlaubt': 'not permitted',
    'angegeben wird': 'is specified',
    'angegeben werden': 'are specified',
    'angegebenen': 'specified',
    'mit dem Wert': 'with the value',
    'übereinstimmend': 'matching',
    'ungültiger Wert': 'invalid value',
    'Wert ist zu lang': 'value is too long',
    'Wert ist zu kurz': 'value is too short',
    'Zollverfahren': 'Customs Procedure',
    'beinhalten': 'contain',
    'enthalten': 'contain',
    'Vereinfachtes': 'Simplified',
    'Vereinfachte': 'Simplified',
    'Verfahren': 'Procedure',
    'Datenfeld': 'data field',
    'Länder': 'countries',
    'Ausfuhr': 'Export',
    'Eines': 'One of',
    'Einer': 'One of',
    'Der': 'of the',
    'muss': 'must',
  };

  // Translate German terms in ATLAS error messages to English
  const translateErrorMessage = (msg: string): string => {
    if (currentLanguage === 'de' || !msg) return msg;
    let translated = msg;

    // Regex-based translations for dynamic patterns (PutData errors)
    // "stimmt nicht mit dem Regex-Muster <PATTERN> überein" → "does not match the required pattern: <PATTERN>"
    translated = translated.replace(
      /stimmt nicht mit dem Regex-Muster\s+(.+?)\s+überein/g,
      'does not match the required pattern: $1'
    );
    // "muss dem Muster <PATTERN> entsprechen" → "must match the pattern: <PATTERN>"
    translated = translated.replace(
      /muss dem Muster\s+(.+?)\s+entsprechen/g,
      'must match the pattern: $1'
    );
    // Transport Route C0248 codelist constraint pattern:
    // "Für alle in den Datenfeldern/Datenfeld ... genannten Länder(n) sind in der Beförderungsroute
    //  nur die Codierungen der Codeliste C0248 zulässig."
    translated = translated.replace(
      /Für alle(?:\s+in\s+(?:den|dem)\s+Datenfelder?n?)?\s*(.*?)\s*genannten\s+Länder[n]?\s+(?:sind|sind in der Beförderungsroute)\s+(?:nur\s+)?(?:die\s+)?Codierungen\s+der\s+Codeliste\s+(\w+)\s+zulässig/gi,
      'For all countries listed in the data field(s) $1, only the codes from codelist $2 are permitted in the Transport Route'
    );

    // Static term replacements, on WHOLE terms only.
    //
    // This was a blind split/join, which replaced the term wherever it appeared
    // — including inside a longer identifier. ATLAS reports schema errors by
    // property name, so "required property 'gesamtRohmasse' not found" came out
    // as "required property 'gesamtGross Weight' not found": a property that
    // exists nowhere, in a message telling the user which property to supply.
    // 'Eigenmasse' did the same to wareEigenmasse.
    //
    // A term is only translated when it is not glued to an identifier on either
    // side. Longer terms are listed before their suffixes ('Gesamt-Rohmasse'
    // before 'Rohmasse'), and object key order is insertion order, so the
    // specific one still wins.
    for (const [german, english] of Object.entries(germanErrorTerms)) {
      if (!translated.includes(german)) continue;
      const escaped = german.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      translated = translated.replace(
        new RegExp(`([A-Za-z0-9_])?${escaped}(?![A-Za-z0-9_])`, 'g'),
        (match, precededBy) => (precededBy ? match : english)
      );
    }
    return translated;
  };

  // ML Predictions Sidebar state
  const [showMLPredictionsSidebar, setShowMLPredictionsSidebar] = useState(false);
  const [mlPredictions, setMlPredictions] = useState<MLPredictionsResult | null>(null);
  // Paths whose current value was applied from an ML prediction (provenance for the form tabs).
  const [aiAppliedFields, setAiAppliedFields] = useState<Set<string>>(new Set());
  const [appliedPredictions, setAppliedPredictions] = useState<Set<string>>(new Set());
  // Deep clone of combinedExtractedData captured immediately before profile/default/ML injection.
  // Drives the Extracted tab to show only fields with true document provenance.
  const [documentSnapshot, setDocumentSnapshot] = useState<any>(null);

  // Load from History state
  const [showHistoryConfirmDialog, setShowHistoryConfirmDialog] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historySuggestions, setHistorySuggestions] = useState<Record<string, string> | null>(null);
  // TEMPORARY: Track if initial ML predictions were fetched - prevent refetching on field changes
  const initialMlPredictionsFetchedRef = useRef(false);

  // Workflow step state
  const [expandedStep, setExpandedStep] = useState<'upload' | 'processing' | 'review' | null>('upload');
  const [activeWorkflowStep, setActiveWorkflowStep] = useState<WorkflowStep>('upload');
  const setModuleDrawerOpen = useModuleDrawerStore(state => state.setOpen);

  // Track document identity to clear predictions only when document changes
  const documentIdRef = useRef<string | null>(null);
  // Track if ML fetch has failed for this document to prevent repeated retries
  const mlFetchFailedRef = useRef(false);
  const mlErrorToastShownRef = useRef(false);  // Track if ML error toast has been shown this session
  const isSendingRef = useRef(false);  // Prevent duplicate validate/send calls and auto-validate re-trigger
  const [isSending, setIsSending] = useState(false);
  // (Removed: hasAutoPreviewedRef — auto preview after gRPC validation disabled)

  // --- ML Correction Tracking ---
  // Stores the ML-predicted value for each field when shown, keyed by ATLAS fieldPath
  const activePredictionsRef = useRef<Record<string, string>>({});
  // Tracks fields where user changed an ML prediction — stores the ML-predicted value
  // The final user value is read at submit time to avoid capturing intermediate typing
  const changedPredictionFieldsRef = useRef<Record<string, {
    ml_predicted_value: string;
    is_position_field: boolean;
    position_index?: number;
  }>>({});
  const startProcessingBtnRef = useRef<HTMLButtonElement>(null);
  // Drives the progress bar's easing between real milestones. Without a ticker
  // the bar only repaints when currentStep changes — which is exactly the
  // 30-60s dead zone we are covering.
  const [procElapsed, setProcElapsed] = useState(0);
  // Progress must never run backwards: a step change can land in a band whose
  // eased start is below where easing had already crept to.
  const procPeakRef = useRef(0);
  // Easing is measured from when the CURRENT band was entered, not from the
  // start of the run — otherwise every band inherits the elapsed time of the
  // ones before it and opens far along its own curve.
  const procBandStartRef = useRef(0);
  const procBandStepRef = useRef('');
  const [selectedPositionIndex, setSelectedPositionIndex] = useState(0);

  // --- Extracted-data autosave (Slice A-D) ------------------------------
  // Debounced background PATCH of edited extracted data back onto its
  // transaction so field corrections (incl. Teach-AI delete/re-map) survive a
  // reload. Scoped to the single-document case: `processing.extractedData` is a
  // MERGED view across files, so it maps cleanly to exactly one transaction
  // only when a single file was uploaded. Multi-file merges are skipped.
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveAbortRef = useRef<AbortController | null>(null);
  // Baseline of the last data we persisted (or the initial extraction), keyed
  // by transaction id — so we don't re-save the untouched initial tree and
  // only fire on genuine user edits.
  const autosaveBaselineRef = useRef<{ txId: string; json: string } | null>(null);

  // Compliance polling state
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [polledComplianceReport, setPolledComplianceReport] = useState<any>(null);
  const [complianceStatus, setComplianceStatus] = useState<string>('');

  // Multi-document compliance tracking
  const [allComplianceReports, setAllComplianceReports] = useState<Array<{
    fileId: string;
    fileName: string;
    report: any;
  }>>([]);

  // Compliance opt-in state
  const [enableCompliance, setEnableCompliance] = useState(user?.enable_compliance_checks || false);

  // Correlation validation state
  const [documentSetId, setDocumentSetId] = useState<string | null>(null);
  // Multi-document merge result (conflicts, provenance, correlation) — null for single-doc
  const [mergeResult, setMergeResult] = useState<MultiDocumentProcessingResult | null>(null);
  // True when the multi-doc merge failed and we silently fell back to first-file data
  const [mergeFallback, setMergeFallback] = useState(false);
  const fieldSources = useMemo(
    () => deriveFieldSources(mergeResult?.field_metadata),
    [mergeResult]
  );
  const [correlationResult, setCorrelationResult] = useState<ComprehensiveCorrelationResponse | null>(null);
  const [isCorrelationValid, setIsCorrelationValid] = useState<boolean>(false);
  const [showCorrelationDetails, setShowCorrelationDetails] = useState<boolean>(false);
  const [actuallyUploadedFileIds, setActuallyUploadedFileIds] = useState<string[]>([]); // Track backend-uploaded files
  const [abortController, setAbortController] = useState<AbortController | null>(null); // For canceling uploads

  // Field-level validation errors from gRPC validation
  const [fieldValidationErrors, setFieldValidationErrors] = useState<Record<string, string>>({});

  // General validation errors (for fields not visible in form)
  const [generalValidationErrors, setGeneralValidationErrors] = useState<string[]>([]);

  // Counter incremented after extraction to guarantee gRPC auto-validation fires
  const [grpcValidationTrigger, setGrpcValidationTrigger] = useState(0);

  // Module user details (EORI, etc.) fetched after authentication
  const [moduleUserDetails, setModuleUserDetails] = useState<{
    atlas_user_id?: string;
    atlas_mandant_id?: string;
    atlas_kundennummer?: string;
    email?: string;
    modules_data?: any;
    ausfuhr_eori?: string;
    eoriNiederlassungsnummer?: string;
  } | null>(null);


  // Helper function to find validation error for a field path with fuzzy matching
  const getFieldError = useCallback((fieldPath: string): string | undefined => {
    if (!fieldPath || Object.keys(fieldValidationErrors).length === 0) return undefined;

    // Helper: extract position index from a path like "position[1].field" -> "1", or null if not a position path
    const getPositionIndex = (path: string): string | null => {
      const m = path.match(/^position\[(\d+)\]/);
      return m ? m[1] : null;
    };

    const fieldPosIdx = getPositionIndex(fieldPath);
    const isPositionField = fieldPosIdx !== null;

    // 1. Try exact match first
    if (fieldValidationErrors[fieldPath]) {
      return fieldValidationErrors[fieldPath];
    }

    // 2. Try matching without the 'kopf.' prefix (NOT position — position index must match)
    if (!isPositionField) {
      const simplifiedPath = fieldPath.replace(/^kopf\./, '');
      for (const [errorPath, errorMsg] of Object.entries(fieldValidationErrors)) {
        // Skip position errors when matching non-position fields
        if (getPositionIndex(errorPath) !== null) continue;
        const simplifiedErrorPath = errorPath.replace(/^kopf\./, '');
        if (simplifiedErrorPath === simplifiedPath) {
          return errorMsg;
        }
      }
    }

    // 3. Try parent path matching (error for 'kopf.empfanger' should match 'kopf.empfanger.name')
    for (const [errorPath, errorMsg] of Object.entries(fieldValidationErrors)) {
      // For position fields, ensure the position index matches
      if (isPositionField) {
        const errorPosIdx = getPositionIndex(errorPath);
        if (errorPosIdx !== null && errorPosIdx !== fieldPosIdx) continue;
      }
      // A bare container root ("kopf", "position[0]") is NOT a parent to
      // inherit from. It is a whole-object error, and letting it match every
      // child broadcasts one problem onto every field: ATLAS answered a single
      // "required property 'gesamtRohmasse' not found" against expdat.0.kopf
      // and the form rendered 76 identical errors, one per header field, with
      // no way to tell which field was actually at fault. Such errors are
      // surfaced once in the unattached-errors list instead.
      const isContainerRoot = !errorPath.includes('.') || /^position\[\d+\]$/.test(errorPath);
      if (!isContainerRoot) {
        if (fieldPath.startsWith(errorPath + '.')) {
          return errorMsg;
        }
        if (errorPath.startsWith(fieldPath + '.')) {
          return errorMsg;
        }
      }
    }

    // 4. Try matching the last part of the path for non-position fields
    if (!isPositionField) {
      const lastPart = fieldPath.split('.').pop() || '';
      for (const [errorPath, errorMsg] of Object.entries(fieldValidationErrors)) {
        // Skip position errors
        if (getPositionIndex(errorPath) !== null) continue;
        const errorLastPart = errorPath.split('.').pop() || '';
        if (lastPart === errorLastPart) {
          const fieldContext = fieldPath.includes('empfanger') ? 'empfanger' :
            fieldPath.includes('ausfuhrer') ? 'ausfuhrer' :
              fieldPath.includes('anmelder') ? 'anmelder' : '';
          const errorContext = errorPath.includes('empfanger') ? 'empfanger' :
            errorPath.includes('ausfuhrer') ? 'ausfuhrer' :
              errorPath.includes('anmelder') ? 'anmelder' : '';
          if (fieldContext && fieldContext === errorContext) {
            return errorMsg;
          }
        }
      }
    }

    // 5. Try partial match - but respect position indices
    for (const [errorPath, errorMsg] of Object.entries(fieldValidationErrors)) {
      if (isPositionField) {
        const errorPosIdx = getPositionIndex(errorPath);
        if (errorPosIdx !== null && errorPosIdx !== fieldPosIdx) continue;
      }
      if (errorPath.endsWith(fieldPath) || fieldPath.endsWith(errorPath)) {
        return errorMsg;
      }
    }

    // 6. Try matching with 'kopf.' prefix added/removed (not for position fields)
    if (!isPositionField) {
      const withKopf = fieldPath.startsWith('kopf.') ? fieldPath : `kopf.${fieldPath}`;
      const withoutKopf = fieldPath.startsWith('kopf.') ? fieldPath.replace(/^kopf\./, '') : fieldPath;

      if (fieldValidationErrors[withKopf]) {
        return fieldValidationErrors[withKopf];
      }
      if (fieldValidationErrors[withoutKopf]) {
        return fieldValidationErrors[withoutKopf];
      }
    }

    // 7. Try matching position fields with dot notation (same index only)
    if (isPositionField) {
      const dotVariant = fieldPath.replace(/position\[(\d+)\]/, 'position.$1');
      if (fieldValidationErrors[dotVariant]) {
        return fieldValidationErrors[dotVariant];
      }
    }

    return undefined;
  }, [fieldValidationErrors]);

  // Compliance polling hook - only enabled if compliance was requested
  const { report: pollingReport, isPolling, status: pollingStatus, error: pollingError } = useCompliancePolling({
    fileId: uploadedFileId,
    enabled: uploadComplete && !!uploadedFileId && complianceStatus === 'in_progress',
    onComplete: (report) => {
      // console.log('✅ Compliance check completed!', report); // HIDDEN - May need later
      setPolledComplianceReport(report);

      // Add to the array of all compliance reports
      const fileName = uploadedFiles.find(f => f.fileId === uploadedFileId)?.name || 'Unknown File';
      setAllComplianceReports(prev => [...prev, {
        fileId: uploadedFileId!,
        fileName: fileName,
        report: report
      }]);
      // console.log(`📋 Added compliance report for ${fileName} to collection`);

      // Update processing state to display the report in UI
      setProcessing(prev => ({
        ...prev,
        complianceReport: report
      }));

      toast.success(`Compliance check completed for ${fileName}!`);
    },
    onError: (error) => {
      console.error('❌ Compliance polling failed:', error);
      toast.error(tString('documentProcessing.toasts.complianceResultsFailed'));
    },
    onTimeout: () => {
      console.warn('⏱️ Compliance check timed out');
      toast.warning(tString('documentProcessing.toasts.complianceTimeout'));
    },
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleMenuClose();
  };

  const handleGoHome = () => {
    navigate('/process');
    handleMenuClose();
  };

  // File upload dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 10,
    maxSize: 50 * 1024 * 1024, // 50MB
    onDrop: handleFilesUpload,
  });

  useEffect(() => {
    if (!processing.isProcessing) {
      setProcElapsed(0);
      procPeakRef.current = 0;
      procBandStartRef.current = 0;
      procBandStepRef.current = '';
      return;
    }
    const id = setInterval(() => setProcElapsed(Date.now()), 200);
    setProcElapsed(Date.now());
    return () => clearInterval(id);
  }, [processing.isProcessing]);

  useEffect(() => {
    loadModules();
  }, []);

  // Initialize codelistService for dropdown value conversion in ML predictions
  useEffect(() => {
    codelistService.initialize().then(() => {
      // console.log('[CodelistService] ✅ Initialized for ML prediction dropdown conversion');
    }).catch(err => {
      console.error('[CodelistService] ❌ Failed to initialize:', err);
    });
  }, []);

  // Handle deep link from dashboard Magic Marker / Teach AI card
  useEffect(() => {
    const locationState = location.state as any;
    if (locationState?.openMagicMarker || locationState?.openTeachAI) {
      // Check if we have extracted data ready
      if (processing.extractedData && uploadedFiles.length > 0) {
        setShowTeachAI(true);
        setSelectedFileForAnnotation(uploadedFiles[0]);
      } else {
        toast.info('Please upload a document to use Teach AI', {
          toastId: 'teach-ai-upload-needed',
          autoClose: 5000,
        });
      }

      // Clear navigation state to prevent re-opening on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, processing.extractedData, uploadedFiles]);

  // Handle deep link from dashboard Tariff Classifier card
  // (Deep-link to legacy in-page Tariff Classifier dialog was removed.
  // The standalone Tariff Classifier page is now the canonical entrypoint.)

  // Capture the original extracted-from-file data the first time it is set.
  // Used by "Reset Form" in the Switch User confirmation.
  useEffect(() => {
    if (processing.extractedData && !originalExtractedDataRef.current) {
      originalExtractedDataRef.current = JSON.parse(JSON.stringify(processing.extractedData));
    }
  }, [processing.extractedData]);

  // Debounced autosave of edited extracted data back to its transaction.
  // Only runs for the single-document case (see autosave refs above): the one
  // uploaded file with a transaction id is the save target. On the first
  // observation of a transaction we record a baseline and DON'T save (the
  // initial tree is already stored); only later user edits trigger a PATCH.
  useEffect(() => {
    if (processing.isProcessing || !processing.extractedData) return;

    const withTx = uploadedFiles.filter(f => f.transactionId);
    if (withTx.length !== 1) return; // merged/multi-doc → can't map to one txn
    const txId = withTx[0].transactionId as string;

    const json = JSON.stringify(processing.extractedData);

    // New transaction (or first load): set the baseline, skip the save.
    if (!autosaveBaselineRef.current || autosaveBaselineRef.current.txId !== txId) {
      autosaveBaselineRef.current = { txId, json };
      return;
    }
    // Unchanged since last save → nothing to do.
    if (autosaveBaselineRef.current.json === json) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      // Cancel any in-flight save so only the latest edit wins.
      if (autosaveAbortRef.current) autosaveAbortRef.current.abort();
      const controller = new AbortController();
      autosaveAbortRef.current = controller;
      try {
        await authService.updateTransactionData(
          txId,
          JSON.parse(json),
          controller.signal
        );
        autosaveBaselineRef.current = { txId, json };
      } catch (err: any) {
        // Background save — stay silent (interceptor suppresses toasts too).
        if (err?.code !== 'ERR_CANCELED') {
          console.warn('Extracted-data autosave failed:', err?.message || err);
        }
      }
    }, 800);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [processing.extractedData, processing.isProcessing, uploadedFiles]);

  // Auto-select all fields when switching to form view (form view doesn't show checkboxes)
  useEffect(() => {
    if (viewMode === 'form' && processing.extractedData) {
      const allSelections: Record<string, boolean> = {};
      Object.keys(flattenObject(processing.extractedData)).forEach(key => {
        allSelections[key] = true;
      });
      setProcessing(prev => ({ ...prev, fieldSelections: allSelections }));
    }
  }, [viewMode, processing.extractedData, processing.selectedModule]);

  // ===== AUTO GRPC VALIDATION ON FORM LOAD =====
  // Automatically call gRPC validation when extractedData is set (after document extraction)
  // This shows validation errors immediately when the form is displayed
  useEffect(() => {
    const runAutoValidation = async () => {
      // Only run for ATLAS module with extracted data
      if (!processing.extractedData || !processing.selectedModule?.name?.toLowerCase().includes('atlas')) {
        return;
      }

      // Don't run if validate-and-send is in progress
      if (isSendingRef.current) {
        return;
      }

      // BLACKOUT CHECK: Skip validation if Apply All ML Predictions is in progress
      // This prevents validation from running and potentially interfering with the apply process
      const isBlackoutActive = isMlBlackoutActive() || isApplyingPredictions();

      if (isBlackoutActive) {
        return;
      }

      // console.log('');
      // console.log('='.repeat(60));
      // console.log('🔄 AUTO GRPC VALIDATION - Form Load');
      // console.log('='.repeat(60));
      // console.log('📍 Automatically validating extracted data via gRPC service...');

      // Step 1: Do NOT pre-clear validation errors — keep existing errors visible
      // until new results arrive (prevents UI flash of no errors during validation)
      // console.log('📍 Step 1: Keeping existing errors visible while re-validating...');

      // Step 2: Prepare data for validation
      // console.log('📍 Step 2: Preparing data for gRPC validation...');
      const selectedData = filterSelectedFields(processing.extractedData, processing.fieldSelections);

      // Remove flat ML-injected fields that are not form fields
      // These get misinterpreted as extra bewilligung entries by the gRPC service
      if (selectedData.kopf) {
        delete selectedData.kopf.bewilligungZugelassenerAusfuhrer;
        delete selectedData.kopf.bewilligungArt;
      }

      // Step 2.5: Format date fields for ATLAS (TT.MM.JJJJ format)
      // Normalize date fields to YYYYMMDD for ATLAS (BEO schema: "Date (n8)")
      const normalizeDateToYYYYMMDD = (dateValue: string | undefined | null): string => {
        if (!dateValue || dateValue === '') return '';
        const trimmed = String(dateValue).trim();
        if (/^\d{8}$/.test(trimmed)) return trimmed;
        const dotMatch = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (dotMatch) return `${dotMatch[3]}${dotMatch[2]}${dotMatch[1]}`;
        const dashMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (dashMatch) return `${dashMatch[3]}${dashMatch[2]}${dashMatch[1]}`;
        const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) return `${isoMatch[1]}${isoMatch[2]}${isoMatch[3]}`;
        if (/^\d{14}$/.test(trimmed)) return trimmed.substring(0, 8);
        let date: Date | null = null;
        try { date = new Date(trimmed); } catch { /* ignore */ }
        if (date && !isNaN(date.getTime())) {
          return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
        }
        return trimmed;
      };

      // Normalize date fields before validation
      if (selectedData.kopf) {
        if (selectedData.kopf.massgeblichesDatum) {
          selectedData.kopf.massgeblichesDatum = normalizeDateToYYYYMMDD(selectedData.kopf.massgeblichesDatum);
        }
        if (selectedData.kopf.kopfDatumdesAusgangs) {
          selectedData.kopf.kopfDatumdesAusgangs = normalizeDateToYYYYMMDD(selectedData.kopf.kopfDatumdesAusgangs);
        }
      }

      // Step 3: Call gRPC validation service
      // console.log('📍 Step 3: Calling gRPC validation service...');
      try {
        const validationResult = await authService.validateModuleData(
          processing.selectedModule.id,
          selectedData,
          currentLanguage,
          sendPartialData
        );

        // console.log('📋 Auto-Validation Result:', {
        //   valid: validationResult.valid,
        //   totalErrors: validationResult.total_errors,
        //   totalWarnings: validationResult.total_warnings
        // });

        // Step 4: Map validation errors to field paths
        if (!validationResult.valid && validationResult.errors?.length > 0) {
          // console.log('📍 Step 4: Mapping validation errors to form fields...');

          const fieldErrors: Record<string, string> = {};
          const generalErrors: string[] = [];

          // Field path normalization function (simplified version)
          const normalizeFieldPath = (rawPath: string): string => {
            let path = rawPath || '';
            path = path.replace(/^expdat\[\d+\]\./, '');
            path = path.replace(/^ausfuhr\.expdat\[\d+\]\./, '');
            path = path.replace(/^messageData\.ausfuhr\.expdat\[\d+\]\./, '');
            path = path.replace(/^data\./, '');
            path = path.replace(/\.(\d+)\./g, '[$1].');
            path = path.replace(/\.(\d+)$/, '[$1]');
            path = path.replace(/empfänger/g, 'empfanger');
            path = path.replace(/empfaenger/g, 'empfanger');

            // gRPC field path to form field path mappings
            const fieldMapping: Record<string, string> = GRPC_FIELD_PATH_ALIASES;

            // Try direct mapping first
            if (fieldMapping[path]) {
              return fieldMapping[path];
            }
            // Try lowercase version of path
            const lowerPath = path.toLowerCase();
            if (fieldMapping[lowerPath]) {
              return fieldMapping[lowerPath];
            }

            // Add kopf prefix if missing for common header fields
            const headerFields = ['eoriNiederlassungsnummer', 'dienststellennummer', 'anmeldeart',
              'artderAnmeldung', 'verfahren', 'artderAusfuhranmeldung', 'beteiligtenKonstellation',
              'sicherheit', 'rechnungswahrung', 'bestimmungsLand', 'ausfuhrland', 'lieferbedingungIncotermCode'];
            if (headerFields.includes(path) && !path.startsWith('kopf.')) {
              path = 'kopf.' + path;
            }

            // Normalize packaging field variants for ANY position index (handles 1-20+ items)
            path = path.replace(/position\[(\d+)\]\.artderPackstucke/g, 'position[$1].verpackung[0].artderVerpackung');
            path = path.replace(/position\[(\d+)\]\.packstuckanzahl/g, 'position[$1].verpackung[0].anzahlderPackstucke');
            path = path.replace(/position\[(\d+)\]\.packstuckzeichennummern/g, 'position[$1].verpackung[0].versandzeichen');
            path = path.replace(/position\[(\d+)\]\.packstuckverpackungsart/g, 'position[$1].verpackung[0].artderVerpackung');
            path = path.replace(/position\[(\d+)\]\.versandzeichen(?!\[)/g, 'position[$1].verpackung[0].versandzeichen');
            path = path.replace(/position\[(\d+)\]\.anzahlderPackstucke(?!\.)/g, 'position[$1].verpackung[0].anzahlderPackstucke');
            path = path.replace(/^position\[(\d+)\]\.verpackung$/, 'position[$1].verpackung[0].artderVerpackung');
            // Position-level aliases that existed ONLY on the submit path. Without
            // them an ATLAS error on e.g. position[5].rohmasse resolved to no field,
            // so it was counted in the Errors badge and rendered nowhere - net mass,
            // gross mass, statistical value, goods description, commodity code and
            // both procedure codes were all unreachable from the Errors tab.
            path = path.replace(/position\[(\d+)\]\.warenbezeichnung/g, 'position[$1].wareWarenbezeichnung');
            path = path.replace(/position\[(\d+)\]\.warennummer(?!KN8)/g, 'position[$1].wareWarennummerKN8');
            path = path.replace(/position\[(\d+)\]\.warennummerKN8/g, 'position[$1].wareWarennummerKN8');
            path = path.replace(/position\[(\d+)\]\.rohmasse/g, 'position[$1].wareRohmasse');
            path = path.replace(/position\[(\d+)\]\.eigenmasse/g, 'position[$1].wareEigenmasse');
            path = path.replace(/position\[(\d+)\]\.positionsnummer/g, 'position[$1].warePositionsnummer');
            path = path.replace(/position\[(\d+)\]\.statistischerWert/g, 'position[$1].wareStatistischerWert');
            path = path.replace(/position\[(\d+)\]\.rechnungspreis/g, 'position[$1].wareRechnungspreis');
            path = path.replace(/position\[(\d+)\]\.angemeldetes(?:Verfahren)?/g, 'position[$1].beantragtesVerfahren');
            path = path.replace(/position\[(\d+)\]\.vorangegangenes(?:Verfahren)?/g, 'position[$1].vorhergehendesVerfahren');
            path = path.replace(/position\[(\d+)\]\.beantragtesVerfahren/g, 'position[$1].beantragtesVerfahren');
            path = path.replace(/position\[(\d+)\]\.vorhergehendesVerfahren/g, 'position[$1].vorhergehendesVerfahren');

            return path;
          };

          // Clean error message - remove {0}: prefix, fix bullet chars, translate German
          const cleanMessage = (msg: string): string => {
            let cleaned = (msg || 'Validation error')
              .replace(/[â¢•]{2,}(\d{2,4})/g, '"$1"')  // â¢â¢01 → "01"
              .replace(/[â¢•]+/g, '')  // Remove stray bullet chars
              .replace(/\{\d+\}:?\s*/g, '')  // Remove {0}: {1}: etc.
              .replace(/^[a-zA-Z0-9_.\[\]]+:\s*/, '')  // Remove field path prefixes
              .trim() || 'Validation error';
            return translateErrorMessage(cleaned);
          };

          validationResult.errors.forEach((error: any) => {
            const rawField = error.field || error.path || 'unknown';
            const message = cleanMessage(error.message || error.error || 'Validation error');
            const normalizedPath = normalizeFieldPath(rawField);

            // Skip network/system errors
            if (rawField.startsWith('_network') || rawField.startsWith('_grpc')) {
              generalErrors.push(message);
              return;
            }

            // Map to field errors or general errors
            if (normalizedPath && normalizedPath !== 'unknown') {
              if (fieldErrors[normalizedPath]) {
                appendFieldError(fieldErrors, normalizedPath, message);
              } else {
                fieldErrors[normalizedPath] = message;
              }
            } else {
              generalErrors.push(`${translateFieldPath(rawField)}: ${message}`);
            }
          });

          // Step 5: Set validation errors in state
          // console.log('📍 Step 5: Setting validation errors in state...');
          // console.log(`  - Field errors: ${Object.keys(fieldErrors).length}`);
          // console.log(`  - General errors: ${generalErrors.length}`);

          // Expand the header-level procedure sentinels onto the line items.
          // Without this the sentinel itself was rendered as a field named
          // "__fanout_beantragtesVerfahren__". See procedureFanout.
          expandProcedureFanout(
            fieldErrors,
            (validationResult as any)?._positions
              || processing.extractedData?.position,
          );

          setFieldValidationErrors(fieldErrors);
          setGeneralValidationErrors(generalErrors);

          // Scroll to first error after Accordions expand and React re-renders
          setTimeout(() => {
            // Try general validation errors alert first (always visible above form)
            const generalErrorsAlert = document.getElementById('general-validation-errors');
            if (generalErrorsAlert) {
              generalErrorsAlert.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
            // Then try fields with .Mui-error class
            const errorFields = document.querySelectorAll('.Mui-error');
            if (errorFields.length > 0) {
              errorFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
            // Fallback: try data-field-path for first error
            const firstErrorField = Object.keys(fieldErrors)[0];
            if (firstErrorField) {
              const el = document.querySelector(`[data-field-path="${firstErrorField}"]`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }, 800);

          // console.log('✅ Auto-validation complete - errors displayed in form');
        } else {
          // Validation passed — clear all previous errors
          setFieldValidationErrors({});
          setGeneralValidationErrors([]);
          // console.log('✅ Auto-validation complete - no errors found, cleared previous errors');
        }

      } catch (error: any) {
        console.error('❌ Auto-validation failed:', error.message || error);
        // Don't show error toast - just log it (validation will happen again on submit)
      }

      // console.log('='.repeat(60));
    };

    // Calculate delay: if blackout is active, wait out the blackout window + 500ms buffer
    const baseDelay = 500; // Normal delay to ensure form is rendered
    // The blackout deadline is encapsulated in mlCoordination; if it's still active,
    // wait the full 5s blackout window so validation can't run before it self-expires.
    const blackoutRemaining = isMlBlackoutActive() ? 5000 : 0;
    const totalDelay = blackoutRemaining > 0 ? blackoutRemaining + baseDelay : baseDelay;

    if (blackoutRemaining > 0) {
      // console.log(`🔄 AUTO GRPC VALIDATION - Delayed by ${totalDelay}ms (waiting for ML blackout to end)`);
    }

    // Run auto-validation with appropriate delay
    const timeoutId = setTimeout(runAutoValidation, totalDelay);
    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processing.extractedData, processing.selectedModule, currentLanguage, sendPartialData, grpcValidationTrigger]);
  // grpcValidationTrigger: explicit counter incremented after extraction to guarantee validation fires
  // processing.isProcessing intentionally omitted — we guard via isSendingRef instead

  // Reset correlation state when file count changes
  useEffect(() => {
    if (uploadedFiles.length < 2) {
      setCorrelationResult(null);
      setIsCorrelationValid(false);
    }
  }, [uploadedFiles.length]);

  // Restore selected module from localStorage when modules are loaded
  useEffect(() => {
    const selectedModuleId = localStorage.getItem('selectedModuleId');
    if (selectedModuleId && modules.length > 0) {
      const savedModule = modules.find(m => m.id === selectedModuleId);
      if (savedModule && savedModule.is_active) {
        setProcessing(prev => ({
          ...prev,
          selectedModule: savedModule,
        }));
        setSelectedModule(savedModule as any);
      }
    }
  }, [modules]);

  // Note: Removed continuous token polling to reduce backend load
  // Token expiration is now checked only when:
  // 1. User selects a module
  // 2. User initiates file processing
  // 3. Page is loaded/refreshed

  const loadModules = async () => {
    try {
      const modulesData = await authService.getModules();
      // Show all modules returned by API (backend handles access control)
      // Modules without access will be shown as disabled in the UI
      // Check authentication status for each module
      const modulesWithAuth = await Promise.all(
        modulesData.map(async (module: Module) => {
          const authStatus = await checkModuleAuthStatus(module.id);
          return { ...module, auth_status: authStatus };
        })
      );
      setModules(modulesWithAuth);
      setModulesLoaded(true);
    } catch (error) {
      setModulesLoaded(true);
      toast.error(tString('documentProcessing.toasts.loadModulesFailed'));
      console.error('Failed to load modules:', error);
    }
  };

  // Function to refresh module authentication status
  const refreshModuleAuthStatus = async () => {
    try {
      const updatedModules = await Promise.all(
        modules.map(async (module: Module) => {
          const authStatus = await checkModuleAuthStatus(module.id);
          return { ...module, auth_status: authStatus };
        })
      );
      setModules(updatedModules);

      // Also update the selected module if it exists
      if (processing.selectedModule) {
        const updatedSelectedModule = updatedModules.find(m => m.id === processing.selectedModule?.id);
        if (updatedSelectedModule) {
          setProcessing(prev => ({
            ...prev,
            selectedModule: updatedSelectedModule,
          }));
        }
      }
    } catch (error) {
      console.error('Failed to refresh module auth status:', error);
    }
  };

  // Expose refresh function globally for use by admin panels
  useEffect(() => {
    (window as any).refreshModuleAuthStatus = refreshModuleAuthStatus;
    return () => {
      delete (window as any).refreshModuleAuthStatus;
    };
  }, [modules, processing.selectedModule]);

  const checkModuleAuthStatus = async (moduleId: string): Promise<'authenticated' | 'expired' | 'unauthenticated'> => {
    try {
      const tokenKey = `module_auth_${moduleId}`;

      // ALWAYS check backend first - this is the source of truth
      // This ensures admin resets are immediately reflected
      try {
        const backendStatus = await authService.checkModuleAuthStatus(moduleId);

        if (!backendStatus.authenticated) {
          // Backend says not authenticated, clear local storage
          localStorage.removeItem(tokenKey);
          return 'unauthenticated';
        }

        // Backend confirms authentication - update local storage timestamp
        const authData = {
          timestamp: new Date().toISOString(),
          token: true
        };
        localStorage.setItem(tokenKey, JSON.stringify(authData));

        return 'authenticated';
      } catch (backendError) {
        // If backend check fails completely, clear local storage and show unauthenticated
        // This ensures we don't show false positive authentication status
        console.error('Backend auth check failed:', backendError);
        localStorage.removeItem(tokenKey);
        return 'unauthenticated';
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      return 'unauthenticated';
    }
  };

  const storeModuleAuth = (moduleId: string, token: string = 'dummy_token') => {
    const tokenKey = `module_auth_${moduleId}`;
    const authData = {
      token,
      timestamp: new Date().toISOString(),
      moduleId
    };
    localStorage.setItem(tokenKey, JSON.stringify(authData));
    // console.log(`Stored authentication for module ${moduleId}, expires in 7 days`);
  };

  async function handleFilesUpload(acceptedFiles: File[]) {
    try {
      // Check for duplicate file names
      const existingFileNames = uploadedFiles.map(f => f.name);
      const duplicates = acceptedFiles.filter(file => existingFileNames.includes(file.name));

      if (duplicates.length > 0) {
        const duplicateNames = duplicates.map(f => f.name).join(', ');
        toast.error(`File(s) already uploaded: ${duplicateNames}`, {
          autoClose: 5000,
        });
        return; // Stop upload
      }

      setProcessing(prev => ({ ...prev, isProcessing: true, currentStep: tString('documentProcessing.processing.uploadingFiles'), progress: 0 }));

      const uploadPromises = acceptedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        // TODO: Implement actual file upload API
        // const response = await authService.uploadFile(formData);

        // Simulate upload for now
        await new Promise(resolve => setTimeout(resolve, 1000));

        return {
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          type: file.type,
          size: file.size,
          url: URL.createObjectURL(file),
        };
      });

      const newFiles = await Promise.all(uploadPromises);
      setUploadedFiles(prev => [...prev, ...newFiles]);

      // Create document set if this is the first upload (for correlation validation)
      if (!documentSetId && newFiles.length > 0) {
        try {
          const setName = `Document Set ${new Date().toLocaleDateString()}`;
          const documentSet = await documentSetService.createDocumentSet(setName);
          setDocumentSetId(documentSet.id);
          // console.log('📋 Created document set:', documentSet.id);
        } catch (error) {
          console.error('Failed to create document set:', error);
          // Non-blocking error - correlation just won't be available
        }
      }

      setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0 }));
      toast.success(tString('documentProcessing.toasts.filesUploaded', newFiles.length));

      // Auto-scroll to Start Processing button after file preview cards render
      setTimeout(() => {
        if (startProcessingBtnRef.current) {
          startProcessingBtnRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
        }
      }, 600);
      // Second scroll after images/previews finish rendering
      setTimeout(() => {
        if (startProcessingBtnRef.current) {
          startProcessingBtnRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
        }
      }, 1500);
    } catch (error) {
      setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0 }));
      toast.error(tString('documentProcessing.toasts.uploadFailed'));
      console.error('Upload error:', error);
    }
  }

  // Fully reset the processing page back to the upload step. Clears uploaded
  // files, extracted data, validation/correlation state and navigates the user
  // back to the Upload step so they never land on a blank Analyse/Review tab.
  const handleResetForm = () => {
    setUploadedFiles([]);
    setAllComplianceReports([]);
    setActuallyUploadedFileIds([]);
    setDocumentSetId(null);
    setMergeResult(null);
    setMergeFallback(false);
    setCorrelationResult(null);
    setIsCorrelationValid(false);
    setModuleSelectionLocked(false);
    setFieldValidationErrors({});
    setGeneralValidationErrors([]);
    activePredictionsRef.current = {};
    changedPredictionFieldsRef.current = {};
    setProcessing(prev => ({
      ...prev,
      isProcessing: false,
      currentStep: '',
      progress: 0,
      extractedData: null,
      fieldSelections: {},
      runComplianceCheck: false,
    }));
    setExpandedStep('upload');
    setActiveWorkflowStep('upload');
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    // Also remove from actually uploaded file IDs
    setActuallyUploadedFileIds(prev => prev.filter(id => id !== fileId));
    // Remove compliance report for this file
    setAllComplianceReports(prev => prev.filter(report => report.fileId !== fileId));
    // Clear correlation results and document set since file composition changed
    setCorrelationResult(null);
    setDocumentSetId(null);
    setMergeResult(null);
    setMergeFallback(false);
    toast.info(tString('documentProcessing.toasts.fileRemoved'));

    // Clear extracted data if all files are removed
    if (uploadedFiles.length === 1) {
      setModuleSelectionLocked(false);  // Unlock module selection
      setAllComplianceReports([]); // Clear all compliance reports
      setAiClassifiedHSCodes(new Set());
      originalExtractedDataRef.current = null;
      setProcessing(prev => ({
        ...prev,
        extractedData: null,
        fieldSelections: {},
      }));
    }
  };

  const handleModuleSelect = async (module: Module) => {
    // Sync the appStore so the right-side drawer can highlight the selected card.
    setSelectedModule(module as any);

    // Always set the module as selected first
    setProcessing(prev => ({
      ...prev,
      selectedModule: module,
    }));

    // Persist selected module in localStorage
    localStorage.setItem('selectedModuleId', module.id);

    // CRITICAL: Re-check authentication status with backend (source of truth)
    // This ensures admin resets are immediately detected when user selects module
    const currentAuthStatus = await checkModuleAuthStatus(module.id);

    // Update the module's auth status in the modules list
    setModules(prevModules =>
      prevModules.map(m =>
        m.id === module.id ? { ...m, auth_status: currentAuthStatus } : m
      )
    );

    // Update the selected module's auth status
    const updatedModule = { ...module, auth_status: currentAuthStatus };
    setProcessing(prev => ({
      ...prev,
      selectedModule: updatedModule,
    }));
    setSelectedModule(updatedModule as any);

    // Then check if authentication is needed based on fresh status
    if (currentAuthStatus !== 'authenticated') {
      setAuthDialog({
        open: true,
        module: updatedModule,
        credentials: { username: '', password: '' },
      });
      return;
    }

    // Fetch module user details after authentication (EORI, etc.)
    try {
      const userDetails = await authService.getModuleUserDetails(module.id);
      if (userDetails && userDetails.found && userDetails.user_details) {
        const details = userDetails.user_details;
        // Get Ausfuhr EORI from backend (selected one or first available)
        const eoriNumber = details.ausfuhr_eori || '';
        // Construct EORI Niederlassungsnummer from EORI number
        // This is typically the EORI number + "0000" suffix
        const eoriNiederlassungsnummer = eoriNumber ? `${eoriNumber}` : '';

        setModuleUserDetails({
          atlas_user_id: details.atlas_user_id,
          atlas_mandant_id: details.atlas_mandant_id,
          atlas_kundennummer: details.atlas_kundennummer,
          email: details.email,
          modules_data: details.modules_data,
          ausfuhr_eori: eoriNumber,
          eoriNiederlassungsnummer: eoriNiederlassungsnummer,
        });
        // console.log('✅ Module user details loaded:', {
        //   atlas_user_id: details.atlas_user_id,
        //   atlas_kundennummer: details.atlas_kundennummer,
        //   eoriNiederlassungsnummer,
        // });
      }
    } catch (error) {
      console.error('Failed to fetch module user details:', error);
    }
  };

  // Bridge: when the global module drawer (in AppShell) selects a module,
  // route it through the same flow as the in-page picker — auth check,
  // EORI fetch, etc. Look up the full module by id from local state so we
  // pass the same shape the in-page picker uses.
  const handleModuleSelectRef = useRef(handleModuleSelect);
  useEffect(() => { handleModuleSelectRef.current = handleModuleSelect; });

  useEffect(() => {
    const onModuleSelected = (e: Event) => {
      const id = (e as CustomEvent).detail?.id;
      if (!id) return;
      const m = modules.find(x => x.id === id);
      if (!m) return;
      // Guard against re-entry: skip if the same module is already selected.
      if (processing.selectedModule?.id === id) return;
      handleModuleSelectRef.current(m);
    };
    window.addEventListener('module-selected', onModuleSelected);
    return () => window.removeEventListener('module-selected', onModuleSelected);
  }, [modules, processing.selectedModule?.id]);

  // Block sidebar navigation while processing is in flight. The dialog
  // is rendered by AppShell; this effect only manages the guard reason.
  useEffect(() => {
    if (processing.isProcessing) {
      setNavGuardReason(
        tString('documentProcessing.navGuard.activeReason') ||
        'Document processing is in progress.'
      );
    } else {
      setNavGuardReason(null);
    }
    return () => { setNavGuardReason(null); };
  }, [processing.isProcessing, setNavGuardReason, tString]);

  // Bridge: drawer's ATLAS sub-radio dispatches process-type-selected.
  // We update local state, persist to localStorage so the drawer can
  // rehydrate its mirror on reload, and tell the drawer that the value
  // landed (via process-type-changed) so its UI re-renders.
  useEffect(() => {
    const onProcessTypeSelected = (e: Event) => {
      const t = (e as CustomEvent).detail?.type;
      if (t !== 'ausfuhr' && t !== 'einfuhr' && t !== 'eas') return;
      setSelectedProcessType(t);
      try { localStorage.setItem('selectedProcessType', t); } catch { /* ignore */ }
      window.dispatchEvent(new CustomEvent('process-type-changed', { detail: { type: t } }));
    };
    window.addEventListener('process-type-selected', onProcessTypeSelected);
    return () => window.removeEventListener('process-type-selected', onProcessTypeSelected);
  }, []);

  // Mirror: drawer's second-level (variant) radio dispatches
  // sub-process-variant-selected. Drawer owns persistence; we just update
  // the in-memory mirror so consumers in this page can react. One-way —
  // no companion `-changed` re-dispatch needed.
  useEffect(() => {
    const onVariantSelected = (e: Event) => {
      const detail = (e as CustomEvent).detail as { subItemId?: string; variantId?: string };
      if (!detail?.subItemId || !detail?.variantId) return;
      setSelectedVariantByType(prev => ({ ...prev, [detail.subItemId!]: detail.variantId! }));
    };
    window.addEventListener('sub-process-variant-selected', onVariantSelected);
    return () => window.removeEventListener('sub-process-variant-selected', onVariantSelected);
  }, []);

  // Bridge: drawer's auth pill (Authenticate / Switch User) dispatches
  // module-auth-requested. We mirror the existing inline logic from the
  // old right-side panel: flip the switch-user ref when kind says so,
  // then open the auth dialog for that module.
  useEffect(() => {
    const onAuthRequested = (e: Event) => {
      const detail = (e as CustomEvent).detail as { moduleId?: string; kind?: 'authenticate' | 'switch_user' };
      if (!detail?.moduleId) return;
      const m = modules.find(x => x.id === detail.moduleId);
      if (!m) return;
      if (detail.kind === 'switch_user') {
        isSwitchUserRef.current = true;
      }
      setAuthDialog({ open: true, module: m, credentials: { username: '', password: '' } });
    };
    window.addEventListener('module-auth-requested', onAuthRequested);
    return () => window.removeEventListener('module-auth-requested', onAuthRequested);
  }, [modules]);

  // Dispatch processing-stats-changed so the drawer footer can render
  // file count, extracted-data indicator, and validation-error count
  // without owning that state itself.
  useEffect(() => {
    const errorCount = Object.keys(fieldValidationErrors).length + generalValidationErrors.length;
    window.dispatchEvent(new CustomEvent('processing-stats-changed', {
      detail: {
        uploadedFiles: uploadedFiles.length,
        extracted: !!processing.extractedData,
        validationErrors: errorCount,
      },
    }));
  }, [uploadedFiles.length, processing.extractedData, fieldValidationErrors, generalValidationErrors]);


  const handleStartProcessing = async () => {
    if (!processing.selectedModule || uploadedFiles.length === 0) {
      toast.error(tString('documentProcessing.toasts.selectModuleFirst'));
      return;
    }

    // CRITICAL: Verify authentication before processing
    // Re-check with backend to catch any admin resets
    const currentAuthStatus = await checkModuleAuthStatus(processing.selectedModule.id);

    if (currentAuthStatus !== 'authenticated') {
      // Update module status and show auth dialog
      const updatedModule = { ...processing.selectedModule, auth_status: currentAuthStatus };
      setProcessing(prev => ({
        ...prev,
        selectedModule: updatedModule,
      }));

      setAuthDialog({
        open: true,
        module: updatedModule,
        credentials: { username: '', password: '' },
      });

      toast.error(tString('documentProcessing.toasts.authenticationRequired'));
      return;
    }

    // CRITICAL: Fetch module user details for ATLAS modules (for EORI, userid, etc.)
    // This ensures moduleUserDetails is loaded before processing starts
    // Use a local variable since state updates are async and won't be available immediately
    // ALWAYS fetch if EORI is missing to ensure it's populated in the form
    let localModuleUserDetails = moduleUserDetails;
    const isAtlasModule = processing.selectedModule?.name?.toLowerCase().includes('atlas');
    const needsEoriFetch = !localModuleUserDetails || !localModuleUserDetails.eoriNiederlassungsnummer;

    if (needsEoriFetch && isAtlasModule) {
      // console.log('🔍 Fetching module user details for EORI...', {
      //   hasModuleUserDetails: !!localModuleUserDetails,
      //   hasEori: localModuleUserDetails?.eoriNiederlassungsnummer || 'missing'
      // });
      try {
        const userDetails = await authService.getModuleUserDetails(processing.selectedModule.id);
        if (userDetails && userDetails.found && userDetails.user_details) {
          const details = userDetails.user_details;
          const eoriNumber = details.ausfuhr_eori || '';
          const eoriNiederlassungsnummer = eoriNumber ? `${eoriNumber}` : '';

          localModuleUserDetails = {
            atlas_user_id: details.atlas_user_id,
            atlas_mandant_id: details.atlas_mandant_id,
            atlas_kundennummer: details.atlas_kundennummer,
            email: details.email,
            modules_data: details.modules_data,
            ausfuhr_eori: eoriNumber,
            eoriNiederlassungsnummer: eoriNiederlassungsnummer,
          };

          // Also update the state for future use
          setModuleUserDetails(localModuleUserDetails);
          // console.log('✅ Module user details loaded in handleStartProcessing:', {
          //   atlas_user_id: details.atlas_user_id,
          //   eoriNiederlassungsnummer,
          //   ausfuhr_eori: eoriNumber,
          // });
        } else {
          console.warn('⚠️ No user details found from getModuleUserDetails API');
        }
      } catch (error) {
        console.error('⚠️ Failed to fetch module user details in handleStartProcessing:', error);
      }
    } else if (isAtlasModule && localModuleUserDetails) {
      // console.log('✅ Using cached module user details:', {
      //   eoriNiederlassungsnummer: localModuleUserDetails.eoriNiederlassungsnummer,
      //   ausfuhr_eori: localModuleUserDetails.ausfuhr_eori,
      // });
    }

    // Create AbortController for cancellation
    const controller = new AbortController();
    setAbortController(controller);

    // Create a smooth progress animator that runs from 0 to 80% slowly
    let progressInterval: NodeJS.Timeout | null = null;
    let currentProgress = 0;
    const startSmoothProgress = () => {
      progressInterval = setInterval(() => {
        currentProgress += 1;
        if (currentProgress <= 80) {
          setProcessing(prev => ({ ...prev, progress: currentProgress }));
        } else {
          // Stop at 80% and wait for actual completion
          if (progressInterval) clearInterval(progressInterval);
        }
      }, 150); // Update every 150ms for smooth animation (80% in 12 seconds)
    };

    const stopSmoothProgress = () => {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
    };

    try {
      // Lock module selection to prevent switching mid-upload
      setModuleSelectionLocked(true);

      // Clear previous compliance reports and ML predictions when starting new processing
      setAllComplianceReports([]);
      setMlPredictions(null);
      setAiAppliedFields(new Set());
      setDocumentSnapshot(null);
      setAppliedPredictions(new Set());
      initialMlPredictionsFetchedRef.current = false;
      // console.log('🗑️ Cleared previous compliance reports and ML predictions');

      // CRITICAL FIX: Clear previously tracked uploaded file IDs to prevent
      // accumulation across multiple processing attempts with the same files
      setActuallyUploadedFileIds([]);
      // console.log('🗑️ Cleared previous uploaded file IDs');

      setProcessing(prev => ({
        ...prev,
        isProcessing: true,
        currentStep: tString('documentProcessing.processing.uploadingToServer'),
        progress: 0,
        extractedData: null,
        fieldSelections: {},
      }));

      // Collapse upload step so progress bar is fully visible
      setExpandedStep(null);

      // Scroll to bottom to show the processing progress bar
      // Use 600ms delay to ensure React has rendered the progress bar component
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
      }, 600);

      // Start the smooth progress animation
      startSmoothProgress();

      // Step 1: Upload files to backend in parallel and create database records
      const uploadedFileRecords: Array<UploadedFile & { fileId: string; transactionId: string; extractedData: any }> = [];
      // Track uploaded file IDs locally (not using state) to avoid stale closure issues
      const currentSessionUploadedIds: string[] = [];

      // Upload all files in parallel (prepare + upload per file inside Promise.allSettled)
      setProcessing(prev => ({
        ...prev,
        currentStep: `Processing ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}...`,
      }));

      let completedCount = 0;
      const uploadPromises = uploadedFiles.map(async (file) => {
        // Prepare file (blob fetch + FormData)
        const originalFile = await fetch(file.url).then(r => r.blob()).then(blob =>
          new File([blob], file.name, { type: file.type })
        );
        const formData = new FormData();
        formData.append('file', originalFile);
        formData.append('module_id', processing.selectedModule!.id);

        // Upload file
        const uploadResponse = await authService.uploadFile(formData, controller.signal);
        completedCount++;
        setProcessing(prev => ({
          ...prev,
          currentStep: `Processing files... (${completedCount} of ${uploadedFiles.length} complete)`,
        }));
        return { file, uploadResponse };
      });

      const results = await Promise.allSettled(uploadPromises);

      // Process results — collect successes, handle failures
      const failedFiles: string[] = [];
      let authRequired = false;
      let cancelError: any = null;

      for (let index = 0; index < results.length; index++) {
        const result = results[index];
        if (result.status === 'fulfilled') {
          const { file, uploadResponse } = result.value;

          // Check if compliance authentication is required
          if (uploadResponse.compliance_check?.auth_required) {
            authRequired = true;
            setProcessing(prev => ({
              ...prev,
              isProcessing: false,
              error: 'BEO Sanctions authentication required'
            }));
            setAuthRequiredDialog({
              open: true,
              moduleId: uploadResponse.compliance_check.sanctions_module_id,
              moduleName: 'BEO Sanctions',
              message: 'Please authenticate with BEO Sanctions to enable automatic compliance screening for your documents.'
            });
            break;
          }

          uploadedFileRecords.push({
            ...file,
            fileId: uploadResponse.file_id,
            transactionId: uploadResponse.transaction_id,
            extractedData: uploadResponse.extracted_data,
            complianceCheck: uploadResponse.compliance_check
          });

          // Link file to document set if one exists
          if (documentSetId && uploadResponse.file_id) {
            try {
              await documentSetService.linkFileToSet(documentSetId, uploadResponse.file_id);
              currentSessionUploadedIds.push(uploadResponse.file_id);
              setActuallyUploadedFileIds(prev => [...prev, uploadResponse.file_id]);
            } catch (linkError) {
              console.error(`Failed to link file to document set:`, linkError);
            }
          }
        } else {
          // Check for cancellation
          const error = result.reason;
          if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
            cancelError = error;
            continue; // Don't break — let remaining results be processed
          }
          failedFiles.push(uploadedFiles[index].name);
          console.error(`Failed to upload ${uploadedFiles[index].name}:`, error);
        }
      }

      // Handle cancellation after processing all results
      if (cancelError && uploadedFileRecords.length === 0) {
        throw cancelError;
      }

      if (authRequired) {
        stopSmoothProgress();
        setModuleSelectionLocked(false);
        return; // Stop processing — auth dialog is already shown
      }

      if (uploadedFileRecords.length === 0) {
        throw new Error(`All ${uploadedFiles.length} file(s) failed to upload`);
      }

      if (failedFiles.length > 0) {
        toast.warning(`${failedFiles.length} file(s) failed to upload: ${failedFiles.join(', ')}`);
      }

      // Set compliance status from last successful upload (compliance is currently disabled)
      if (uploadedFileRecords.length > 0) {
        const lastRecord = uploadedFileRecords[uploadedFileRecords.length - 1];
        const lastResult = results.find(r =>
          r.status === 'fulfilled' && r.value.file.id === lastRecord.id
        );
        if (lastResult?.status === 'fulfilled') {
          const { uploadResponse } = lastResult.value;
          setComplianceStatus(uploadResponse.compliance_status || 'disabled');
          if (uploadResponse.compliance_status === 'in_progress') {
            setUploadedFileId(uploadResponse.file_id);
            setUploadComplete(true);
            // compliance running silently
          } else if (uploadResponse.compliance_status === 'requires_auth') {
            // auth warning suppressed
          } else if (uploadResponse.compliance_status === 'disabled') {
            // disabled
          } else if (uploadResponse.compliance_report) {
            setPolledComplianceReport(uploadResponse.compliance_report);
          }
        }
      }

      setProcessing(prev => ({
        ...prev,
        currentStep: 'Processing extracted data...',
      }));

      // Step 2: Intelligently merge data from multiple documents OR use single document extraction
      let combinedExtractedData: any = {};

      // Reset merge UI state at the start of every run so a re-process that
      // lands in the single-doc branch can't show a stale banner/provenance
      setMergeResult(null);
      setMergeFallback(false);

      // Use local array (currentSessionUploadedIds) instead of state (actuallyUploadedFileIds)
      // to avoid stale closure issues - state updates are async and won't be reflected here
      if (currentSessionUploadedIds.length >= 2 && documentSetId) {
        // Multi-document: Use intelligent merging
        setProcessing(prev => ({
          ...prev,
          currentStep: 'Intelligently merging data from multiple documents...',
          progress: 65
        }));

        try {
          // console.log(`🔀 Processing and merging ${currentSessionUploadedIds.length} documents...`);

          const mergeResult = await documentSetService.processAndMerge(documentSetId, {
            auto_resolve_conflicts: true,
            validate_correlation: false,  // Already validated separately
            declaration_type: 'export',
            target_module: 'BEO_ATLAS'
          });

          // console.log('✅ Multi-document merge complete:', mergeResult);

          // Use the intelligently merged data (no field prefixes!)
          combinedExtractedData = mergeResult.merged_data;
          setMergeResult(mergeResult);
          setMergeFallback(false);

          // merge success — covered by main processing success toast

        } catch (mergeError) {
          console.error('⚠️ Multi-document merge failed, using individual extractions:', mergeError);
          setMergeResult(null);
          setMergeFallback(true);  // surfaced by MergeStatusBanner — no longer silent
          // merge warning suppressed — fallback is handled silently

          // Fallback: Use first file's extraction (no prefix)
          if (uploadedFileRecords[0]?.extractedData) {
            Object.keys(uploadedFileRecords[0].extractedData).forEach(key => {
              if (key !== 'metadata' && key !== '_compliance_extraction') {
                combinedExtractedData[key] = uploadedFileRecords[0].extractedData[key];
              }
            });
          }
        }
      } else {
        // Single document: Use direct extraction (no prefix)
        uploadedFileRecords.forEach((fileRecord) => {
          if (fileRecord.extractedData) {
            Object.keys(fileRecord.extractedData).forEach(key => {
              if (key !== 'metadata' && key !== '_compliance_extraction') {
                combinedExtractedData[key] = fileRecord.extractedData[key];
              }
            });
          }
        });
      }

      // Snapshot the pure document extraction (before profile/default/ML injection)
      // so the Extracted tab can be classified by true document provenance.
      setDocumentSnapshot(JSON.parse(JSON.stringify(combinedExtractedData)));

      // Inject module user details (EORI, etc.) into the extracted data if available
      // These are system-generated fields from GetClientDetails - ALWAYS use these values
      // The user's EORI from their account takes precedence over any extracted values
      // Use localModuleUserDetails which was fetched at the start of this function
      if (localModuleUserDetails) {
        // console.log('📝 Injecting module user details into extracted data...');
        // console.log('  - localModuleUserDetails available:', {
        //   eoriNiederlassungsnummer: localModuleUserDetails.eoriNiederlassungsnummer,
        //   atlas_user_id: localModuleUserDetails.atlas_user_id,
        //   ausfuhr_eori: localModuleUserDetails.ausfuhr_eori
        // });

        // Ensure kopf exists
        if (!combinedExtractedData.kopf) {
          combinedExtractedData.kopf = {};
        }

        // ALWAYS inject EORI from user's GetClientDetails data (overwrite any extracted value)
        // This ensures the user's registered EORI is always used
        if (localModuleUserDetails.eoriNiederlassungsnummer) {
          combinedExtractedData.kopf.eoriNiederlassungsnummer = localModuleUserDetails.eoriNiederlassungsnummer;
          // console.log(`  - eoriNiederlassungsnummer: ${localModuleUserDetails.eoriNiederlassungsnummer} (from GetClientDetails)`);
        }

        // ALWAYS inject userid from GetClientDetails
        if (localModuleUserDetails.atlas_user_id) {
          combinedExtractedData.kopf.userid = localModuleUserDetails.atlas_user_id;
          // console.log(`  - userid: ${localModuleUserDetails.atlas_user_id} (from GetClientDetails)`);
        }

        // Also inject EORI into anmelder (declarant) TIN
        if (localModuleUserDetails.ausfuhr_eori) {
          if (!combinedExtractedData.kopf.anmelder) {
            combinedExtractedData.kopf.anmelder = {};
          }
          // Only set anmelder TIN if not already populated (avoid overwriting
          // declarant TIN with exporter EORI). Split off the branch number:
          // BEO returns "DE90001270000" but ATLAS wants "DE9000127" + "0000",
          // and injecting the joined form here meant the field had to be
          // shortened by hand on every declaration. See applyPartyEori.
          applyPartyEori(combinedExtractedData.kopf.anmelder, localModuleUserDetails.ausfuhr_eori);
          // console.log(`  - anmelder.tin: ${localModuleUserDetails.ausfuhr_eori} (from GetClientDetails)`);
        }

        // console.log('✅ Module user details injected into kopf section');
        // console.log('  Final kopf.eoriNiederlassungsnummer:', combinedExtractedData.kopf.eoriNiederlassungsnummer);
        // console.log('  Final kopf.userid:', combinedExtractedData.kopf.userid);
        // console.log('  Final kopf.anmelder.tin:', combinedExtractedData.kopf.anmelder?.tin);
      } else {
        // console.log('⚠️ localModuleUserDetails not available - EORI will not be pre-filled');
        // console.log('  - Please ensure the module is authenticated before uploading documents');
      }

      // Auto-generate missing required fields
      // Ensure kopf object exists
      if (!combinedExtractedData.kopf) {
        combinedExtractedData.kopf = {};
      }

      // 1. Auto-generate Declaration Timestamp if missing
      if (!combinedExtractedData.kopf.zeitpunktderAnmeldung) {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        combinedExtractedData.kopf.zeitpunktderAnmeldung = `${year}${month}${day}${hours}${minutes}${seconds}`;
      }

      // Helper function to format date as DD-MM-YYYY (dashes format accepted by gRPC)
      // Normalize date to YYYYMMDD for ATLAS (BEO schema: "Date (n8)")
      const normalizeDateToYYYYMMDD = (dateValue: string | undefined | null): string => {
        if (!dateValue || dateValue === '') return '';
        const trimmed = String(dateValue).trim();
        if (/^\d{8}$/.test(trimmed)) return trimmed;
        const dotMatch = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (dotMatch) return `${dotMatch[3]}${dotMatch[2]}${dotMatch[1]}`;
        const dashMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (dashMatch) return `${dashMatch[3]}${dashMatch[2]}${dashMatch[1]}`;
        const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) return `${isoMatch[1]}${isoMatch[2]}${isoMatch[3]}`;
        if (/^\d{14}$/.test(trimmed)) return trimmed.substring(0, 8);
        let date: Date | null = null;
        try { date = new Date(trimmed); } catch { /* ignore */ }
        if (date && !isNaN(date.getTime())) {
          return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
        }
        return trimmed;
      };

      // Add hidden date fields as empty strings for ATLAS compliance
      if (!combinedExtractedData.kopf.zeitpunktDerGestellung) {
        combinedExtractedData.kopf.zeitpunktDerGestellung = "";
      }
      if (!combinedExtractedData.kopf.zeitpunktdesEndesderLadetatigkeit) {
        combinedExtractedData.kopf.zeitpunktdesEndesderLadetatigkeit = "";
      }
      // Normalize date fields to YYYYMMDD
      if (combinedExtractedData.kopf.massgeblichesDatum) {
        combinedExtractedData.kopf.massgeblichesDatum = normalizeDateToYYYYMMDD(combinedExtractedData.kopf.massgeblichesDatum);
      } else {
        combinedExtractedData.kopf.massgeblichesDatum = "";
      }
      if (combinedExtractedData.kopf.kopfDatumdesAusgangs) {
        combinedExtractedData.kopf.kopfDatumdesAusgangs = normalizeDateToYYYYMMDD(combinedExtractedData.kopf.kopfDatumdesAusgangs);
      } else {
        combinedExtractedData.kopf.kopfDatumdesAusgangs = "";
      }

      // 2. Add user profile defaults for exporter if available and missing
      // NOTE: Company fields are not yet available in User interface
      // TODO: Add company fields to User model and uncomment this section
      /*
      const userProfile = user;
      if (userProfile) {
        // Add company details as exporter if missing
        if (!combinedExtractedData.kopf.ausfuehrer) {
          const exporterDetails: any = {};
          if (userProfile.company_name) {
            exporterDetails.name = userProfile.company_name;
          }
          if (userProfile.company_city) {
            exporterDetails.ort = userProfile.company_city;
          }
          if (userProfile.company_postcode) {
            exporterDetails.plz = userProfile.company_postcode;
          }
          if (userProfile.company_country) {
            exporterDetails.land = userProfile.company_country;
          }
          if (Object.keys(exporterDetails).length > 0) {
            combinedExtractedData.kopf.ausfuehrer = exporterDetails;
          }
        }

        // Add customs office if available and missing
        if (userProfile.customs_office && !combinedExtractedData.kopf.ausstellungszollstelle) {
          combinedExtractedData.kopf.ausstellungszollstelle = userProfile.customs_office;
        }
        
        // Add presentation customs office (gestellungszollstelle) from user profile
        if (userProfile.atlas_gestellungszollstelle && !combinedExtractedData.kopf.gestellungszollstelle) {
          combinedExtractedData.kopf.gestellungszollstelle = userProfile.atlas_gestellungszollstelle;
        }
      }
      */

      // 3. Calculate total gross weight if missing but positions have weights
      if (!combinedExtractedData.kopf.rohmasse && combinedExtractedData.positionen && Array.isArray(combinedExtractedData.positionen)) {
        let totalWeight = 0;
        combinedExtractedData.positionen.forEach((position: any) => {
          if (position.rohmasse) {
            const weight = parseFloat(position.rohmasse);
            if (!isNaN(weight)) {
              totalWeight += weight;
            }
          }
        });
        if (totalWeight > 0) {
          combinedExtractedData.kopf.rohmasse = totalWeight.toString();
        }
      }

      // 4. Bidirectional weight auto-filling for positions
      // If gross weight (rohmasse) is null but net weight (eigenmasse) exists, copy net to gross
      // If net weight (eigenmasse) is null but gross weight (rohmasse) exists, copy gross to net
      if (combinedExtractedData.positionen && Array.isArray(combinedExtractedData.positionen)) {
        combinedExtractedData.positionen.forEach((position: any, index: number) => {
          // Handle short field names (rohmasse/eigenmasse)
          if (!position.rohmasse && position.eigenmasse) {
            position.rohmasse = position.eigenmasse;
            // console.log(`Position ${index + 1}: Copied net weight (${position.eigenmasse}) to gross weight`);
          } else if (!position.eigenmasse && position.rohmasse) {
            position.eigenmasse = position.rohmasse;
            // console.log(`Position ${index + 1}: Copied gross weight (${position.rohmasse}) to net weight`);
          }

          // Handle mapped field names (wareRohmasse/wareEigenmasse)
          if (!position.wareRohmasse && position.wareEigenmasse) {
            position.wareRohmasse = position.wareEigenmasse;
            // console.log(`Position ${index + 1}: Copied net weight (${position.wareEigenmasse}) to gross weight (mapped fields)`);
          } else if (!position.wareEigenmasse && position.wareRohmasse) {
            position.wareEigenmasse = position.wareRohmasse;
            // console.log(`Position ${index + 1}: Copied gross weight (${position.wareRohmasse}) to net weight (mapped fields)`);
          }
        });
      }

      // Remove flat ML-injected fields that don't map to any form field
      // These cause phantom bewilligung entries if left in the data
      if (combinedExtractedData.kopf) {
        delete combinedExtractedData.kopf.bewilligungZugelassenerAusfuhrer;
        delete combinedExtractedData.kopf.bewilligungArt;
      }

      // Normalize position items: flatten nested `ware` sub-object into the position level.
      // The AI may return position[n].ware.wareWarenbezeichnung but the form reads
      // position[n].wareWarenbezeichnung directly.
      if (Array.isArray(combinedExtractedData.position)) {
        combinedExtractedData.position = combinedExtractedData.position.map((pos: any) => {
          if (!pos || typeof pos !== 'object') return pos;

          // 1. Flatten `ware` sub-object (AI sometimes nests ware fields)
          let normalized = pos;
          if (pos.ware && typeof pos.ware === 'object') {
            const { ware, ...rest } = pos;
            normalized = { ...rest, ...ware };
          }

          // 2. Move flat packaging fields into verpackung[0] where they belong in ATLAS v2.1
          // AI may return artderPackstucke/anzahlderPackstucke/versandzeichen at position level
          const packagingFields: Record<string, string> = {
            artderPackstucke: 'artderVerpackung',
            artderVerpackung: 'artderVerpackung',
            anzahlderPackstucke: 'anzahlderPackstucke',
            versandzeichen: 'versandzeichen',
          };
          const verpackungData: Record<string, any> = {};
          const toRemove: string[] = [];
          Object.entries(packagingFields).forEach(([flatKey, nestedKey]) => {
            const val = normalized[flatKey];
            if (val !== undefined && val !== null && val !== '') {
              verpackungData[nestedKey] = val;
              if (flatKey !== nestedKey) toRemove.push(flatKey);
            }
          });
          if (Object.keys(verpackungData).length > 0) {
            const existingVerpackung = Array.isArray(normalized.verpackung) ? normalized.verpackung : [];
            if (existingVerpackung.length === 0) {
              // No existing verpackung: create from flat fields
              normalized = { ...normalized, verpackung: [verpackungData] };
            } else {
              // Merge into first verpackung entry (fill empty slots only)
              const first = { ...existingVerpackung[0] };
              Object.entries(verpackungData).forEach(([k, v]) => {
                if (!first[k] || first[k] === '') first[k] = v;
              });
              normalized = { ...normalized, verpackung: [first, ...existingVerpackung.slice(1)] };
            }
            // Remove flat keys that were migrated
            toRemove.forEach(k => { delete normalized[k]; });
          }

          return normalized;
        });
      }

      // Initialize field selections (all checked by default)
      const fieldSelections: Record<string, boolean> = {};
      Object.keys(flattenObject(combinedExtractedData)).forEach(key => {
        fieldSelections[key] = true;
      });

      // Stop the smooth progress animation
      stopSmoothProgress();

      // Jump to 100% when complete
      // console.log('📊 Setting extractedData to state with EORI:', {
      //   'kopf.eoriNiederlassungsnummer': combinedExtractedData?.kopf?.eoriNiederlassungsnummer,
      //   'kopf.userid': combinedExtractedData?.kopf?.userid,
      //   'kopf.anmelder.tin': combinedExtractedData?.kopf?.anmelder?.tin,
      // });
      setProcessing(prev => ({
        ...prev,
        isProcessing: false,
        progress: 100,
        extractedData: combinedExtractedData,
        fieldSelections,
        currentStep: 'Files uploaded and processed successfully',
      }));

      // Auto-expand review step after processing completes
      setExpandedStep('review');
      setActiveWorkflowStep('analyse');

      // Trigger gRPC auto-validation immediately after extraction
      setGrpcValidationTrigger(prev => prev + 1);

      // Fire parallel HS code classification for line items missing an HS code.
      // This is intentionally not awaited — runs silently alongside the UI render.
      autoFillMissingHSCodes(combinedExtractedData);

      // Update uploadedFiles with backend file IDs and extracted data
      setUploadedFiles(prev => prev.map((file, index) => ({
        ...file,
        fileId: uploadedFileRecords[index]?.fileId,
        transactionId: uploadedFileRecords[index]?.transactionId,
        extractedData: uploadedFileRecords[index]?.extractedData
      })));

      toast.success(tString('documentProcessing.toasts.filesUploadedProcessed', uploadedFileRecords.length));

      // Extraction warnings logged to console only — no popup (form fields are visible for review)
      uploadedFileRecords.forEach((fileRecord) => {
        if (fileRecord.extractedData?.metadata?.show_warning_to_user) {
          const errorMsg = fileRecord.extractedData.metadata.error_message || "AI extraction had issues.";
          const extractionMethod = fileRecord.extractedData.metadata.extraction_method || "unknown";
          console.warn(`⚠️ Extraction warning for ${fileRecord.name}: [${extractionMethod}] ${errorMsg}`);
        }
      });

      // Compliance polling is handled by useCompliancePolling hook
      // No manual polling needed here

    } catch (error: any) {
      // Stop the smooth progress animation on error
      stopSmoothProgress();

      // Clear abort controller
      setAbortController(null);

      // Check if error was due to cancellation (axios uses code 'ERR_CANCELED')
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.message?.includes('cancel')) {
        // console.log('✋ Processing cancelled by user');
        toast.info(tString('documentProcessing.toasts.processingCancelled'));
        setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0 }));
        setModuleSelectionLocked(false); // Unlock module selection
        return;
      }

      setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0 }));
      toast.error(`Processing failed: ${error.message || error}`);
      console.error('Processing error:', error);
    } finally {
      // Clear abort controller on completion
      setAbortController(null);
    }
  };


  const handleCancelProcessing = () => {
    if (abortController) {
      // console.log('🛑 Cancelling processing...');
      abortController.abort();
      setAbortController(null);
      setModuleSelectionLocked(false); // Unlock module selection
      toast.info(tString('documentProcessing.toasts.cancellingProcessing'));
    }
  };

  const handleSendData = async () => {
    // Clear the previous attempt's findings before this one runs.
    // Both setters below accumulate — the field map with {...prev} and the
    // banner with [...prev] — so without this a resolved error survives every
    // later submit. Setting Container Indicator to "0" genuinely clears the
    // transportausrustung requirement (verified against the validator), but the
    // field went on displaying the old message no matter what was entered, so
    // there was no way to tell a fixed field from a broken one.
    setFieldValidationErrors({});
    setGeneralValidationErrors([]);

    // Prevent duplicate calls (e.g. double-click, or auto-validate re-trigger during send)
    if (isSendingRef.current) {
      // console.log('⚠️ handleSendData already in progress — ignoring duplicate call');
      return;
    }

    // console.log('========================================');
    // console.log('🚀 VALIDATE AND SEND - STARTED');
    // console.log('========================================');
    // console.log('📅 Timestamp:', new Date().toISOString());

    if (!processing.selectedModule || !processing.extractedData) {
      console.error('❌ Missing module or extracted data');
      // console.log('  - Selected Module:', processing.selectedModule);
      // console.log('  - Extracted Data exists:', !!processing.extractedData);
      return;
    }

    // console.log('📦 Module:', processing.selectedModule.name);
    // console.log('📦 Module ID:', processing.selectedModule.id);
    // console.log('📋 Send Partial Data:', sendPartialData);

    // Helper function to format dates to ATLAS format (TT.MM.JJJJ)
    // Normalize date to YYYYMMDD for ATLAS (BEO schema: "Date (n8)")
    const formatDateForATLAS = (dateValue: string | undefined | null): string => {
      if (!dateValue || dateValue === '') return '';
      const trimmed = String(dateValue).trim();
      if (/^\d{8}$/.test(trimmed)) return trimmed;
      const dotMatch = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (dotMatch) return `${dotMatch[3]}${dotMatch[2]}${dotMatch[1]}`;
      const dashMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (dashMatch) return `${dashMatch[3]}${dashMatch[2]}${dashMatch[1]}`;
      const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return `${isoMatch[1]}${isoMatch[2]}${isoMatch[3]}`;
      if (/^\d{14}$/.test(trimmed)) return trimmed.substring(0, 8);
      let date: Date | null = null;
      try { date = new Date(trimmed); } catch { /* ignore */ }
      if (date && !isNaN(date.getTime())) {
        return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
      }
      return trimmed;
    };

    isSendingRef.current = true;
    setIsSending(true);

    try {
      // ===== STEP 1: Check Authentication =====
      // console.log('\n📍 STEP 1: Checking module authentication...');
      setProcessing(prev => ({ ...prev, isProcessing: true, currentStep: 'Checking authentication...', progress: 25 }));

      // First check if user is authenticated with the module
      const authStatus = await authService.checkModuleAuthStatus(processing.selectedModule.id);
      // console.log('🔐 Auth Status:', authStatus);

      if (!authStatus.authenticated) {
        console.error('❌ STEP 1 FAILED: Not authenticated');
        // console.log('  - Message:', authStatus.message);
        setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0 }));

        if (authStatus.message && authStatus.message.includes('expired')) {
          toast.error(`Authentication token expired for ${processing.selectedModule.name}. Please re-authenticate.`);
        } else {
          toast.error(`Authentication required for ${processing.selectedModule.name}. Please authenticate first.`);
        }

        // Open authentication dialog automatically
        setAuthDialog({
          open: true,
          module: processing.selectedModule,
          credentials: { username: '', password: '' }
        });

        return;
      }
      // console.log('✅ STEP 1 COMPLETE: Authentication verified');

      // CRITICAL: Fetch module user details if not already available (for EORI, userid, etc.)
      // This ensures moduleUserDetails is loaded before validation/submission
      // Use a local variable since state updates are async and won't be available immediately
      let localModuleUserDetails = moduleUserDetails;
      if (!localModuleUserDetails && processing.selectedModule?.name?.toLowerCase().includes('atlas')) {
        // console.log('🔍 moduleUserDetails not available in handleSendData, fetching now...');
        try {
          const userDetails = await authService.getModuleUserDetails(processing.selectedModule.id);
          if (userDetails && userDetails.found && userDetails.user_details) {
            const details = userDetails.user_details;
            const eoriNumber = details.ausfuhr_eori || '';
            const eoriNiederlassungsnummer = eoriNumber ? `${eoriNumber}` : '';

            localModuleUserDetails = {
              atlas_user_id: details.atlas_user_id,
              atlas_mandant_id: details.atlas_mandant_id,
              atlas_kundennummer: details.atlas_kundennummer,
              email: details.email,
              modules_data: details.modules_data,
              ausfuhr_eori: eoriNumber,
              eoriNiederlassungsnummer: eoriNiederlassungsnummer,
            };

            // Also update the state for future use
            setModuleUserDetails(localModuleUserDetails);
            // console.log('✅ Module user details loaded in handleSendData:', {
            //   atlas_user_id: details.atlas_user_id,
            //   eoriNiederlassungsnummer,
            //   ausfuhr_eori: eoriNumber,
            // });
          }
        } catch (error) {
          console.error('⚠️ Failed to fetch module user details in handleSendData:', error);
        }
      }

      // ===== STEP 2: Local Field Validation - COMMENTED OUT FOR TESTING =====
      // Only gRPC and ATLAS API errors should be shown
      // console.log('\n📍 STEP 2: Local field validation - SKIPPED (disabled for testing)');
      setProcessing(prev => ({ ...prev, currentStep: 'Validating data...', progress: 40 }));
      // console.log('  - Local required field validation is DISABLED');
      // console.log('  - Only gRPC validation and ATLAS API errors will be shown');
      // console.log('✅ STEP 2 COMPLETE: Local validation skipped (disabled)');

      /* COMMENTED OUT - Local required field validation
      // Validate document using backend API (only if NOT sending partial data)
      if (processing.selectedModule.name?.toLowerCase().includes('atlas') && !sendPartialData) {
        // console.log('  - Checking ATLAS required fields (sendPartialData=false)...');
        const missingRequired = ATLAS_FIELDS.filter(field => {
          // Check the primary field path
          let value = getNestedValue(processing.extractedData, field.path);

          // For ausfuhrer fields, also check under kopf.ausfuhrer if not found at root
          if (!value && field.path.startsWith('ausfuhrer.')) {
            const altPath = 'kopf.' + field.path;
            value = getNestedValue(processing.extractedData, altPath);
          }

          // For empfanger fields, also check without kopf prefix if not found
          if (!value && field.path.startsWith('kopf.empfanger.')) {
            const altPath = field.path.replace('kopf.', '');
            value = getNestedValue(processing.extractedData, altPath);
          }

          return field.required && (!value || value === '' || value === 'null');
        });

        if (missingRequired.length > 0) {
          console.error('❌ STEP 2 FAILED: Missing required fields');
          // console.log('  - Missing fields:', missingRequired.map(f => f.label));
          setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0 }));
          const missingFieldNames = missingRequired.map(f => f.label).slice(0, 5);
          const moreCount = missingRequired.length > 5
            ? tString('documentProcessing.errors.missingFieldsList', missingFieldNames.join('\n'), missingRequired.length - 5)
            : '';
          toast.error(
            `❌ ${tString('documentProcessing.errors.missingAtlasFields', missingRequired.length)}:\n\n` +
            missingFieldNames.join('\n') + moreCount +
            `\n\n${tString('documentProcessing.errors.fillRedFields')}`,
            { autoClose: 8000 }
          );

          // Auto-scroll to first missing/error field after toast
          setTimeout(() => {
            // console.log('📍 Auto-scroll: Looking for missing required fields...');

            // First, try to find elements with 'missing' status (red fields)
            const missingFields = document.querySelectorAll('[data-field-status="missing"]');
            // console.log(`  Found ${missingFields.length} fields with missing status`);

            if (missingFields.length > 0) {
              const firstMissingField = missingFields[0];
              // console.log('  Scrolling to first missing field');
              firstMissingField.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
              });
              // Try to focus the input
              const inputElement = firstMissingField.querySelector('input, textarea') as HTMLElement;
              if (inputElement) {
                inputElement.focus();
              }
              return;
            }

            // Fallback: try to find elements with Mui-error class
            const errorFields = document.querySelectorAll('.Mui-error');
            // console.log(`  Found ${errorFields.length} elements with .Mui-error class`);

            if (errorFields.length > 0) {
              const firstErrorElement = errorFields[0];
              // console.log('  Scrolling to first error element');
              firstErrorElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
              });
              return;
            }

            // Last fallback: scroll to extracted data table
            const extractedDataSection = document.querySelector('[data-testid="extracted-data-table"]') ||
                                         document.querySelector('.MuiTableContainer-root');
            if (extractedDataSection) {
              // console.log('  Scrolling to extracted data table');
              extractedDataSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
              });
            }
          }, 300);

          return;
        }
        // console.log('  - All required fields present');
      } else {
        // console.log('  - Skipping local validation (sendPartialData=true or non-ATLAS module)');
      }
      END COMMENTED OUT */

      // ===== STEP 3: Prepare Data =====
      // console.log('\n📍 STEP 3: Preparing data for transmission...');
      setProcessing(prev => ({ ...prev, currentStep: 'Preparing data for transmission...', progress: 50 }));

      // Filter data based on field selections
      const selectedData = filterSelectedFields(processing.extractedData, processing.fieldSelections);

      // Remove flat ML-injected fields that are not form fields
      if (selectedData.kopf) {
        delete selectedData.kopf.bewilligungZugelassenerAusfuhrer;
        delete selectedData.kopf.bewilligungArt;
      }

      // Log detailed data structure for debugging
      // console.log('📦 DATA STRUCTURE FOR SUBMISSION:');
      // console.log('  - Top-level keys:', Object.keys(selectedData));
      if (selectedData.kopf) {
        // console.log('  - kopf fields:', Object.keys(selectedData.kopf));
        if (selectedData.kopf.anmelder) {
          // console.log('    - anmelder fields:', Object.keys(selectedData.kopf.anmelder));
        }
        if (selectedData.kopf.ausfuhrer) {
          // console.log('    - ausfuhrer fields:', Object.keys(selectedData.kopf.ausfuhrer));
        }
        if (selectedData.kopf.empfanger) {
          // console.log('    - empfanger fields:', Object.keys(selectedData.kopf.empfanger));
        }
      }
      if (selectedData.position && Array.isArray(selectedData.position)) {
        // console.log(`  - position array: ${selectedData.position.length} items`);
        selectedData.position.forEach((pos: any, idx: number) => {
          if (pos && typeof pos === 'object') {
            // console.log(`    - position[${idx}] fields:`, Object.keys(pos));
          }
        });
      }

      // Add file information to the data payload
      const filesWithIds = uploadedFiles.filter(file => file.fileId);
      if (filesWithIds.length > 0) {
        selectedData.file_ids = filesWithIds.map(file => file.fileId);
        selectedData.file_names = filesWithIds.map(file => file.name);
        selectedData.transaction_ids = filesWithIds.map(file => file.transactionId);
        // console.log('  - Attached files:', filesWithIds.length);
      }
      // console.log('✅ STEP 3 COMPLETE: Data prepared');

      // ===== STEP 4: gRPC VALIDATION =====
      // Validate data using external gRPC service for ATLAS modules
      if (processing.selectedModule.name?.toLowerCase().includes('atlas')) {
        // console.log('\n📍 STEP 4: gRPC Validation...');

        // Ensure EORI from GetClientDetails is injected before validation
        // Use localModuleUserDetails which was fetched at the start of this function
        if (localModuleUserDetails) {
          if (!selectedData.kopf) {
            selectedData.kopf = {};
          }
          // Always use EORI from user's GetClientDetails data
          if (localModuleUserDetails.eoriNiederlassungsnummer) {
            selectedData.kopf.eoriNiederlassungsnummer = localModuleUserDetails.eoriNiederlassungsnummer;
            // console.log(`  📝 EORI injected for validation: ${localModuleUserDetails.eoriNiederlassungsnummer}`);
          }
          if (localModuleUserDetails.atlas_user_id) {
            selectedData.kopf.userid = localModuleUserDetails.atlas_user_id;
            // console.log(`  📝 UserID injected for validation: ${localModuleUserDetails.atlas_user_id}`);
          }
          if (localModuleUserDetails.ausfuhr_eori) {
            if (!selectedData.kopf.anmelder) {
              selectedData.kopf.anmelder = {};
            }
            applyPartyEori(selectedData.kopf.anmelder, localModuleUserDetails.ausfuhr_eori);
            // console.log(`  📝 Anmelder TIN injected for validation: ${localModuleUserDetails.ausfuhr_eori}`);
          }
        }

        // Normalize date fields to YYYYMMDD for ATLAS validation (BEO schema: "Date (n8)")
        const normalizeDateToYYYYMMDD = (dateValue: string | undefined | null): string => {
          if (!dateValue || dateValue === '') return '';
          const trimmed = String(dateValue).trim();
          // Already in YYYYMMDD format (8 digits)
          if (/^\d{8}$/.test(trimmed)) return trimmed;
          // DD.MM.YYYY (dots) → YYYYMMDD
          const dotMatch = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
          if (dotMatch) return `${dotMatch[3]}${dotMatch[2]}${dotMatch[1]}`;
          // DD-MM-YYYY (dashes) → YYYYMMDD
          const dashMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
          if (dashMatch) return `${dashMatch[3]}${dashMatch[2]}${dashMatch[1]}`;
          // YYYY-MM-DD (ISO) → YYYYMMDD
          const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (isoMatch) return `${isoMatch[1]}${isoMatch[2]}${isoMatch[3]}`;
          // YYYYMMDDHHmmss (14 digits) → YYYYMMDD
          if (/^\d{14}$/.test(trimmed)) return trimmed.substring(0, 8);
          // Fallback: try Date parse
          let date: Date | null = null;
          try { date = new Date(trimmed); } catch { /* ignore */ }
          if (date && !isNaN(date.getTime())) {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}${m}${d}`;
          }
          return trimmed;
        };

        if (selectedData.kopf) {
          if (selectedData.kopf.massgeblichesDatum) {
            selectedData.kopf.massgeblichesDatum = normalizeDateToYYYYMMDD(selectedData.kopf.massgeblichesDatum);
          }
          if (selectedData.kopf.kopfDatumdesAusgangs) {
            selectedData.kopf.kopfDatumdesAusgangs = normalizeDateToYYYYMMDD(selectedData.kopf.kopfDatumdesAusgangs);
          }
        }

        // console.log('  - Calling gRPC validation service...');
        setProcessing(prev => ({ ...prev, currentStep: 'Validating with Atlas...', progress: 60 }));

        try {
          // console.log('  - Sending data to validation endpoint...');
          // console.log('  - Payload size:', JSON.stringify(selectedData).length, 'bytes');

          const validationResult = await authService.validateModuleData(
            processing.selectedModule.id,
            selectedData,
            currentLanguage,  // Pass current language for error message translation
            sendPartialData   // Pass sendPartialData for autosend configuration (same as send API)
          );

          // console.log('  - Validation response received');
          // console.log('📋 gRPC Validation Result:', validationResult);
          // console.log('  - Valid:', validationResult.valid);
          // console.log('  - Total Errors:', validationResult.total_errors);
          // console.log('  - Total Warnings:', validationResult.total_warnings);
          // console.log('  - Validation Phases:', validationResult.validation_phases);

          // Check if validation failed
          if (!validationResult.valid) {
            console.error('❌ STEP 4 FAILED: gRPC validation returned errors');
            setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0 }));

            // Map validation errors to field paths for inline display
            const fieldErrors: Record<string, string> = {};
            const backendLabels: Record<string, string> = {}; // Store backend-provided labels
            let networkError = '';
            let generalErrors: string[] = [];
            const unmappedErrors: Array<{ field: string, message: string }> = [];

            // Field path normalization function - maps gRPC field paths to form field paths
            const normalizeFieldPath = (rawPath: string): string => {
              let path = rawPath || '';

              // Remove common prefixes from gRPC responses
              path = path.replace(/^expdat\[\d+\]\./, ''); // expdat[0].kopf -> kopf
              path = path.replace(/^ausfuhr\.expdat\[\d+\]\./, ''); // ausfuhr.expdat[0].kopf -> kopf
              path = path.replace(/^messageData\.ausfuhr\.expdat\[\d+\]\./, ''); // nested prefix
              path = path.replace(/^data\./, ''); // data.kopf -> kopf

              // Normalize array notation: position.0.field -> position[0].field
              path = path.replace(/\.(\d+)\./g, '[$1].');
              path = path.replace(/\.(\d+)$/, '[$1]');

              // Ensure recipient fields use 'empfanger' (without umlaut) - do this early
              path = path.replace(/empfänger/g, 'empfanger');
              path = path.replace(/empfaenger/g, 'empfanger');

              // Comprehensive field mappings from gRPC field names to form field names
              const fieldMapping: Record<string, string> = GRPC_FIELD_PATH_ALIASES;

              // Apply field mappings (exact match only to prevent double-prefix bugs)
              let mappingApplied = false;
              if (fieldMapping[path]) {
                path = fieldMapping[path];
                mappingApplied = true;
              }
              // If no exact match, try lowercase version of path
              if (!mappingApplied) {
                const lowerPath = path.toLowerCase();
                for (const [from, to] of Object.entries(fieldMapping)) {
                  if (lowerPath === from.toLowerCase()) {
                    path = to;
                    mappingApplied = true;
                    break;
                  }
                }
              }

              // Handle position array item field paths: position[N].wareXxx
              // gRPC might return: position[N].warenbezeichnung -> position[N].wareWarenbezeichnung
              // These regex replacements handle ANY position index (for 1-20+ line items)
              path = path.replace(/position\[(\d+)\]\.warenbezeichnung/g, 'position[$1].wareWarenbezeichnung');
              path = path.replace(/position\[(\d+)\]\.warennummer(?!KN8)/g, 'position[$1].wareWarennummerKN8');
              path = path.replace(/position\[(\d+)\]\.warennummerKN8/g, 'position[$1].wareWarennummerKN8');
              path = path.replace(/position\[(\d+)\]\.rohmasse/g, 'position[$1].wareRohmasse');
              path = path.replace(/position\[(\d+)\]\.eigenmasse/g, 'position[$1].wareEigenmasse');
              path = path.replace(/position\[(\d+)\]\.positionsnummer/g, 'position[$1].warePositionsnummer');
              path = path.replace(/position\[(\d+)\]\.statistischerWert/g, 'position[$1].wareStatistischerWert');
              path = path.replace(/position\[(\d+)\]\.rechnungspreis/g, 'position[$1].wareRechnungspreis');

              // Procedure fields for ANY position index — gRPC uses 'angemeldetes' / 'vorangegangenes'
              // e.g. position[0].angemeldetes -> position[0].beantragtesVerfahren
              path = path.replace(/position\[(\d+)\]\.angemeldetes(?:Verfahren)?/g, 'position[$1].beantragtesVerfahren');
              path = path.replace(/position\[(\d+)\]\.vorangegangenes(?:Verfahren)?/g, 'position[$1].vorhergehendesVerfahren');
              path = path.replace(/position\[(\d+)\]\.beantragtesVerfahren/g, 'position[$1].beantragtesVerfahren');
              path = path.replace(/position\[(\d+)\]\.vorhergehendesVerfahren/g, 'position[$1].vorhergehendesVerfahren');

              // Normalize packaging field variants for ANY position index (handles 1-20+ items)
              // position[N].artderPackstucke -> position[N].verpackung[0].artderVerpackung
              path = path.replace(/position\[(\d+)\]\.artderPackstucke/g, 'position[$1].verpackung[0].artderVerpackung');
              path = path.replace(/position\[(\d+)\]\.packstuckanzahl/g, 'position[$1].verpackung[0].anzahlderPackstucke');
              path = path.replace(/position\[(\d+)\]\.packstuckzeichennummern/g, 'position[$1].verpackung[0].versandzeichen');
              // position[N].versandzeichen (bare, not inside verpackung) -> position[N].verpackung[0].versandzeichen
              path = path.replace(/position\[(\d+)\]\.versandzeichen(?!\[)/g, 'position[$1].verpackung[0].versandzeichen');
              // position[N].anzahlderPackstucke (bare, not inside verpackung) -> position[N].verpackung[0].anzahlderPackstucke
              path = path.replace(/position\[(\d+)\]\.anzahlderPackstucke(?!\.)/g, 'position[$1].verpackung[0].anzahlderPackstucke');
              // position[N].verpackung (bare group error) -> position[N].verpackung[0].artderVerpackung
              path = path.replace(/^position\[(\d+)\]\.verpackung$/, 'position[$1].verpackung[0].artderVerpackung');
              // Package TYPE, previously normalized only on the Errors-tab copy.
              path = path.replace(/position\[(\d+)\]\.packstuckverpackungsart/g, 'position[$1].verpackung[0].artderVerpackung');

              return path;
            };

            // Human-readable field name mapping for better error display
            const fieldDisplayNames: Record<string, string> = {
              // System-generated fields (from module user details)
              'kopf.eoriNiederlassungsnummer': 'EORI Number',
              'kopf.userid': 'User ID',
              'kopf.dienststellennummer': 'Customs Office Number',
              'kopf.rechnungswahrung': 'Invoice Currency',
              'kopf.versionsnr': 'Version Number',
              'kopf.erstelldatum': 'Creation Date',
              'kopf.lrn': 'LRN (Local Reference Number)',
              'eoriNiederlassungsnummer': 'EORI Number',
              'userid': 'User ID',

              // Recipient fields
              'kopf.empfanger.name': 'Recipient Name',
              'kopf.empfanger.strasse': 'Recipient Street',
              'kopf.empfanger.plz': 'Recipient Postal Code',
              'kopf.empfanger.ort': 'Recipient City',
              'kopf.empfanger.land': 'Recipient Country',
              'kopf.empfanger.tin': 'Recipient TIN/EORI',

              // Exporter fields
              'kopf.ausfuhrer.name': 'Exporter Name',
              'kopf.ausfuhrer.strasse': 'Exporter Street',
              'kopf.ausfuhrer.plz': 'Exporter Postal Code',
              'kopf.ausfuhrer.ort': 'Exporter City',
              'kopf.ausfuhrer.land': 'Exporter Country',
              'kopf.ausfuhrer.tin': 'Exporter TIN/EORI',

              // Declarant fields (Anmelder)
              'kopf.anmelder.tin': 'Declarant TIN',
              'kopf.anmelder.name': 'Declarant Name',
              'kopf.anmelder.phone': 'Declarant Phone',
              'kopf.anmelder.niederlassungsNummer': 'Declarant Branch Number',
              'kopf.anmelder.ansprechName': 'Declarant Contact Person',
              'kopf.anmelder.beauftragterName': 'Declarant Authorized Name',

              // Representative fields (Vertreter)
              'kopf.vertreter.tin': 'Representative TIN',
              'kopf.vertreter.niederlassungsNummer': 'Representative Branch Number',
              'kopf.vertreter.phone': 'Representative Phone',
              'kopf.vertreter.ansprechName': 'Representative Contact Person',

              // Subcontractor fields (Subunternehmer)
              'kopf.subunternehmer.tin': 'Subcontractor TIN',
              'kopf.subunternehmer.niederlassungsNummer': 'Subcontractor Branch Number',

              // Declaration type fields
              'kopf.anmeldeart': 'Declaration Type',
              'kopf.artderAnmeldung': 'Type of Registration',
              'kopf.verfahren': 'Procedure Code',
              'kopf.artderAusfuhranmeldung': 'Export Declaration Type',
              'kopf.beteiligtenKonstellation': 'Party Constellation',
              'kopf.sicherheit': 'Security',
              'kopf.verkehrszweig': 'Mode of Transport',
              'kopf.massgeblichesDatum': 'Relevant Date',
              'kopf.inlandischerVerkehrszweig': 'Domestic Transport Mode',
              'kopf.verkehrszweigAnDerGrenze': 'Border Transport Mode',
              'kopf.messages': 'Validation Messages',

              // Bewilligung (Authorization) fields
              'bewilligung.art': 'Authorization Type',
              'bewilligung.referenznummer': 'Authorization Reference Number',
              'kopf.bewilligung[0].art': 'Authorization Type',
              'kopf.bewilligung[0].referenznummer': 'Authorization Reference Number',

              // Warenort (Location of Goods) fields
              'kopf.warenortArtdesOrtes': 'Location Type',
              'kopf.warenortArtDerOrtsbestimmung': 'Location Determination Type',
              'kopf.warenortZusatzlicheKennung': 'Additional Location Identifier',
              'kopf.warenortBewilligungsnummer': 'Location Authorization Number',

              // Beforderungsmittel beim Abgang (Transport at Departure) fields
              'kopf.beforderungsmittelBeimAbgang[0].artderIdentifikation': 'Transport ID Type at Departure',
              'kopf.beforderungsmittelBeimAbgang[0].kennzeichen': 'Vehicle Registration at Departure',
              'kopf.beforderungsmittelBeimAbgang[0].staatszugehorigkeit': 'Vehicle Nationality at Departure',

              // Beforderungsroute (Transport Route) fields
              'kopf.beforderungsroute.ausgewahlteLander': 'Transit Countries',

              // Border transport fields
              'kopf.beforderungsmittelderGrenzeKennzeichen': 'Border Vehicle Registration',

              // Additional header fields
              'kopf.besondereUmstande': 'Special Circumstances',
              'kopf.ausfuhrZollstelleFurDieErganzende': 'Supplementary Export Customs Office',
              'kopf.lieferbedingungLand': 'Delivery Country',

              // Sendung (Shipment) fields
              'sendung.beforderungsmittel': 'Transport Means',
              'sendung.gesamtRohmasse': 'Total Gross Weight',
              'sendung.anzahlPackstucke': 'Number of Packages',
              'sendung.containerNummer': 'Container Number',
              'sendung.versandzeichen': 'Shipping Marks',
              'sendung.beforderungsroute': 'Transport Route',

              // Lieferung (Delivery) fields
              'lieferung.lieferbedingungen': 'Delivery Terms',
              'lieferung.ort': 'Delivery Location',
              'lieferung.bestimmungsLand': 'Destination Country',

              // Position (item) fields - basic
              'position[0].wareWarenbezeichnung': 'Item 1 Description',
              'position[0].wareWarennummerKN8': 'Item 1 HS Code',
              'position[0].wareRohmasse': 'Item 1 Gross Mass',
              'position[0].wareEigenmasse': 'Item 1 Net Mass',
              'position[0].wareStatistischerWert': 'Item 1 Statistical Value',
              'position[0].warePositionsnummer': 'Item 1 Position Number',
              'position[0].beantragtesVerfahren': 'Item 1 Requested Procedure',
              'position[1].beantragtesVerfahren': 'Item 2 Requested Procedure',
              'position[2].beantragtesVerfahren': 'Item 3 Requested Procedure',
              'position[0].vorhergehendesVerfahren': 'Item 1 Previous Procedure',
              'position[1].vorhergehendesVerfahren': 'Item 2 Previous Procedure',
              'position[2].vorhergehendesVerfahren': 'Item 3 Previous Procedure',
              'position[0].ursprungsland': 'Item 1 Country of Origin',
              'position[0].ursprungsVersendungsregion': 'Item 1 Dispatch Region',
              'position[0].mengeinbesondererMabeinheit': 'Item 1 Quantity in Special Unit',

              // Position verpackung (packaging) fields
              'position[0].verpackung[0].artderVerpackung': 'Item 1 Package Type',
              'position[0].verpackung[0].anzahlderPackstucke': 'Item 1 Number of Packages',
              'position[0].verpackung[0].versandzeichen': 'Item 1 Shipping Marks',
              'position[0].verpackung[0].packstuckverweisPositionsnummer': 'Item 1 Package Reference Position',
              'position[0].artderPackstucke': 'Item 1 Package Type',
              'position[0].anzahlderPackstucke': 'Item 1 Number of Packages',

              // Position unterlage (document) fields
              'position[0].unterlage[0].art': 'Item 1 Document Type',
              'position[0].unterlage[0].referenznummer': 'Item 1 Document Reference',
              'position[0].unterlage[0].qualifikator': 'Item 1 Document Qualifier',

              // Position sonstigerVerweis (other reference) fields
              'position[0].sonstigerVerweis[0].art': 'Item 1 Other Reference Type',
              'position[0].sonstigerVerweis[0].referenznummer': 'Item 1 Other Reference Number',

              // Item 2 fields (for multi-item declarations)
              'position[1].wareWarenbezeichnung': 'Item 2 Description',
              'position[1].wareWarennummerKN8': 'Item 2 HS Code',
              'position[1].artderPackstucke': 'Item 2 Package Type',
              'position[1].anzahlderPackstucke': 'Item 2 Number of Packages',
              'position[1].ursprungsland': 'Item 2 Country of Origin',
              'position[1].ursprungsVersendungsregion': 'Item 2 Dispatch Region',
              'position[1].verpackung[0].artderVerpackung': 'Item 2 Package Type',
              'position[1].verpackung[0].anzahlderPackstucke': 'Item 2 Number of Packages',
              'position[2].ursprungsVersendungsregion': 'Item 3 Dispatch Region',
              'position[2].verpackung[0].artderVerpackung': 'Item 3 Package Type',
            };

            // Get display name for a field path (translated to current language)
            const getDisplayName = (fieldPath: string): string => {
              // First, check backend-provided labels
              if (backendLabels[fieldPath]) {
                return backendLabels[fieldPath];
              }

              // Use i18n-aware translateFieldPath helper
              const translated = translateFieldPath(fieldPath);
              const fallbackPart = fieldPath.split('.').pop() || fieldPath;
              if (translated !== fallbackPart) {
                return translated;
              }

              // Then check static mapping (English fallback)
              if (fieldDisplayNames[fieldPath]) {
                return fieldDisplayNames[fieldPath];
              }
              return translated;
            };

            // Track all field errors count (before merging)
            let mappedFieldErrorCount = 0;
            let skippedFieldErrorCount = 0;
            const skippedErrors: Array<{ field: string, message: string, reason: string }> = [];

            // Get all visible field paths from extracted data AND ATLAS required fields
            // The form displays addMissingRequiredFields(flattenDataForTable(extractedData)),
            // so we need to include ALL fields that are shown in the form
            const visibleFieldPaths = new Set<string>();

            // 1. Add ALL ATLAS fields (required and optional) - these are always shown in the form
            const atlasFields = getATLASFields(processing.extractedData);
            atlasFields.forEach(field => {
              visibleFieldPaths.add(field.path);
              // Also add simplified versions for matching
              const simplified = field.path.replace(/^kopf\./, '').replace(/^position\[\d+\]\./, '');
              visibleFieldPaths.add(simplified);
            });

            // 2. Add fields from extracted data (in case there are extra fields not in ATLAS_FIELDS)
            if (processing.extractedData) {
              const flattenedData = flattenDataForTable(processing.extractedData);
              flattenedData.forEach(item => {
                visibleFieldPaths.add(item.fieldPath);
                // Also add simplified versions for matching
                const simplified = item.fieldPath.replace(/^kopf\./, '').replace(/^position\[\d+\]\./, '');
                visibleFieldPaths.add(simplified);
              });
            }
            // console.log('📋 Visible field paths in form:', visibleFieldPaths.size, '(includes ATLAS required fields)');

            // Helper to check if a field path exists in the form
            // STRICT: Only show errors for fields that are actually displayed in the form
            const isFieldVisibleInForm = (fieldPath: string): boolean => {
              // First, log the field path we're checking
              // console.log(`    Checking visibility for: "${fieldPath}"`);

              // System fields that should NEVER show errors (truly auto-generated and hidden)
              // MINIMAL list - only fields that are completely auto-generated and not editable
              const systemFieldsExact = [
                'autosend', 'versionsnr', 'erstelldatum', 'zuordnungskennung',
                'nachrichtennummer', 'interchangecontrolreference',
              ];

              // These use SUBSTRING matching (for compound field names)
              const systemFieldsContains = [
                'autosend', 'versionsnr', 'erstelldatum', 'zuordnungskennung', 'nachrichtennummer',
              ];

              const fieldLower = fieldPath.toLowerCase();
              // Extract the last part of the field path for exact matching
              const fieldParts = fieldLower.replace(/\[/g, '.').replace(/\]/g, '.').split('.');
              const fieldLastPart = fieldParts[fieldParts.length - 1] || fieldLower;

              // Check exact match against system fields
              for (const sysField of systemFieldsExact) {
                if (fieldLower === sysField.toLowerCase() || fieldLastPart === sysField.toLowerCase()) {
                  // console.log(`      -> SYSTEM FIELD (exact match skipped): ${sysField}`);
                  return false;
                }
              }

              // Check substring match against system fields that need it
              for (const sysField of systemFieldsContains) {
                if (fieldLower.includes(sysField.toLowerCase())) {
                  // console.log(`      -> SYSTEM FIELD (contains skipped): ${sysField}`);
                  return false;
                }
              }

              // Check exact match
              if (visibleFieldPaths.has(fieldPath)) {
                // console.log(`      -> VISIBLE (exact match)`);
                return true;
              }

              // Try without kopf prefix
              const withoutKopf = fieldPath.replace(/^kopf\./, '');
              if (visibleFieldPaths.has(withoutKopf)) {
                // console.log(`      -> VISIBLE (without kopf prefix)`);
                return true;
              }

              // Try with kopf prefix
              if (visibleFieldPaths.has(`kopf.${fieldPath}`)) {
                // console.log(`      -> VISIBLE (with kopf prefix)`);
                return true;
              }

              // Try removing position array indices and checking
              const withoutIndices = fieldPath.replace(/\[\d+\]/g, '[0]');
              if (visibleFieldPaths.has(withoutIndices)) {
                // console.log(`      -> VISIBLE (normalized array index)`);
                return true;
              }

              // REMOVED: Fuzzy last-part matching was causing false positives
              // Fields that don't have exact matches should go to general errors

              // console.log(`      -> NOT VISIBLE (field not in form - will show in general errors)`);
              return false;
            };

            // console.log('📋 Processing', validationResult.errors.length, 'errors from gRPC:');

            // Helper function to clean up invalid/corrupted characters in error messages
            // and extract only the error message (not field paths or codes)
            const cleanErrorMessage = (msg: string): string => {
              if (!msg) return currentLanguage === 'de' ? 'Validierungsfehler' : 'Validation error';

              // Replace corrupted Unicode bullet codes (â¢â¢XX or ••XX) with just the code digits
              // These are ATLAS codes where bullets are visual padding (e.g., ••01 → "0001", â¢â¢01 → "0001")
              let cleaned = msg
                .replace(/[â¢•]{2,}(\d{2,4})/g, '"$1"') // â¢â¢01 → "01", ••0100 → "0100"
                .replace(/[â¢•]+/g, '') // Remove any remaining stray bullet chars
                .replace(/[\u0080-\u009F]/g, '') // Remove control characters
                .replace(/\s{2,}/g, ' ') // Replace multiple spaces with single space
                .replace(/^\s+|\s+$/g, '') // Trim whitespace
                .replace(/[^\x20-\x7E\xA0-\xFF\u0100-\u017F\u0400-\u04FF\u00C0-\u024F\u00DF"]/g, ''); // Keep ASCII + common European chars including ß and quotes

              // Remove Java-style format placeholders (e.g., "{0}", "{0}:", "{1}: ", etc.)
              cleaned = cleaned.replace(/\{\d+\}:?\s*/g, '');

              // Remove any field path prefixes (e.g., "kopf.empfanger.name: ")
              cleaned = cleaned.replace(/^[a-zA-Z0-9_.\[\]]+:\s*/, '');

              // Remove error codes in brackets (e.g., "[VALIDATION_ERROR]")
              cleaned = cleaned.replace(/\[[A-Z_]+\]\s*/g, '');

              // Translate German terms to English if needed
              cleaned = translateErrorMessage(cleaned);

              return cleaned || (currentLanguage === 'de' ? 'Validierungsfehler' : 'Validation error');
            };

            // =============================================================================
            // Helper function to extract field references from dependent error messages
            // Dependent errors mention multiple fields that must match/be consistent
            // Example: "Nachrichtenempfänger / Reference number and EXPORT CUSTOMS OFFICE / Reference number: are not übereinstimmend"
            // =============================================================================
            const dependentFieldPatterns: Record<string, string[]> = {
              // German term -> form field paths that should be highlighted
              'nachrichtenempfänger': ['kopf.eoriNiederlassungsnummer'],
              'nachrichtenempfaenger': ['kopf.eoriNiederlassungsnummer'],
              'message recipient': ['kopf.eoriNiederlassungsnummer'],
              'reference number': ['kopf.bezugsnummer', 'kopf.lrn'],
              'bezugsnummer': ['kopf.bezugsnummer', 'kopf.lrn'],
              'export customs office': ['kopf.ausfuhrzollstelleDienststellennummer'],
              'ausfuhrzollstelle': ['kopf.ausfuhrzollstelleDienststellennummer'],
              'exit customs office': ['kopf.vorgeseheneAusgangszollstelleDienststellennummer'],
              'ausgangszollstelle': ['kopf.vorgeseheneAusgangszollstelleDienststellennummer'],
              'declarant': ['kopf.anmelder.tin', 'kopf.anmelder.niederlassungsNummer'],
              'anmelder': ['kopf.anmelder.tin', 'kopf.anmelder.niederlassungsNummer'],
              'exporter': ['kopf.ausfuhrer.tin', 'kopf.ausfuhrer.name'],
              'ausführer': ['kopf.ausfuhrer.tin', 'kopf.ausfuhrer.name'],
              'ausfuhrer': ['kopf.ausfuhrer.tin', 'kopf.ausfuhrer.name'],
              'recipient': ['kopf.empfanger.name', 'kopf.empfanger.land'],
              'empfänger': ['kopf.empfanger.name', 'kopf.empfanger.land'],
              'empfaenger': ['kopf.empfanger.name', 'kopf.empfanger.land'],
              'destination country': ['kopf.bestimmungsLand'],
              'bestimmungsland': ['kopf.bestimmungsLand'],
              'incoterm': ['kopf.lieferbedingungIncotermCode'],
              'lieferbedingung': ['kopf.lieferbedingungIncotermCode'],
              'gross weight': ['kopf.gesamtRohmasse'],
              'rohmasse': ['kopf.gesamtRohmasse'],
              'gesamtrohmasse': ['kopf.gesamtRohmasse'],
              'transport mode': ['kopf.verkehrszweigAnDerGrenze', 'kopf.inlandischerVerkehrszweig'],
              'verkehrszweig': ['kopf.verkehrszweigAnDerGrenze', 'kopf.inlandischerVerkehrszweig'],
              'packages': ['kopf.packstuckanzahl'],
              'packstück': ['kopf.packstuckanzahl'],
              'packstucke': ['kopf.packstuckanzahl'],
            };

            // Patterns where "reference number" is contextual (not about kopf.bezugsnummer)
            const referenceNumberExclusionContexts = [
              'nachrichtenempfänger', 'nachrichtenempfaenger', 'message recipient',
              'export customs office', 'ausfuhrzollstelle',
              'exit customs office', 'ausgangszollstelle',
            ];

            // Patterns where a short term is a substring of a longer term
            // When the longer term matches, the shorter one should be skipped
            const substringExclusions: Record<string, string[]> = {
              'empfänger': ['nachrichtenempfänger', 'nachrichtenempfaenger'],
              'empfaenger': ['nachrichtenempfänger', 'nachrichtenempfaenger'],
              'recipient': ['message recipient'],
            };

            const extractDependentFields = (message: string): string[] => {
              const messageLower = message.toLowerCase();
              const foundFields = new Set<string>();

              // Check if "reference number" appears in a context where it does NOT mean kopf.bezugsnummer
              const hasExclusionContext = referenceNumberExclusionContexts.some(ctx => messageLower.includes(ctx));

              for (const [pattern, fields] of Object.entries(dependentFieldPatterns)) {
                // Skip generic "reference number" when it appears alongside a specific entity context
                if (pattern === 'reference number' && hasExclusionContext) {
                  continue;
                }
                // Skip short patterns when a longer compound term is present in the message
                // e.g. skip "empfänger" → recipient fields when "nachrichtenempfänger" is in the message
                const exclusions = substringExclusions[pattern];
                if (exclusions && exclusions.some(longer => messageLower.includes(longer))) {
                  continue;
                }
                if (messageLower.includes(pattern)) {
                  fields.forEach(field => foundFields.add(field));
                }
              }

              return Array.from(foundFields);
            };

            validationResult.errors.forEach((err: any, index: number) => {
              const rawField = err.field || '';
              const message = cleanErrorMessage(err.message || 'Validation error');
              const code = err.code || 'UNKNOWN';
              const fieldLabel = err.fieldLabel || ''; // Backend-provided label
              const isSystemField = err.isSystemField || false; // Backend-provided flag

              // console.log(`  [${index + 1}] Raw field: "${rawField}"`);
              // console.log(`      Label: "${fieldLabel}"`);
              // console.log(`      Message: "${message}"`);
              // console.log(`      Code: "${code}"`);
              // console.log(`      IsSystemField: ${isSystemField}`);
              // Normalize the field path first to check if it's visible
              const normalizedPath = normalizeFieldPath(rawField);

              // Check if backend marked this as a system field
              // If system field is NOT visible in the form, add to general errors (don't skip silently)
              if (isSystemField) {
                // Check if this "system field" is actually visible in the form
                if (normalizedPath && isFieldVisibleInForm(normalizedPath)) {
                  // Field IS visible - don't skip, show the error
                  // console.log(`System field ${rawField} -> ${normalizedPath} is visible in form, showing error`);
                } else {
                  // Field is not visible - add to general errors so user sees it
                  const displayName = translateErrorMessage(fieldLabel || getDisplayName(normalizedPath || rawField));
                  generalErrors.push(`${displayName}: ${message}`);
                  skippedFieldErrorCount++;
                  skippedErrors.push({ field: rawField, message, reason: 'System field (shown in general errors)' });
                  // console.log(`      -> SYSTEM FIELD NOT IN FORM: Added to general errors`);
                  return;
                }
              }

              // Check for network/service errors
              if (rawField === '_network' || rawField === '_grpc' ||
                rawField === '_grpc_service' || rawField === '_validation_service') {
                networkError = message;
                // console.log(`      -> Classified as: NETWORK ERROR`);
                return;
              }

              // Check for parse errors
              if (rawField === '_parse_error') {
                generalErrors.push(message);
                // console.log(`      -> Classified as: PARSE ERROR`);
                return;
              }

              // =============================================================================
              // DEPENDENT ERROR HANDLING: Extract field references from _general errors
              // These are errors that mention multiple fields (consistency checks)
              // =============================================================================
              if (rawField.startsWith('_') || rawField === '_general') {
                // Try to extract dependent field references from the error message
                const dependentFields = extractDependentFields(message);

                if (dependentFields.length > 0) {
                  // console.log(`      -> DEPENDENT ERROR: Found ${dependentFields.length} field references in message`);
                  // console.log(`         Fields: ${dependentFields.join(', ')}`);

                  // Apply the error to all dependent fields that are visible in the form
                  let appliedToAnyField = false;
                  dependentFields.forEach(depField => {
                    if (isFieldVisibleInForm(depField)) {
                      mappedFieldErrorCount++;
                      if (fieldErrors[depField]) {
                        appendFieldError(fieldErrors, depField, message);
                      } else {
                        fieldErrors[depField] = message;
                      }
                      // console.log(`         -> Applied to field: ${depField}`);
                      appliedToAnyField = true;
                    }
                  });

                  // Also add to general errors so user can see the full error message
                  if (!appliedToAnyField) {
                    generalErrors.push(message);
                    // console.log(`         -> No visible fields found, added to general errors`);
                  } else {
                    // Add a summary to general errors for dependent errors
                    generalErrors.push(`⚠️ Related fields highlighted: ${message}`);
                  }
                  return;
                }

                // No dependent fields found, add as general error
                const cleanedField = rawField.replace(/^_/, '');
                generalErrors.push(cleanedField ? `${cleanedField}: ${message}` : message);
                // console.log(`      -> Classified as: GENERAL ERROR (no dependent fields found)`);
                return;
              }

              // Handle "unknown" fields as general errors
              if (normalizedPath === 'unknown' || rawField === 'unknown') {
                generalErrors.push(message);
                // console.log(`      -> Classified as: UNKNOWN FIELD (added to general errors)`);
                return;
              }

              // Check if this field is visible in the form
              const isVisible = normalizedPath && isFieldVisibleInForm(normalizedPath);

              // Store the error message for this field
              if (normalizedPath) {
                mappedFieldErrorCount++; // Count all field errors before merging
                // Store backend-provided label if available
                if (fieldLabel && !backendLabels[normalizedPath]) {
                  backendLabels[normalizedPath] = fieldLabel;
                }

                if (isVisible) {
                  // Field is visible in form - show inline error
                  if (fieldErrors[normalizedPath]) {
                    appendFieldError(fieldErrors, normalizedPath, message);
                    // console.log(`      -> MERGED with existing error for: ${normalizedPath}`);
                  } else {
                    fieldErrors[normalizedPath] = message;
                    // console.log(`      -> Mapped to form field: ${normalizedPath}`);
                  }
                } else {
                  // Field is NOT visible in form - show as general error so user is aware
                  const displayName = translateErrorMessage(fieldLabel || getDisplayName(normalizedPath));
                  generalErrors.push(`${displayName}: ${message}`);
                  skippedFieldErrorCount++;
                  skippedErrors.push({ field: rawField, message, reason: 'Field not in form (shown as general error)' });
                  // console.log(`      -> ⚠️ NOT IN FORM (added to general errors): ${normalizedPath}`);
                }
              } else {
                // If normalization resulted in empty string, treat as unmapped
                unmappedErrors.push({ field: rawField, message });
                // console.log(`      -> UNMAPPED (empty after normalization)`);
              }
            });

            // Fan the header-level procedure errors out onto the line items.
            // Shared with the auto-validation path — this used to be an inline
            // copy here and nothing at all there, so the sentinel leaked into
            // the Errors tab as a field name. See procedureFanout.
            expandProcedureFanout(
              fieldErrors,
              (validationResult as any)._positions
                || processing.extractedData?.position,
            );

            // Add unmapped errors to general errors list
            unmappedErrors.forEach(err => {
              generalErrors.push(`${err.field}: ${err.message}`);
            });

            // POST-PROCESSING: Verify field errors can be displayed in DOM
            // Move truly orphaned field errors (no matching DOM input, not a known ATLAS field) to general errors
            setTimeout(() => {
              const orphanedErrors: string[] = [];
              const domFieldPaths = new Set<string>();

              // Collect all field paths from DOM
              document.querySelectorAll('[data-field-path]').forEach(el => {
                const path = el.getAttribute('data-field-path');
                if (path) {
                  domFieldPaths.add(path);
                  domFieldPaths.add(path.replace(/^kopf\./, ''));
                  domFieldPaths.add(`kopf.${path}`);
                }
              });

              // console.log('🔍 POST-VALIDATION: Checking field errors have DOM elements...');
              // console.log(`   DOM field paths found: ${domFieldPaths.size}`);
              // console.log(`   ATLAS field paths (trusted): ${visibleFieldPaths.size}`);

              // Check each field error has a DOM element
              Object.entries(fieldErrors).forEach(([fieldPath, message]) => {
                const variations = [
                  fieldPath,
                  fieldPath.replace(/^kopf\./, ''),
                  `kopf.${fieldPath}`,
                  fieldPath.replace(/\[\d+\]/g, '[0]'),
                ];

                const hasDOM = variations.some(v => domFieldPaths.has(v));
                if (!hasDOM) {
                  // IMPORTANT: If the field is a known ATLAS field (in visibleFieldPaths),
                  // keep it as an inline field error — it's likely on an inactive tab
                  // (e.g., Line Items tab not rendered when user is on Header tab)
                  const isKnownAtlasField = variations.some(v => visibleFieldPaths.has(v));
                  if (isKnownAtlasField) {
                    // console.log(`   ✅ ATLAS FIELD on inactive tab: ${fieldPath} - keeping as inline error`);
                    return; // Keep as inline field error, do NOT move to general errors
                  }
                  // console.log(`   ⚠️ ORPHANED: ${fieldPath} - no DOM element, not in ATLAS fields`);
                  const displayName = translateErrorMessage(backendLabels[fieldPath] || getDisplayName(fieldPath));
                  orphanedErrors.push(`${displayName}: ${message}`);
                  delete fieldErrors[fieldPath];
                }
              });

              if (orphanedErrors.length > 0) {
                // console.log(`   Moving ${orphanedErrors.length} truly orphaned errors to general errors`);
                setFieldValidationErrors({...fieldErrors});
                setGeneralValidationErrors(prev => [...prev, ...orphanedErrors]);
              }
            }, 200);

            // console.log('');
            // console.log('📋 ERROR SUMMARY:');
            // console.log(`  - Total errors from backend: ${validationResult.total_errors || validationResult.errors.length}`);
            // console.log(`  - Displayed in form: ${mappedFieldErrorCount} (${Object.keys(fieldErrors).length} unique fields)`);
            // console.log(`  - Skipped (not in form): ${skippedFieldErrorCount}`);
            // console.log(`  - Network errors: ${networkError ? 1 : 0}`);
            // console.log(`  - General/unmapped errors: ${generalErrors.length}`);

            if (skippedErrors.length > 0) {
              // console.log('');
              // console.log('⏭️ SKIPPED ERRORS (fields not in form - logged for debugging):');
              skippedErrors.forEach(err => {
                // console.log(`  - ${err.field}: ${err.message}`);
              });
            }

            // console.log('');
            // console.log('📋 FIELD ERRORS (for form display):');
            Object.entries(fieldErrors).forEach(([field, msg]) => {
              // console.log(`  - ${field} (${getDisplayName(field)}): ${msg}`);
            });

            // Update field validation errors state
            setFieldValidationErrors(fieldErrors);

            // Update general validation errors state (for fields not in form)
            setGeneralValidationErrors(generalErrors);

            // Log general errors
            if (generalErrors.length > 0) {
              // console.log('');
              // console.log('📋 GENERAL ERRORS (fields not in form):');
              generalErrors.forEach((err, i) => {
                // console.log(`  ${i + 1}. ${err}`);
              });
            }

            // Log all form field paths for debugging
            // console.log('');
            // console.log('📋 DEBUG: Form field paths available in DOM:');
            setTimeout(() => {
              const formFields = document.querySelectorAll('[data-field-path]');
              const formFieldPaths: string[] = [];
              formFields.forEach((el) => {
                const path = el.getAttribute('data-field-path');
                if (path) formFieldPaths.push(path);
              });
              // console.log('  Form fields found:', formFieldPaths.length);
              // formFieldPaths.slice(0, 20).forEach(p => console.log(`    - ${p}`));
              // if (formFieldPaths.length > 20) console.log(`    ... and ${formFieldPaths.length - 20} more`);
            }, 100);

            // Build error message showing only displayable errors (not hidden/system fields)
            const totalBackendErrors = validationResult.total_errors || validationResult.errors.length;
            const displayableErrorCount = Object.keys(fieldErrors).length + generalErrors.length;
            const uniqueFieldCount = Object.keys(fieldErrors).length;
            const generalCount = generalErrors.length;

            let errorMessage = '';

            if (networkError) {
              errorMessage = `❌ gRPC Validation Service Error:\n\n${networkError}\n\nPlease check if the validation service is running.`;
            } else {
              // Build user-friendly error message
              errorMessage = `Please fix the validation issues highlighted in the form and resubmit.`;
            }

            // Show error toast and block submission — toastId deduplicates if triggered multiple times
              toast.error(errorMessage, {
                toastId: 'send-error',
                autoClose: networkError ? 10000 : 6000,
                position: 'top-center',
                style: { whiteSpace: 'pre-line', maxWidth: '600px' }
              });

              // Auto-scroll to first error field after a short delay (to allow state update and re-render)
              setTimeout(() => {
                // console.log('📍 Auto-scroll: Looking for error fields...');
                // console.log('  Field errors to look for:', Object.keys(fieldErrors));

                // First, try to find elements with data-field-status="missing" (required fields)
                const missingFields = document.querySelectorAll('[data-field-status="missing"]');
                // console.log(`  Found ${missingFields.length} elements with data-field-status="missing"`);

                if (missingFields.length > 0) {
                  const firstMissingField = missingFields[0];
                  // console.log('  Scrolling to first missing field');
                  firstMissingField.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                  });
                  const inputElement = firstMissingField.querySelector('input, textarea') as HTMLElement;
                  if (inputElement) {
                    inputElement.focus();
                  }
                  return;
                }

                // Next, try to find elements with Mui-error class (fields that have errors after re-render)
                const errorFields = document.querySelectorAll('.Mui-error');
                // console.log(`  Found ${errorFields.length} elements with .Mui-error class`);

                if (errorFields.length > 0) {
                  const firstErrorElement = errorFields[0];
                  // console.log(`  Scrolling to first error element`);
                  firstErrorElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                  });
                  // Try to focus the input
                  const inputElement = firstErrorElement.querySelector('input, textarea') as HTMLElement ||
                    firstErrorElement.closest('.MuiTextField-root')?.querySelector('input, textarea') as HTMLElement;
                  if (inputElement) {
                    inputElement.focus();
                  }
                  return;
                }

                // Fallback: try to find by data-field-path
                const firstErrorField = Object.keys(fieldErrors)[0];
                if (firstErrorField) {
                  // console.log(`  Trying to find element for field: ${firstErrorField}`);

                  // Try multiple selectors to find the error field element
                  const selectors = [
                    `[data-field-path="${firstErrorField}"]`,
                    `[name="${firstErrorField}"]`,
                    `input[data-field-path="${firstErrorField}"]`,
                  ];

                  let errorElement: Element | null = null;
                  for (const selector of selectors) {
                    errorElement = document.querySelector(selector);
                    if (errorElement) {
                      // console.log(`  Found element with selector: ${selector}`);
                      break;
                    }
                  }

                  // Try partial match on data-field-path
                  if (!errorElement) {
                    const allFieldElements = Array.from(document.querySelectorAll('[data-field-path]'));
                    const lastPart = firstErrorField.split('.').pop() || '';
                    for (let i = 0; i < allFieldElements.length; i++) {
                      const el = allFieldElements[i];
                      const elPath = el.getAttribute('data-field-path') || '';
                      if (elPath.endsWith(lastPart) || elPath.includes(lastPart)) {
                        errorElement = el;
                        // console.log(`  Found element with partial match: ${elPath}`);
                        break;
                      }
                    }
                  }

                  if (errorElement) {
                    // console.log(`📍 Scrolling to first error field: ${firstErrorField}`);
                    errorElement.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                      inline: 'nearest'
                    });
                    // Focus the input if it's an input element
                    const inputElement = errorElement.querySelector('input, textarea') as HTMLElement;
                    if (inputElement) {
                      inputElement.focus();
                    }
                  } else {
                    // console.log(`⚠️ Could not find element for field: ${firstErrorField}`);
                    // Fallback: scroll to the general errors section if it exists
                    const generalErrorsAlert = document.getElementById('general-validation-errors');
                    if (generalErrorsAlert) {
                      // console.log('  Scrolling to general validation errors alert');
                      generalErrorsAlert.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } else {
                      // Fallback: scroll to the data table section
                      const dataTable = document.querySelector('[data-testid="extracted-data-table"]') ||
                        document.querySelector('.MuiTableContainer-root');
                      if (dataTable) {
                        // console.log('  Scrolling to data table as fallback');
                        dataTable.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }
                  }
                } else {
                  // No field errors - check for general errors and scroll to them
                  const generalErrorsAlert = document.getElementById('general-validation-errors');
                  if (generalErrorsAlert) {
                    // console.log('  No field errors but found general errors - scrolling to general errors section');
                    generalErrorsAlert.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  } else {
                    // No field errors, no general errors visible, scroll to the data table
                    // console.log('  No specific field errors, scrolling to data table');
                    const dataTable = document.querySelector('[data-testid="extracted-data-table"]') ||
                      document.querySelector('.MuiTableContainer-root');
                    if (dataTable) {
                      dataTable.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }
                }
              }, 600); // Increased timeout to allow React to re-render with new error state

              // console.log('========================================');
              // console.log('🛑 VALIDATE AND SEND - BLOCKED');
              // console.log('  Reason: gRPC validation failed');
              // console.log(`  Total errors from backend: ${totalBackendErrors}`);
              // console.log(`  Unique fields with errors: ${uniqueFieldCount}`);
              // console.log(`  General/unmapped errors: ${generalCount}`);
              // console.log('  No data will be sent to ATLAS');
              // console.log('========================================');

              return; // STOP - Do not proceed to ATLAS submission
          }

          // Clear field errors on successful validation
          setFieldValidationErrors({});
          setGeneralValidationErrors([]);
          // console.log('✅ STEP 4 COMPLETE: gRPC validation passed');

          // Validation passed — log warnings to console only, no popup
          if (validationResult.warnings && validationResult.warnings.length > 0) {
            console.warn(`⚠️ Validation warnings (${validationResult.warnings.length}):`, validationResult.warnings);
          }

        } catch (validationError: any) {
          // Handle validation service errors - BLOCK submission
          console.error('❌ STEP 4 FAILED: gRPC Validation service exception');
          console.error('  - Error:', validationError.message || validationError);
          console.error('  - Stack:', validationError.stack);

          setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0 }));

          // Show concise error — full detail in console
          toast.error(`Validation service error: ${validationError.message || 'Unknown error'}`, { toastId: 'send-error' });

          // console.log('========================================');
          // console.log('🛑 VALIDATE AND SEND - BLOCKED');
          // console.log('  Reason: gRPC validation service error');
          // console.log('  No data will be sent to ATLAS');
          // console.log('========================================');

          return; // STOP - Do not proceed to ATLAS subm
        }
      } else {
        // console.log('  - Skipping gRPC validation (non-ATLAS module)');
      }
      // ===== END STEP 4: gRPC VALIDATION =====

      // ===== STEP 5: Send to ATLAS API =====
      // console.log('\n📍 STEP 5: Sending data to ATLAS API...');

      // Ensure EORI from GetClientDetails is injected before sending
      // This is a safety net in case it wasn't injected during processing
      // Use localModuleUserDetails which was fetched at the start of this function
      if (localModuleUserDetails && processing.selectedModule?.name?.toLowerCase().includes('atlas')) {
        if (!selectedData.kopf) {
          selectedData.kopf = {};
        }
        // Always use EORI from user's GetClientDetails data
        if (localModuleUserDetails.eoriNiederlassungsnummer) {
          selectedData.kopf.eoriNiederlassungsnummer = localModuleUserDetails.eoriNiederlassungsnummer;
          // console.log(`  📝 EORI injected: ${localModuleUserDetails.eoriNiederlassungsnummer} (from GetClientDetails)`);
        }
        if (localModuleUserDetails.atlas_user_id) {
          selectedData.kopf.userid = localModuleUserDetails.atlas_user_id;
          // console.log(`  📝 UserID injected: ${localModuleUserDetails.atlas_user_id} (from GetClientDetails)`);
        }
        if (localModuleUserDetails.ausfuhr_eori) {
          if (!selectedData.kopf.anmelder) {
            selectedData.kopf.anmelder = {};
          }
          if (!selectedData.kopf.anmelder.tin) {
            selectedData.kopf.anmelder.tin = localModuleUserDetails.ausfuhr_eori;
          }
          // console.log(`  📝 Anmelder TIN injected: ${localModuleUserDetails.ausfuhr_eori} (from GetClientDetails)`);
        }
      }

      // Normalize date fields to YYYYMMDD before ATLAS submission (BEO schema: "Date (n8)")
      if (selectedData.kopf?.massgeblichesDatum) {
        selectedData.kopf.massgeblichesDatum = formatDateForATLAS(selectedData.kopf.massgeblichesDatum);
      }

      // ===== PREVIEW STEP: Navigate to Review tab before submitting =====
      // Validation passed — store validated data and navigate to the Review tab.
      // The actual send happens in handleProceedFromPreview() when user clicks Proceed there.
      setPreviewSubmissionData({ ...selectedData });
      setActiveWorkflowStep('review');
      setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0, currentStep: '' }));
      return;
      // ===== END PREVIEW STEP =====

    } catch (error: any) {
      setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0, currentStep: '' }));
      console.error('Send data error:', error);

      // Check for 401 authentication_expired error from backend
      const detail = error.response?.data?.detail;
      if (error.response?.status === 401 || (typeof detail === 'object' && detail?.error === 'authentication_expired')) {
        const errorData = typeof detail === 'object' ? detail : {};
        const moduleName = errorData.module_name || processing.selectedModule?.name;

        toast.error(`🔒 Authentication expired for ${moduleName}. Please re-authenticate to continue.`, {
          toastId: 'send-error',
          autoClose: 6000,
          position: 'top-center'
        });

        // Refresh module auth status to show expired state
        await refreshModuleAuthStatus();

        // Open authentication dialog automatically
        const currentModule = modules.find(m => m.id === errorData.module_id || m.id === processing.selectedModule?.id);
        if (currentModule) {
          setAuthDialog({
            open: true,
            module: currentModule,
            credentials: { username: '', password: '' }
          });
        }
        return; // Don't show generic error message
      }

      if (error.authError) {
        // Handle OAuth authentication errors
        if (error.errorType === 'token_expired') {
          toast.error(tString('documentProcessing.errors.tokenExpired', error.moduleName), { toastId: 'send-error' });
        } else if (error.errorType === 'authentication_required') {
          toast.error(tString('documentProcessing.errors.authRequiredFor', error.moduleName), { toastId: 'send-error' });
        } else {
          toast.error(`Authentication error: ${error.message}`, { toastId: 'send-error' });
        }

        // Open authentication dialog automatically
        const currentModule = modules.find(m => m.name === error.moduleName || m.id === processing.selectedModule?.id);
        if (currentModule) {
          setAuthDialog({
            open: true,
            module: currentModule,
            credentials: { username: '', password: '' }
          });
        }
      } else {
        // Handle validation errors with detailed message
        const detail = error.response?.data?.detail;

        // =====================================================================
        // ATLAS PutData API validation errors — map to form fields inline
        // =====================================================================
        if (typeof detail === 'object' && detail !== null && detail.error === 'atlas_api_validation_failed' && Array.isArray(detail.field_errors)) {
          const apiFieldErrors: Record<string, string> = {};
          const apiGeneralErrors: string[] = [];

          detail.field_errors.forEach((err: { field: string; message: string }) => {
            const rawField = err.field || '';
            let message = translateErrorMessage(err.message || 'Validation error');

            // Normalize the field path using the same mapping as gRPC validation
            let normalizedPath = rawField;
            // Ensure position fields have correct array syntax (position.0.x → position[0].x)
            normalizedPath = normalizedPath.replace(/position\.(\d+)\./g, 'position[$1].');

            if (normalizedPath && normalizedPath !== 'unknown') {
              if (apiFieldErrors[normalizedPath]) {
                appendFieldError(apiFieldErrors, normalizedPath, message);
              } else {
                apiFieldErrors[normalizedPath] = message;
              }
            } else {
              // Use translated field name for general errors
              const displayName = translateErrorMessage(translateFieldPath(rawField));
              apiGeneralErrors.push(`${displayName}: ${message}`);
            }
          });

          // Set errors in form state (merge with any existing gRPC errors)
          setFieldValidationErrors(prev => {
            const merged = { ...prev };
            Object.entries(apiFieldErrors).forEach(([field, msg]) => {
              merged[field] = prev[field] ? `${prev[field]}; ${msg}` : msg;
            });
            return merged;
          });
          if (apiGeneralErrors.length > 0) {
            setGeneralValidationErrors(prev => [...prev, ...apiGeneralErrors]);
          }

          // Errors are already shown inline in the form — no redundant toast needed

          // Auto-scroll to first error field
          setTimeout(() => {
            const errorFields = document.querySelectorAll('.Mui-error');
            if (errorFields.length > 0) {
              errorFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
              const inputEl = errorFields[0].querySelector('input, textarea') as HTMLElement;
              if (inputEl) inputEl.focus();
              return;
            }
            // Fallback: scroll by data-field-path
            const firstField = Object.keys(apiFieldErrors)[0];
            if (firstField) {
              const el = document.querySelector(`[data-field-path="${firstField}"]`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const inputEl = el.querySelector('input, textarea') as HTMLElement;
                if (inputEl) inputEl.focus();
              }
            }
          }, 300);
        } else {
          // Generic error handling for non-field-mapped errors
          let errorMessage = 'Unknown error';

          if (typeof detail === 'object' && detail !== null) {
            if (detail.error === 'ATLAS validation failed' && detail.validation_report) {
              const report = detail.validation_report;
              errorMessage = `Validation failed: ${report.error_count} errors. ${detail.errors?.slice(0, 3).map((e: any) => e.message).join(', ')}`;
            } else if (detail.message) {
              errorMessage = detail.message;
            } else {
              errorMessage = JSON.stringify(detail);
            }
          } else if (typeof detail === 'string') {
            errorMessage = detail;
          } else if (error.message) {
            errorMessage = error.message;
          }

          toast.error(`Failed to send data: ${errorMessage}`, { toastId: 'send-error' });
        }
      }
    } finally {
      // Always release the send lock so the button works again after completion/error
      isSendingRef.current = false;
      setIsSending(false);

      // --- Submit pending ML corrections (fire-and-forget) ---
      const changedFields = changedPredictionFieldsRef.current;
      if (Object.keys(changedFields).length > 0) {
        changedPredictionFieldsRef.current = {};
        try {
          const formData = processing.extractedData || {};
          // Build corrections from changed fields, reading FINAL values from form
          const corrections: Array<{
            field_path: string;
            ml_predicted_value: string | null;
            user_corrected_value: string;
            is_position_field: boolean;
            position_index?: number;
          }> = [];

          for (const [fieldPath, info] of Object.entries(changedFields)) {
            // Read the final value from the form data
            const pathParts = fieldPath.replace(/\[(\d+)\]/g, '.$1').split('.');
            let finalValue: any = formData;
            for (const part of pathParts) {
              finalValue = finalValue?.[part];
            }
            const finalStr = finalValue != null ? String(finalValue) : '';

            // Skip if user reverted back to ML prediction
            if (!finalStr || finalStr === info.ml_predicted_value) continue;

            // Convert ATLAS form path to ML field name for backend storage
            const isPos = info.is_position_field;
            // Extract just the field name part (e.g. "kopf.empfanger.land" → "empfanger.land")
            const fieldName = fieldPath.replace(/^kopf\./, '').replace(/positionen\[\d+\]\./, '');
            const mlFieldName = atlasToMlField(fieldName, isPos) || fieldPath;

            corrections.push({
              field_path: mlFieldName,
              ml_predicted_value: info.ml_predicted_value,
              user_corrected_value: finalStr,
              is_position_field: isPos,
              position_index: info.position_index,
            });
          }

          if (corrections.length > 0) {
            api.post('/api/v1/atlas-ml/corrections', {
              corrections,
              context: formData,
            }).catch(err => {
              console.warn('[ML Learning] Failed to submit corrections:', err);
            });
          }
        } catch (e) {
          // Non-blocking — corrections are best-effort
          console.warn('[ML Learning] Error preparing corrections:', e);
        }
      }
    }
  };

  // ─── Preview dialog handlers ──────────────────────────────────────────────

  /** Called when user clicks "Proceed" in the preview dialog. Executes the actual API push. */
  const handleProceedFromPreview = useCallback(async () => {
    setShowPreviewDialog(false);
    if (!previewSubmissionData || !processing.selectedModule) return;

    const selectedData = previewSubmissionData;
    const filesWithIds = uploadedFiles.filter(f => f.fileId);

    isSendingRef.current = true;
    setIsSending(true);
    setProcessing(prev => ({ ...prev, isProcessing: true, currentStep: 'Sending data to external API...', progress: 70 }));

    try {
      const response = await authService.sendToModule(
        processing.selectedModule.id,
        selectedData,
        sendPartialData,
      );

      setProcessing(prev => ({ ...prev, currentStep: 'Processing complete!', progress: 100 }));

      if (response.transaction_id) {
        toast.success(
          tString('documentProcessing.toasts.dataSentWithDetails',
            processing.selectedModule.name,
            filesWithIds.length,
            response.transaction_id,
          ),
        );
      } else {
        toast.success(tString('documentProcessing.toasts.dataSentSuccess', processing.selectedModule.name));
      }

      setLastTransaction({
        transactionId: response.transaction_id || 'N/A',
        filesProcessed: filesWithIds.length,
        moduleName: processing.selectedModule.name,
        apiResponse: response,
      });

      setTimeout(() => {
        setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0, currentStep: '' }));
        setShowSuccessDialog(true);
      }, 2000);
    } catch (error: any) {
      setProcessing(prev => ({ ...prev, isProcessing: false, progress: 0, currentStep: '' }));
      console.error('Send data error (from preview proceed):', error);

      const detail = error.response?.data?.detail;
      if (error.response?.status === 401 || (typeof detail === 'object' && detail?.error === 'authentication_expired')) {
        const errorData = typeof detail === 'object' ? detail : {};
        const moduleName = errorData.module_name || processing.selectedModule?.name;
        toast.error(`🔒 Authentication expired for ${moduleName}. Please re-authenticate to continue.`, {
          toastId: 'send-error', autoClose: 6000, position: 'top-center',
        });
        await refreshModuleAuthStatus();
        const currentModule = modules.find(m => m.id === errorData.module_id || m.id === processing.selectedModule?.id);
        if (currentModule) setAuthDialog({ open: true, module: currentModule, credentials: { username: '', password: '' } });
      } else if (error.authError) {
        if (error.errorType === 'token_expired') {
          toast.error(tString('documentProcessing.errors.tokenExpired', error.moduleName), { toastId: 'send-error' });
        } else if (error.errorType === 'authentication_required') {
          toast.error(tString('documentProcessing.errors.authRequiredFor', error.moduleName), { toastId: 'send-error' });
        } else {
          toast.error(`Authentication error: ${error.message}`, { toastId: 'send-error' });
        }
        const currentModule = modules.find(m => m.name === error.moduleName || m.id === processing.selectedModule?.id);
        if (currentModule) setAuthDialog({ open: true, module: currentModule, credentials: { username: '', password: '' } });
      } else if (typeof detail === 'object' && detail !== null && detail.error === 'atlas_api_validation_failed' && Array.isArray(detail.field_errors)) {
        // Map ATLAS PutData API errors to form fields
        const apiFieldErrors: Record<string, string> = {};
        const apiGeneralErrors: string[] = [];

        detail.field_errors.forEach((err: { field: string; message: string }) => {
          const rawField = err.field || '';
          const message = translateErrorMessage(err.message || 'Validation error');

          let normalizedPath = rawField;
          normalizedPath = normalizedPath.replace(/position\.(\d+)\./g, 'position[$1].');

          if (normalizedPath && normalizedPath !== 'unknown') {
            if (apiFieldErrors[normalizedPath]) {
              appendFieldError(apiFieldErrors, normalizedPath, message);
            } else {
              apiFieldErrors[normalizedPath] = message;
            }
          } else {
            const displayName = translateErrorMessage(translateFieldPath(rawField));
            apiGeneralErrors.push(`${displayName}: ${message}`);
          }
        });

        setFieldValidationErrors(prev => {
          const merged = { ...prev };
          Object.entries(apiFieldErrors).forEach(([field, msg]) => {
            merged[field] = prev[field] ? `${prev[field]}; ${msg}` : msg;
          });
          return merged;
        });
        if (apiGeneralErrors.length > 0) {
          setGeneralValidationErrors(prev => [...prev, ...apiGeneralErrors]);
        }

        // Switch back to form so the user can fix the errors.
        setPreviewSubmissionData(null);
        setActiveWorkflowStep('analyse');
        setViewMode('form');

        // After the form re-renders, scroll to and focus the first error field.
        setTimeout(() => {
          const errorFields = document.querySelectorAll('.Mui-error');
          if (errorFields.length > 0) {
            errorFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
            const inputEl = errorFields[0].querySelector('input, textarea') as HTMLElement;
            if (inputEl) inputEl.focus();
            return;
          }
          // Fallback: query by data-field-path attribute
          const firstField = Object.keys(apiFieldErrors)[0];
          if (firstField) {
            const el = document.querySelector(`[data-field-path="${firstField}"]`);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              const inputEl = el.querySelector('input, textarea') as HTMLElement;
              if (inputEl) inputEl.focus();
            }
          }
        }, 500);
      } else {
        const errorMsg = typeof detail === 'string' ? detail : (detail?.message ?? error.message ?? 'Unknown error');
        toast.error(`Failed to send data: ${errorMsg}`, { toastId: 'send-error' });
      }
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  }, [previewSubmissionData, processing.selectedModule, sendPartialData, uploadedFiles, tString, modules]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Called when user clicks "Edit" (or closes) the preview dialog. */
  const handleEditFromPreview = useCallback(() => {
    setShowPreviewDialog(false);
    setPreviewSubmissionData(null);
  }, []);

  /** Open the preview popup with the current form data (no validation required). */
  const handleOpenPreview = useCallback(() => {
    if (!processing.extractedData) return;
    const data = filterSelectedFields(processing.extractedData, processing.fieldSelections);
    setPreviewSubmissionData(data);
    setShowPreviewDialog(true);
  }, [processing.extractedData, processing.fieldSelections]);

  // Correlation validation callbacks
  const handleCorrelationValidationComplete = (result: ComprehensiveCorrelationResponse) => {
    // console.log('📊 Correlation validation complete:', result);
    setCorrelationResult(result);
    setIsCorrelationValid(result.correlation_valid && result.atlas_validation.can_submit);

    if (result.correlation_valid && result.atlas_validation.can_submit) {
      toast.success('✓ Documents are properly correlated and ready for ATLAS submission');
    } else if (result.correlation_details.correlation_status === 'partially_correlated') {
      toast.warning('⚠ Some documents are correlated, but issues were found');
    } else {
      toast.error('✗ Documents are not properly correlated. Please review the issues.');
    }
  };

  const handleCorrelationValidationError = (error: string) => {
    console.error('❌ Correlation validation error:', error);
    toast.error(`Correlation validation failed: ${error}`);
    setIsCorrelationValid(false);
  };

  const handleFieldToggle = (fieldPath: string) => {
    setProcessing(prev => ({
      ...prev,
      fieldSelections: {
        ...prev.fieldSelections,
        [fieldPath]: !prev.fieldSelections[fieldPath],
      },
    }));
  };

  const generateMockExtractedData = (dataStructure: any): any => {
    // Generate mock data based on the module's data structure
    if (!dataStructure) {
      return {
        name: 'John Doe',
        email: 'john@example.com',
        company: 'Acme Corp',
        phone: '+1-555-0123',
      };
    }

    // Handle field-based structure
    if (dataStructure.fields) {
      const mockData: any = {};
      dataStructure.fields.forEach((field: any) => {
        mockData[field.name] = generateMockValue(field.type);
      });
      return mockData;
    }

    // Handle free-form JSON structure
    return generateMockFromSchema(dataStructure);
  };

  const generateMockValue = (type: string): any => {
    switch (type) {
      case 'string': return 'Sample Text';
      case 'email': return 'example@domain.com';
      case 'number': return 42;
      case 'boolean': return true;
      case 'date': return '2024-01-15';
      case 'phone': return '+1-555-0123';
      case 'url': return 'https://example.com';
      default: return 'Sample Value';
    }
  };

  const generateMockFromSchema = (schema: any): any => {
    if (typeof schema !== 'object' || schema === null) return schema;

    const result: any = {};
    Object.keys(schema).forEach(key => {
      const value = schema[key];
      if (typeof value === 'string') {
        result[key] = generateMockValue(value);
      } else if (typeof value === 'object') {
        result[key] = generateMockFromSchema(value);
      } else {
        result[key] = value;
      }
    });
    return result;
  };

  const flattenObject = (obj: any, prefix = ''): Record<string, any> => {
    let flattened: Record<string, any> = {};

    Object.keys(obj).forEach(key => {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (Array.isArray(value)) {
        // Handle arrays by expanding them with index notation like position[0], position[1]
        value.forEach((item, index) => {
          const arrayKey = `${newKey}[${index}]`;
          if (typeof item === 'object' && item !== null) {
            // Recursively flatten array items that are objects
            Object.assign(flattened, flattenObject(item, arrayKey));
          } else {
            // For primitive array items, add directly
            flattened[arrayKey] = item;
          }
        });
      } else if (typeof value === 'object' && value !== null) {
        Object.assign(flattened, flattenObject(value, newKey));
      } else {
        flattened[newKey] = value;
      }
    });

    return flattened;
  };

  // Helper to set nested values preserving ATLAS structure
  const setNestedValue = (obj: any, path: string, value: any) => {
    const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const nextKey = keys[i + 1];
      const isArrayIndex = !isNaN(parseInt(nextKey));

      // Guard: if current itself is a primitive we cannot set properties on it — stop.
      if (typeof current !== 'object' || current === null) return;

      // If current node is a primitive (string, number, etc.) we cannot navigate into it.
      // Replace it with the appropriate container type so navigation can continue.
      if (typeof current[key] !== 'object' || current[key] === null) {
        current[key] = isArrayIndex ? [] : {};
      }

      // If the next key is a numeric index but the current node is not an array, convert it.
      if (isArrayIndex && !Array.isArray(current[key])) {
        current[key] = [];
      }

      current = current[key];

      // Safety: if current became a non-object (shouldn't happen after above guards), stop
      if (typeof current !== 'object' || current === null) return;
    }

    const finalKey = keys[keys.length - 1];
    // Guard: only set if current is an object/array (not a primitive)
    if (typeof current === 'object' && current !== null) {
      current[finalKey] = value;
    }
  };

  const filterSelectedFields = (data: any, selections: Record<string, boolean>): any => {
    // Preserve ATLAS nested structure for proper API submission
    // Include ALL fields from data that have values AND are selected
    const result: any = {};
    const includedFields: string[] = [];
    const skippedFields: string[] = [];

    // First, get all fields from the extracted data
    const allDataFields = flattenObject(data);

    // Include fields that are either:
    // 1. Explicitly selected in fieldSelections
    // 2. Have values in extractedData (auto-include all data fields)
    Object.keys(allDataFields).forEach(fieldPath => {
      const value = allDataFields[fieldPath];
      const isSelected = selections[fieldPath] !== false; // Default to true if not explicitly deselected

      // Include if selected AND has a meaningful value
      if (isSelected && value !== undefined && value !== null && value !== '') {
        setNestedValue(result, fieldPath, value);
        includedFields.push(fieldPath);
      } else if (!isSelected) {
        skippedFields.push(fieldPath);
      }
    });

    // Also include any fields in selections that might have been manually added
    Object.keys(selections).forEach(fieldPath => {
      if (selections[fieldPath] && !includedFields.includes(fieldPath)) {
        const value = getNestedValue(data, fieldPath);
        if (value !== undefined && value !== null && value !== '') {
          setNestedValue(result, fieldPath, value);
          includedFields.push(fieldPath);
        }
      }
    });

    // Log what's being included/excluded
    // console.log('📊 filterSelectedFields Summary:');
    // console.log(`  - Total data fields: ${Object.keys(allDataFields).length}`);
    // console.log(`  - Included fields: ${includedFields.length}`);
    // console.log(`  - Skipped fields: ${skippedFields.length}`);
    if (skippedFields.length > 0 && skippedFields.length <= 10) {
      // console.log(`  - Skipped: ${skippedFields.join(', ')}`);
    }

    return result;
  };

  // Type definition for table items
  type TableItem = {
    key: string;
    value: any;
    fieldPath: string;
    isMissing?: boolean;
  };

  const flattenDataForTable = useCallback((data: any, prefix = ''): Array<TableItem> => {
    const result: Array<TableItem> = [];

    Object.keys(data).forEach(key => {
      const value = data[key];
      const fieldPath = prefix ? `${prefix}.${key}` : key;

      if (Array.isArray(value)) {
        // For arrays, expand each item with index notation: position[0], position[1], etc.
        value.forEach((item, index) => {
          const arrayFieldPath = `${fieldPath}[${index}]`;
          if (typeof item === 'object' && item !== null) {
            // Recursively flatten array items that are objects
            result.push(...flattenDataForTable(item, arrayFieldPath));
          } else {
            // For primitive array items, add directly
            result.push({
              key: `${fieldPath}[${index}]`,
              value: String(item),
              fieldPath: arrayFieldPath
            });
          }
        });
      } else if (typeof value === 'object' && value !== null) {
        // For nested objects, add them as sub-items
        result.push(...flattenDataForTable(value, fieldPath));
      } else {
        // Add leaf values to the table
        result.push({
          key: fieldPath,
          value: String(value),
          fieldPath
        });
      }
    });

    return result;
  }, []);

  // Helper function to get nested value from object by path
  const getNestedValue = (obj: any, path: string): any => {
    const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    return keys.reduce((acc, key) => {
      if (acc === null || acc === undefined) return undefined;
      return acc[key];
    }, obj);
  };

  // Helper function to translate field paths to human-readable labels
  // Helper function to translate field labels (English labels from ATLAS_FIELDS to current language)
  const translateFieldLabel = (englishLabel: string): string => {
    // Create a mapping of English labels to translation keys
    const labelMap: Record<string, string> = {
      // Kopf (Header) fields
      'Invoice Number (LRN)': tString('atlasFields.invoiceNumber'),
      'Type of Registration': tString('atlasFields.declarationType'),
      'Declaration Type': tString('atlasFields.declarationType'),
      'Export Declaration Type': tString('atlasFields.exportDeclarationType'),
      'Participant Constellation': tString('atlasFields.participantConstellation'),
      'Declaration Timestamp': tString('atlasFields.declarationTimestamp'),
      'Security Indicator': tString('atlasFields.securityIndicator'),
      'Customs Office of Presentation': tString('atlasFields.customsOfficePresentation'),
      'Export Customs Office': tString('atlasFields.exportCustomsOffice'),
      'Total Gross Mass': tString('atlasFields.totalGrossMass'),
      'Country of Destination': tString('atlasFields.countryOfDestination'),
      'Incoterm Code': tString('atlasFields.incotermCode'),
      'Total Invoice Amount': tString('atlasFields.totalInvoiceAmount'),
      'Invoice Currency': tString('atlasFields.invoiceCurrency'),
      'Invoice Date': tString('atlasFields.invoiceDate'),

      // Exporter fields
      'Exporter Name': tString('atlasFields.exporterName'),
      'Exporter Street': tString('atlasFields.exporterStreet'),
      'Exporter Postal Code': tString('atlasFields.exporterPostalCode'),
      'Exporter City': tString('atlasFields.exporterCity'),
      'Exporter Country': tString('atlasFields.exporterCountry'),
      'Exporter EORI/TIN': tString('atlasFields.exporterEORI'),

      // Recipient fields
      'Recipient Name': tString('atlasFields.recipientName'),
      'Recipient Street': tString('atlasFields.recipientStreet'),
      'Recipient Postal Code': tString('atlasFields.recipientPostalCode'),
      'Recipient City': tString('atlasFields.recipientCity'),
      'Recipient Country': tString('atlasFields.recipientCountry'),

      // Shipment fields
      'Number of Packages': tString('atlasFields.numberOfPackages'),
      'Total Gross Weight (kg)': tString('atlasFields.totalGrossWeight'),
      'Transport Mode': tString('atlasFields.transportMode'),

      // Delivery fields
      'Destination Country': tString('atlasFields.destinationCountry'),
      'Incoterm': tString('atlasFields.incoterm'),

      // Additional fields - Header
      'Customs Office Number': tString('atlasFields.customsOfficeNumber'),
      'Special Circumstances': tString('atlasFields.specialCircumstances'),
      'Additional Location Identifier': tString('atlasFields.additionalLocationIdentifier'),

      // Additional fields - Border Transport
      'Border Vehicle Nationality': tString('atlasFields.borderVehicleNationality'),
      'Border Vehicle Registration': tString('atlasFields.borderVehicleRegistration'),

      // Additional fields - Representative
      'Representative EORI/TIN': tString('atlasFields.representativeTIN'),
      'Representative TIN': tString('atlasFields.representativeTIN'),
      'Representative Name': tString('atlasFields.representativeName'),
      'Representative Phone': tString('atlasFields.representativePhone'),
      'Representative Branch Number': tString('atlasFields.representativeBranchNumber'),
      'Representative Contact Person': tString('atlasFields.representativeContactPerson'),

      // Additional fields - Subcontractor
      'Subcontractor Branch Number': tString('atlasFields.subcontractorBranchNumber'),
    };

    // Check if it's a position field (with "Item X" suffix)
    const itemMatch = englishLabel.match(/^(.+?)\s*\(Item (\d+)\)$/);
    if (itemMatch) {
      const baseLabel = itemMatch[1];
      const itemNumber = itemMatch[2];

      const positionLabelMap: Record<string, string> = {
        'Position Number': tString('atlasFields.positionNumber'),
        'Requested Procedure': tString('atlasFields.requestedProcedure'),
        'Previous Procedure': tString('atlasFields.previousProcedure'),
        'Country of Origin': tString('atlasFields.countryOfOrigin'),
        'Product Description': tString('atlasFields.productDescription'),
        'HS/CN Code': tString('atlasFields.hsCode'),
        'Gross Weight (kg)': tString('atlasFields.grossWeight'),
        'Net Weight (kg)': tString('atlasFields.netWeight'),
        'Quantity': tString('atlasFields.quantity'),
        'User ID (System)': tString('atlasFields.userId'),
        'Region of Origin': tString('atlasFields.regionOfOrigin'),
        'Region of Origin/Dispatch': tString('atlasFields.regionOfOrigin'),
      };

      const translatedBase = positionLabelMap[baseLabel] || baseLabel;
      return `${translatedBase} (${tString('atlasFields.item')} ${itemNumber})`;
    }

    // For position fields without item number (shouldn't happen but handle it)
    const positionLabelMapSimple: Record<string, string> = {
      'Position Number': tString('atlasFields.positionNumber'),
      'Requested Procedure': tString('atlasFields.requestedProcedure'),
      'Previous Procedure': tString('atlasFields.previousProcedure'),
      'Country of Origin': tString('atlasFields.countryOfOrigin'),
      'Product Description': tString('atlasFields.productDescription'),
      'HS/CN Code': tString('atlasFields.hsCode'),
      'Gross Weight (kg)': tString('atlasFields.grossWeight'),
      'Net Weight (kg)': tString('atlasFields.netWeight'),
      'Quantity': tString('atlasFields.quantity'),
      'User ID (System)': tString('atlasFields.userId'),
      'Region of Origin': tString('atlasFields.regionOfOrigin'),
      'Region of Origin/Dispatch': tString('atlasFields.regionOfOrigin'),
    };

    return labelMap[englishLabel] || positionLabelMapSimple[englishLabel] || englishLabel;
  };

  // Add missing required ATLAS fields as RED rows for manual entry
  const addMissingRequiredFields = useCallback((extractedItems: Array<TableItem>): Array<TableItem> => {
    // Only add missing fields for ATLAS module
    if (!processing.selectedModule?.name?.toLowerCase().includes('atlas')) {
      return extractedItems;
    }

    // Start with ALL ATLAS required fields to ensure they're all visible
    const result: Array<TableItem> = [];

    // Use dynamic fields based on actual data (handles multiple line items)
    const atlasFields = getATLASFields(processing.extractedData);

    // Check each ATLAS required field
    atlasFields.forEach(field => {
      const fieldPath = field.path;

      // Look for existing item with EXACT path matching only (no fuzzy matching for position fields)
      const existingItem = extractedItems.find(item => {
        // Exact match
        if (item.fieldPath === fieldPath) return true;

        // Handle array notation differences: position[0] vs position.0
        // Normalize both paths to bracket notation for comparison
        const normalizedItemPath = item.fieldPath.replace(/\.(\d+)\./g, '[$1].');
        const normalizedFieldPath = fieldPath.replace(/\.(\d+)\./g, '[$1].');

        if (normalizedItemPath === normalizedFieldPath) return true;

        // NO fuzzy matching for position fields - must match exact index
        // This prevents position[1] from matching position[0]
        return false;
      });

      // Check if field actually has a value using getNestedValue on actual data
      const actualValue = getNestedValue(processing.extractedData, fieldPath);
      const hasValue = actualValue !== null && actualValue !== undefined && actualValue !== '';

      if (existingItem && hasValue) {
        // Field exists AND has a value - add it with current value but use the label as key
        result.push({
          ...existingItem,
          key: field.label, // Always use the user-friendly label, not the technical field path
          fieldPath: fieldPath,
          value: existingItem.value
        });
      } else {
        // Field is missing OR has no value - add as empty RED row for manual entry
        result.push({
          key: field.label,
          value: hasValue ? String(actualValue) : '', // Use actual value if it exists
          fieldPath: fieldPath,
          isMissing: !hasValue
        });
      }
    });

    // Add any remaining non-ATLAS fields from extracted data
    extractedItems.forEach(item => {
      // Use exact matching to check if item is an ATLAS field
      const isAtlasField = atlasFields.some(field => {
        if (item.fieldPath === field.path) return true;

        // Normalize both paths to bracket notation for comparison
        const normalizedItemPath = item.fieldPath.replace(/\.(\d+)\./g, '[$1].');
        const normalizedFieldPath = field.path.replace(/\.(\d+)\./g, '[$1].');

        return normalizedItemPath === normalizedFieldPath;
      });

      if (!isAtlasField && !result.some(r => r.fieldPath === item.fieldPath)) {
        result.push(item);
      }
    });

    // Sort by ATLAS field definition order (stable - prevents jumping when typing)
    return result.sort((a, b) => {
      // Use exact matching for finding field indices
      const indexA = atlasFields.findIndex(f => {
        if (a.fieldPath === f.path) return true;
        const normalizedA = a.fieldPath.replace(/\.(\d+)\./g, '[$1].');
        const normalizedF = f.path.replace(/\.(\d+)\./g, '[$1].');
        return normalizedA === normalizedF;
      });

      const indexB = atlasFields.findIndex(f => {
        if (b.fieldPath === f.path) return true;
        const normalizedB = b.fieldPath.replace(/\.(\d+)\./g, '[$1].');
        const normalizedF = f.path.replace(/\.(\d+)\./g, '[$1].');
        return normalizedB === normalizedF;
      });

      // If both found in atlasFields, sort by that order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // ATLAS fields come first
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // For non-ATLAS fields, maintain original order
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processing.selectedModule, processing.extractedData, processing.extractedData?.position]);

  // Primary position field names - only these are shown by default in line items.
  // All other position fields go into the collapsible "Optional Fields" section.
  const PRIMARY_POSITION_FIELDS = useMemo(() => [
    'warePositionsnummer',     // Position Number
    'wareWarenbezeichnung',    // Product Description
    'menge',                   // Quantity
    'mengeinbesondererMabeinheit', // Quantity in Special Unit
    'wareWarennummerKN8',      // HS/CN Code
    'wareRohmasse',            // Gross Weight
    'wareEigenmasse',          // Net Weight
  ], []);

  // Helper: Check if a position field path is optional (not in the primary fields list)
  const isOptionalPositionField = useCallback((fieldPath: string): boolean => {
    if (!fieldPath.startsWith('position[')) return false;
    // Extract the field name after position[N]. e.g. "position[0].wareWarenbezeichnung" -> "wareWarenbezeichnung"
    // Also handle nested paths like "position[0].verpackung[0].artderVerpackung" -> "verpackung[0].artderVerpackung"
    const fieldNameMatch = fieldPath.match(/^position\[\d+\]\.(.+)$/);
    if (!fieldNameMatch) return true;
    const fieldName = fieldNameMatch[1];
    return !PRIMARY_POSITION_FIELDS.includes(fieldName);
  }, [PRIMARY_POSITION_FIELDS]);

  // Helper: Extract the position index from a field path (returns -1 if not a position field)
  const getPositionIndex = (fieldPath: string): number => {
    const match = fieldPath.match(/^position\[(\d+)\]/);
    return match ? parseInt(match[1], 10) : -1;
  };

  // Determine field status for color coding (only for ATLAS module)
  // ===== DISABLED FOR TESTING - Only gRPC and ATLAS API errors should be shown =====
  const getFieldStatus = useCallback((fieldPath: string, extractedData: any): 'missing' | 'verify' | 'complete' | 'optional' => {
    // ===== TESTING MODE: Disable required field validation visuals =====
    // All fields return 'optional' to remove red/yellow highlighting
    // Only gRPC validation and ATLAS API errors will indicate issues
    // console.log(`[getFieldStatus] DISABLED FOR TESTING - returning 'optional' for: ${fieldPath}`);
    return 'optional';

    /* COMMENTED OUT - Required field visual validation
    // ====== ENHANCED DEBUG LOGGING ======
    // console.log('[getFieldStatus] ==== FUNCTION CALLED ====');
    // console.log('[getFieldStatus] fieldPath:', fieldPath);
    // console.log('[getFieldStatus] processing.selectedModule:', processing.selectedModule);
    // console.log('[getFieldStatus] module name:', processing.selectedModule?.name);
    // console.log('[getFieldStatus] module name lowercase:', processing.selectedModule?.name?.toLowerCase());
    // console.log('[getFieldStatus] includes atlas:', processing.selectedModule?.name?.toLowerCase().includes('atlas'));
    // console.log('[getFieldStatus] extractedData keys:', Object.keys(extractedData || {}));
    // ====== END DEBUG LOGGING ======

    // Only apply color coding for BEO ATLAS module
    if (!processing.selectedModule?.name?.toLowerCase().includes('atlas')) {
      // console.log(`[getFieldStatus] Not ATLAS module (${processing.selectedModule?.name}), returning optional for: ${fieldPath}`);
      return 'optional';
    }

    // console.log(`[getFieldStatus] Checking ${fieldPath} for ATLAS module`);

    // FIX: Use dynamic ATLAS fields and check field.required property
    // This ensures correct validation for multiple position items and optional fields
    const atlasFields = getATLASFields(extractedData);
    const matchedField = atlasFields.find(field => {
      // Exact match first
      if (field.path === fieldPath) return true;

      // Handle array notation differences: position[0] vs position.0
      const normalizedFieldPath = field.path.replace(/\.(\d+)\./g, '[$1].');
      const normalizedCheckPath = fieldPath.replace(/\.(\d+)\./g, '[$1].');

      return normalizedFieldPath === normalizedCheckPath;
    });

    const isRequired = matchedField?.required === true;

    // Get actual value from nested data structure (not flattened string)
    const actualValue = getNestedValue(extractedData, fieldPath);

    // Use same validation logic as AtlasValidationPreview to match BEO ATLAS requirements
    const hasValue = actualValue !== null && actualValue !== undefined && actualValue !== '';

    // console.log(`[getFieldStatus] ${fieldPath}: isRequired=${isRequired}, hasValue=${hasValue}, matchedField=${matchedField?.label}, value=${JSON.stringify(actualValue)?.substring(0, 50)}`);

    if (!isRequired) return 'optional';  // Not ATLAS required field or optional field
    if (!hasValue) {
      // console.log(`[getFieldStatus] ${fieldPath} → MISSING (RED)`);
      return 'missing';     // RED - required but missing
    }

    // Check if field needs verification (for visual highlighting only)
    const needsVerification = VERIFICATION_REQUIRED_FIELDS.some(vField =>
      fieldPath.includes(vField.replace('[0]', ''))
    );

    if (needsVerification) {
      // console.log(`[getFieldStatus] ${fieldPath} → VERIFY (YELLOW)`);
      return 'verify';  // YELLOW - has value but needs verification
    }

    // console.log(`[getFieldStatus] ${fieldPath} → COMPLETE (GREEN)`);
    return 'complete';  // GREEN - complete and validated
    END COMMENTED OUT */
  }, [processing.selectedModule]);

  // Count fields by status for legend - using useMemo for dynamic updates
  // IMPORTANT: Use EXACT same logic as AtlasValidationPreview to ensure perfect match
  const fieldStatusCounts = useMemo(() => {
    if (!processing.extractedData || !processing.selectedModule?.name?.toLowerCase().includes('atlas')) {
      return { complete: 0, missing: 0, verify: 0, optional: 0 };
    }

    const counts = { complete: 0, missing: 0, verify: 0, optional: 0 };

    // Use dynamic fields based on actual data (handles multiple line items)
    // This ensures we count ALL fields including all position items
    const atlasFields = getATLASFields(processing.extractedData);

    atlasFields.forEach((field) => {
      const value = getNestedValue(processing.extractedData, field.path);

      // Use same validation logic as AtlasValidationPreview
      if (value !== null && value !== undefined && value !== '') {
        counts.complete++;
      } else if (field.required) {
        counts.missing++;
      }
    });

    // Consistency check logging for debugging validation discrepancies
    // console.log('📊 Field Status Consistency Check:');
    // console.log(`  - Missing count: ${counts.missing}`);
    // console.log(`  - Complete count: ${counts.complete}`);
    // console.log(`  - Total required fields: ${counts.missing + counts.complete}`);
    // console.log(`  - Dynamic ATLAS fields (all): ${atlasFields.length}`);
    // console.log(`  - Required fields: ${atlasFields.filter(f => f.required).length}`);
    // console.log(`  - Optional fields: ${atlasFields.filter(f => !f.required).length}`);

    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processing.extractedData, processing.selectedModule, processing.extractedData?.position]);

  // --- ML Correction Tracking: record which predictions are shown ---
  const handlePredictionShown = useCallback((fieldPath: string, predictedValue: string) => {
    activePredictionsRef.current[fieldPath] = predictedValue;
  }, []);

  const handleFieldValueChange = (fieldPath: string, newValue: string) => {
    // Update the extracted data with the new value.
    // Handle array notation like position[0].ware.menge
    //
    // The clone is taken from `prev` INSIDE the updater, never from the
    // `processing` captured in this closure. "Apply to all N items" calls this
    // N times in one tick; React has not re-rendered between the calls, so a
    // closure clone would give every call the SAME starting data and each
    // setProcessing would overwrite the last — only the final path survived.
    // That is why the button had to be pressed once per item: each press
    // landed exactly one value, because the state only advances between
    // renders. Cloning from `prev` makes the calls accumulate.
    const pathParts = fieldPath.replace(/\[(\d+)\]/g, '.$1').split('.');

    setProcessing(prev => {
      const newData = JSON.parse(JSON.stringify(prev.extractedData || {}));

      // Create nested structure if it doesn't exist
      let current: any = newData;
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        const nextPart = pathParts[i + 1];

        // Check if next part is an array index
        const isArrayIndex = !isNaN(parseInt(nextPart));

        // If current[part] is a primitive (string/number), replace with the right container
        if (typeof current[part] !== 'object' || current[part] === null) {
          current[part] = isArrayIndex ? [] : {};
        }

        // If next key is numeric but current[part] is not an array, convert it
        if (isArrayIndex && !Array.isArray(current[part])) {
          current[part] = [];
        }

        current = current[part];

        // Safety: if current became a non-object, leave the state untouched
        if (typeof current !== 'object' || current === null) return prev;
      }

      // Set the final value (only if current is a proper object/array)
      const finalKey = pathParts[pathParts.length - 1];
      if (typeof current === 'object' && current !== null) {
        current[finalKey] = newValue;
      }

      return { ...prev, extractedData: newData };
    });

    // Auto-check the field when user enters a value
    if (newValue && newValue.trim() !== '') {
      setProcessing(prev => ({
        ...prev,
        fieldSelections: {
          ...prev.fieldSelections,
          [fieldPath]: true
        }
      }));
    }

    // --- ML Correction Tracking: detect when user changes a predicted value ---
    const predictedValue = activePredictionsRef.current[fieldPath];
    if (predictedValue && newValue && newValue !== predictedValue) {
      // Mark this field as changed from its ML prediction.
      // We record the ML value now but read the FINAL user value at submit time
      // to avoid capturing intermediate typing (e.g. "G", "Ge", "Germany").
      const isPosition = fieldPath.includes('positionen[') || fieldPath.includes('position[');
      const posMatch = fieldPath.match(/(?:positionen|position)\[(\d+)\]/);
      const posIndex = posMatch ? parseInt(posMatch[1], 10) : undefined;
      changedPredictionFieldsRef.current[fieldPath] = {
        ml_predicted_value: predictedValue,
        is_position_field: isPosition,
        position_index: posIndex,
      };
    }
  };

  // --- Customs Audit handlers ---
  const handleRunAudit = async () => {
    if (!processing.extractedData) return;
    setIsAuditing(true);
    setAuditReport(null);
    setAppliedFixes(new Map());
    try {
      const report = await customsAuditService.runFullAudit({
        declaration_data: processing.extractedData,
        documents: [],
        supporting_files: uploadedFiles.filter(f => f.fileId).map(f => f.fileId!),
      });
      // Try to enrich findings with suggested fixes
      try {
        const allFindings = report.area_results.flatMap(ar => ar.findings);
        if (allFindings.length > 0) {
          const enriched = await customsAuditService.suggestFixes(
            processing.extractedData,
            allFindings,
          );
          // Map enriched findings back to area results
          let idx = 0;
          for (const ar of report.area_results) {
            ar.findings = enriched.slice(idx, idx + ar.findings.length);
            idx += ar.findings.length;
          }
        }
      } catch {
        // Fixes are optional, audit still shows without them
      }
      setAuditReport(report);
      setShowAuditResults(true);
    } catch (err: any) {
      toast.error(`Audit failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setIsAuditing(false);
    }
  };

  const handleAuditApplyFix = (field: string, value: string) => {
    const currentValue = getNestedValue(processing.extractedData, field) || '';
    setAppliedFixes(prev => new Map(prev).set(field, { originalValue: String(currentValue), newValue: value }));
    handleFieldValueChange(field, value);
    toast.success(`Applied fix to ${field}`);
  };

  const handleAuditUndoFix = (field: string) => {
    const fix = appliedFixes.get(field);
    if (fix) {
      handleFieldValueChange(field, fix.originalValue);
      setAppliedFixes(prev => { const next = new Map(prev); next.delete(field); return next; });
      toast.info(`Reverted ${field}`);
    }
  };

  const handleAuditViewField = (field: string) => {
    const el = document.querySelector(`[data-field-path="${field}"]`) ||
      document.getElementById(`atlas-field-${field.replace(/\./g, '-').replace(/\[/g, '-').replace(/\]/g, '')}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (el as HTMLElement).style.outline = '2px solid #1976d2';
      setTimeout(() => { (el as HTMLElement).style.outline = ''; }, 3000);
    }
  };

  const handleApplyAllAuditFixes = () => {
    if (!auditReport) return;
    let count = 0;
    for (const ar of auditReport.area_results) {
      for (const f of ar.findings) {
        if (f.suggested_fix && f.field && (f.fix_confidence || 0) >= 0.6 && !appliedFixes.has(f.field)) {
          const currentValue = getNestedValue(processing.extractedData, f.field) || '';
          setAppliedFixes(prev => new Map(prev).set(f.field!, { originalValue: String(currentValue), newValue: f.suggested_fix! }));
          handleFieldValueChange(f.field, f.suggested_fix);
          count++;
        }
      }
    }
    setShowApplyDialog(false);
    if (count > 0) toast.success(`Applied ${count} fixes`);
    else toast.info('No applicable fixes remaining');
  };

  const handleDownloadAuditPdf = async () => {
    if (!auditReport?.id) {
      toast.error('Audit report must be saved before PDF export');
      return;
    }
    try {
      await customsAuditService.downloadPdf(auditReport.id);
    } catch {
      toast.error('PDF download failed');
    }
  };

  // (Legacy in-page Tariff Classifier dialog handlers removed —
  // the standalone Tariff Classifier page is now the canonical entrypoint.
  // `autoFillMissingHSCodes` below still uses the batch classifier service
  // for silent inline AI-fill of empty HS code fields.)

  /**
   * Silently classify HS codes for line items that have no HS code after extraction.
   * Runs in parallel with extraction — never blocks the UI.
   * One batch Mistral API call covers all missing items.
   */
  const autoFillMissingHSCodes = useCallback(async (extractedData: any) => {
    const positionKey = Array.isArray(extractedData?.position) ? 'position'
      : Array.isArray(extractedData?.positionen) ? 'positionen'
      : null;
    if (!positionKey) return;

    const positions: any[] = extractedData[positionKey];

    // Collect positions that are missing an HS code AND have a product description to classify
    const missingItems = positions
      .map((pos: any, idx: number) => ({ pos, idx }))
      .filter(({ pos }) =>
        pos &&
        !pos.wareWarennummerKN8 &&
        (pos.wareWarenbezeichnung || pos.description || '').trim() !== ''
      );

    if (missingItems.length === 0) return;

    try {
      // Only send product description — nothing else — matching what the manual classifier sends
      const products = missingItems.map(({ pos }) => ({
        description: (pos.wareWarenbezeichnung || pos.description || '').trim(),
      }));

      console.log(
        `[Auto HS Classify] Batch request — ${products.length} item(s):`,
        products.map((p, i) => `[${missingItems[i].idx}] "${p.description}"`),
      );

      const results = await classifyBatch({ products, return_alternatives: false });

      console.log(
        '[Auto HS Classify] Batch results:',
        results.map((r, i) => ({
          idx: missingItems[i]?.idx,
          hs_code: r.hs_code,
          confidence: r.confidence,
          error: r.error,
        })),
      );

      // Compute filled paths and new data outside the updater to keep it pure
      const filledPaths: string[] = [];
      const fillMap: Record<number, string> = {}; // originalIdx → hs_code

      results.forEach((classification, batchIdx) => {
        const originalIdx = missingItems[batchIdx]?.idx;
        if (originalIdx === undefined) return;
        if (
          !classification.error &&
          classification.hs_code &&
          classification.confidence >= 0.6
        ) {
          fillMap[originalIdx] = classification.hs_code;
          filledPaths.push(`position[${originalIdx}].wareWarennummerKN8`);
        }
      });

      if (filledPaths.length === 0) return;

      setProcessing(prev => {
        if (!prev.extractedData) return prev;
        const newData = JSON.parse(JSON.stringify(prev.extractedData));
        const posArr: any[] = newData[positionKey] || [];

        Object.entries(fillMap).forEach(([idxStr, hsCode]) => {
          const idx = Number(idxStr);
          // Final guard: only fill if field is still empty in current state
          if (posArr[idx] && !posArr[idx].wareWarennummerKN8) {
            posArr[idx].wareWarennummerKN8 = hsCode;
          } else {
            // field was filled by user before result arrived — remove from paths
            const path = `position[${idx}].wareWarennummerKN8`;
            const i = filledPaths.indexOf(path);
            if (i !== -1) filledPaths.splice(i, 1);
          }
        });

        return { ...prev, extractedData: newData };
      });

      setAiClassifiedHSCodes(prev => new Set(Array.from(prev).concat(filledPaths)));
    } catch (error) {
      // Silent failure — leave HS code fields empty, log only
      console.error('[Auto HS Classify] Classification failed (silent):', error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAutoClassifyAll = async () => {
    if (!processing.extractedData?.position || !Array.isArray(processing.extractedData.position)) {
      toast.error('No position data found to classify');
      return;
    }

    // console.log('[Auto-Classify] Starting classification for positions:', processing.extractedData.position);

    setClassifyingAll(true);

    try {
      toast.info(tString('documentProcessing.toasts.classifyingProducts', processing.extractedData.position.length));

      const results = await autoClassifyAllPositions(processing.extractedData.position);

      // console.log('[Auto-Classify] Classification results:', results);

      // Apply all classifications
      const newData = JSON.parse(JSON.stringify(processing.extractedData));
      let successCount = 0;
      let failedCount = 0;

      results.forEach(({ positionIndex, classification }) => {
        if (classification.error) {
          console.error(`[Auto-Classify] Failed for position ${positionIndex}:`, classification.error);
          failedCount++;
        } else if (classification.hs_code && classification.confidence >= 0.6) {
          if (!newData.position[positionIndex]) {
            newData.position[positionIndex] = {};
          }
          newData.position[positionIndex].wareWarennummerKN8 = classification.hs_code;
          // console.log(`[Auto-Classify] Position ${positionIndex} classified as ${classification.hs_code} (${Math.round(classification.confidence * 100)}%)`);
          successCount++;
        } else {
          console.warn(`[Auto-Classify] Low confidence for position ${positionIndex}: ${classification.confidence}`);
          failedCount++;
        }
      });

      setProcessing(prev => ({ ...prev, extractedData: newData }));

      if (successCount > 0) {
        toast.success(tString('documentProcessing.toasts.classificationSuccess', successCount, results.length, failedCount));
      } else {
        toast.error(tString('documentProcessing.toasts.classificationFailed', results.length));
      }
    } catch (error: any) {
      console.error('[Auto-Classify] Error:', error);
      console.error('[Auto-Classify] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      const errorMessage = error.response?.data?.detail || error.message || 'Auto-classification failed';
      toast.error(`Classification failed: ${errorMessage}`);
    } finally {
      setClassifyingAll(false);
    }
  };

  // ML Predictions Sidebar handlers
  const fetchMLPredictions = useCallback(async () => {
    if (!processing.extractedData) return;

    // Don't retry if ML fetch has already failed for this document
    if (mlFetchFailedRef.current) {
      // console.log('🤖 ML Predictions: Skipping fetch - already failed for this document');
      return;
    }

    try {
      // Convert extracted data to the format expected by ML API
      let headerData = {};
      let positionsData: any[] = [];

      if (processing.extractedData?.kopf) {
        // ATLAS v2.1 format
        headerData = processing.extractedData.kopf || {};
        positionsData = processing.extractedData.positionen || processing.extractedData.position || [];
      } else if (processing.extractedData?.header) {
        // Direct header/positions format
        headerData = processing.extractedData.header || {};
        positionsData = processing.extractedData.positions || [];
      } else {
        // Fallback to entire extracted data as header
        headerData = processing.extractedData || {};
        positionsData = [];
      }

      const mlRequest = {
        header: headerData,
        positions: positionsData,
        confidence_threshold: 0.05  // Match MLPredictionCell threshold so Apply All sees same fields as chips
      };

      // console.log('🤖 ML Predictions: Fetching all predictions for sidebar...');
      const response = await api.post('/api/v1/atlas-ml/predict/impute', mlRequest);

      if (response.data.success) {
        // AUDIT FIX: Properly transform API response to combine values with confidence scores
        // API returns: { header: {...values}, positions: [...values], confidence_scores: {...scores} }
        // We need: { header: { field: { value, confidence } }, positions: [{ field: { value, confidence } }] }

        const confidenceScores = response.data.confidence_scores || {};

        // Transform header fields
        const transformedHeader: Record<string, { value: string; confidence: number }> = {};
        const headerData = response.data.header || {};
        Object.entries(headerData).forEach(([field, value]) => {
          // Skip NULL values
          if (value && value !== '[NULL]' && value !== '' && !isJunkPrediction(field, value)) {
            // Confidence key format: "header.{field}"
            const confidenceKey = `header.${field}`;
            const confidence = confidenceScores[confidenceKey] ?? 0.5; // Default to 0.5 if not found
            transformedHeader[field] = {
              value: String(value),
              confidence: typeof confidence === 'number' ? confidence : 0.5
            };
          }
        });

        // Transform position fields
        const rawPositions = response.data.positions || (response.data.position ? [response.data.position] : []);
        const transformedPositions: Array<Record<string, { value: string; confidence: number }>> = [];

        rawPositions.forEach((pos: Record<string, any>, posIndex: number) => {
          const transformedPos: Record<string, { value: string; confidence: number }> = {};
          Object.entries(pos).forEach(([field, value]) => {
            if (value && value !== '[NULL]' && value !== '' && !isJunkPrediction(field, value)) {
              // Confidence key format: "position_{index}.{field}"
              const confidenceKey = `position_${posIndex}.${field}`;
              const confidence = confidenceScores[confidenceKey] ?? 0.5;
              transformedPos[field] = {
                value: String(value),
                confidence: typeof confidence === 'number' ? confidence : 0.5
              };
            }
          });
          transformedPositions.push(transformedPos);
        });

        // Commodity-code suggestions from the tariff classifier, merged into the
        // same prediction shape so they surface under "AI Prefilled" as
        // unapplied previews the user accepts (resolveFieldTab ->
        // hasPendingPrediction). The backend deliberately does NOT write these
        // into wareWarennummerKN8: a commodity code sets the duty rate and
        // export-licensing treatment, and this one is inferred from the goods
        // description rather than read off the page.
        //
        // Where both sources offer a code, the tariff classifier wins. It is
        // the purpose-built model (FAISS retrieval + Mistral judge, with a
        // reviewable reasoning trail); the generic ATLAS imputer treats the
        // commodity code as one categorical field among ~64 and has been
        // observed returning it at under 2% confidence.
        const hsSuggestions = (processing.extractedData as any)?._hs_suggestions;
        if (Array.isArray(hsSuggestions)) {
          hsSuggestions.forEach((sug: any) => {
            const idx = Number(sug?.position_index);
            const code = String(sug?.value ?? '').trim();
            if (!Number.isInteger(idx) || idx < 0 || !code) return;
            while (transformedPositions.length <= idx) transformedPositions.push({});
            transformedPositions[idx].ware_warenum_kn8 = {
              value: code,
              confidence: typeof sug?.confidence === 'number' ? sug.confidence : 0.5,
            };
          });
        }

        const predictions: MLPredictionsResult = {
          header: transformedHeader,
          position: transformedPositions[0] || {},
          positions: transformedPositions
        };

        setMlPredictions(predictions);
        // console.log('🤖 ML Predictions: Loaded', {
        //   headerFields: Object.keys(predictions.header || {}).length,
        //   positionFields: Object.keys(predictions.position || {}).length,
        //   confidenceScoresCount: Object.keys(confidenceScores).length
        // });
      }
    } catch (err: any) {
      console.error('🤖 ML Predictions Error:', err);
      // Mark as failed to prevent repeated retries for this document
      mlFetchFailedRef.current = true;
      // Only show toast ONCE per session - never show again after first time
      if (!mlErrorToastShownRef.current) {
        mlErrorToastShownRef.current = true;
        const errorMessage = err.response?.data?.detail || 'ML prediction service not available';
        toast.warning(errorMessage, { toastId: 'ml-service-error', autoClose: 3000 });
      }
    }
  }, [processing.extractedData]);

  // Fetch ML predictions automatically when extraction completes (or when sidebar opens)
  useEffect(() => {
    // Guard: fetch ML predictions once per document. The ref is reset to false
    // when a new document loads (see setMlPredictions(null) sites), so this does
    // not block re-fetch across documents.
    if (initialMlPredictionsFetchedRef.current) {
      // console.log('[ML useEffect] ⛔ Skipping fetch - initial predictions already fetched');
      return;
    }

    // NUCLEAR OPTION: Check BOTH the flag AND the blackout timer
    // This ensures NO ML fetch happens within 5 seconds of Apply All
    const isBlackoutActive = isMlBlackoutActive();

    if (isApplyingPredictions() || isBlackoutActive || isSendingRef.current) {
      // console.log('[ML useEffect] ⛔ Skipping fetch - blackout active, apply in progress, or send in progress');
      return;
    }

    // Auto-fetch predictions when extracted data is available (for Apply All button)
    if (!mlPredictions && processing.extractedData) {
      fetchMLPredictions();
      // TEMPORARY: Mark initial predictions as fetched to prevent refetching on field changes
      initialMlPredictionsFetchedRef.current = true;
    }
    // NOTE: Removed fetchMLPredictions and isApplyingPredictions from dependencies
    // Using ref instead of state, so no need to track it in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mlPredictions, processing.extractedData]);

  // Auto-fetch history suggestions when extracted data first becomes available
  useEffect(() => {
    if (processing.extractedData && !historySuggestions) {
      api.get('/api/v1/transactions/history-suggestions')
        .then(r => {
          const suggestions = r.data?.suggestions;
          if (suggestions && Object.keys(suggestions).length > 0) {
            setHistorySuggestions(suggestions);
          }
        })
        .catch(() => { }); // silent — field chips just won't appear
    }
    if (!processing.extractedData) {
      setHistorySuggestions(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processing.extractedData]);

  // REMOVED: This useEffect was causing predictions to clear on every field change
  // The old code watched lrn and artderAnmeldung, but when those fields changed
  // (e.g., when applying predictions), it would clear ALL predictions
  // useEffect(() => {
  //   setMlPredictions(null);
  //   setAppliedPredictions(new Set());
  // }, [processing.extractedData?.kopf?.lrn, processing.extractedData?.kopf?.artderAnmeldung]);

  // CRITICAL FIX: Track document identity to clear predictions ONLY when document changes
  // This replaces the removed useEffect above with smarter change detection
  useEffect(() => {
    const currentId = processing.extractedData?.kopf?.lrn ||
      processing.extractedData?.kopf?.mrn ||
      processing.extractedData?.kopf?.bezugsnummer ||
      null;

    // NUCLEAR OPTION: Check blackout period
    const isBlackoutActive = isMlBlackoutActive();

    // Only clear predictions if:
    // 1. We had a previous document (documentIdRef is not null)
    // 2. We have a new document (currentId is not null)
    // 3. The document ID actually changed
    // 4. We're NOT in the middle of applying predictions
    // 5. We're NOT in a blackout period
    if (documentIdRef.current !== null &&
      currentId !== null &&
      documentIdRef.current !== currentId &&
      !isApplyingPredictions() &&
      !isBlackoutActive) {
      // console.log('[ML] Document changed from', documentIdRef.current, 'to', currentId, '- clearing predictions');
      setMlPredictions(null);
      setAiAppliedFields(new Set());
      setDocumentSnapshot(null);
      setAppliedPredictions(new Set());
      // Reset ML fetch failed flag for new document
      mlFetchFailedRef.current = false;
      initialMlPredictionsFetchedRef.current = false;
    } else if (isBlackoutActive) {
      // console.log('[ML] ⛔ Document change detection blocked by blackout');
    }

    // Update the ref to track current document
    documentIdRef.current = currentId;
  }, [processing.extractedData?.kopf?.lrn, processing.extractedData?.kopf?.mrn, processing.extractedData?.kopf?.bezugsnummer]);

  const handleApplyMLPrediction = useCallback((mlFieldName: string, value: string, isPosition?: boolean, positionIndex?: number) => {
    // AUDIT FIX: Convert ML field name to ATLAS path before applying
    // ML format: empfng_name → ATLAS format: kopf.empfanger.name
    const atlasPath = mlToAtlasPath(mlFieldName, isPosition || false);

    console.debug('[ML Apply] Converting ML field to ATLAS path:', {
      mlField: mlFieldName,
      atlasPath,
      isPosition,
      positionIndex,
      value
    });

    // SCROLL FIX: Preserve scroll position before state update to prevent jumping
    const scrollTop = window.scrollY || 0;

    setProcessing(prev => {
      // CRITICAL FIX: Use deep clone to trigger React re-render for nested objects
      // Shallow copy ({ ...prev.extractedData }) doesn't work because nested objects
      // like kopf.empfanger share the same reference, so React doesn't detect changes
      const newData = JSON.parse(JSON.stringify(prev.extractedData || {}));

      if (isPosition && positionIndex !== undefined) {
        // For position fields, construct the full path
        // Position mapping returns field names without "position." prefix
        // e.g., "ware_warenum_kn8" → "wareWarennummerKN8"
        const fullPath = `position[${positionIndex}].${atlasPath}`;
        console.debug('[ML Apply] Setting position field:', fullPath, '=', value);
        setNestedValue(newData, fullPath, value);
      } else {
        // For header fields - atlasPath already includes "kopf." prefix
        // e.g., "empfng_name" → "kopf.empfanger.name"
        console.debug('[ML Apply] Setting header field:', atlasPath, '=', value);
        setNestedValue(newData, atlasPath, value);
      }

      return { ...prev, extractedData: newData };
    });

    // SCROLL FIX: Restore scroll position after state update using requestAnimationFrame
    // to ensure DOM has been updated before restoring scroll
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollTop);
    });

    const appliedPath = (isPosition && positionIndex !== undefined)
      ? `position[${positionIndex}].${atlasPath}`
      : atlasPath;
    setAiAppliedFields(prev => {
      const next = new Set(prev);
      next.add(appliedPath);
      return next;
    });

    // Track applied prediction using ML field name (for sidebar tracking)
    setAppliedPredictions(prev => new Set([...Array.from(prev), mlFieldName]));
  }, []);

  /**
   * Convert ML prediction value to proper codelist code for dropdown fields
   * This ensures that when "Apply All" is clicked, dropdown values are properly selected
   * by matching the prediction to the correct codelist code
   */
  const convertPredictionToCodelistValue = useCallback(async (atlasPath: string, predictionValue: any): Promise<any> => {
    // If value is an object (like empfanger: {name, land}), process each field recursively
    if (typeof predictionValue === 'object' && predictionValue !== null && !Array.isArray(predictionValue)) {
      const result: Record<string, any> = {};
      for (const [key, val] of Object.entries(predictionValue)) {
        const fullPath = `${atlasPath}.${key}`;
        result[key] = await convertPredictionToCodelistValue(fullPath, val);
      }
      return result;
    }

    // Only process string values
    if (typeof predictionValue !== 'string' || !predictionValue) {
      return predictionValue;
    }

    try {
      // Get the codelist ID for this field
      const codelistId = codelistService.getCodelistIdForField(atlasPath);

      if (!codelistId) {
        // No codelist for this field, return raw value
        return predictionValue;
      }

      // Get the codelist
      const codelist = await codelistService.getCodelist(codelistId);

      if (!codelist || !codelist.values || codelist.values.length === 0) {
        // console.log(`[ML Apply All] No codelist values found for ${atlasPath} (${codelistId})`);
        return predictionValue;
      }

      // Try to find exact code match first (case-insensitive)
      const valueLower = predictionValue.toLowerCase().trim();
      const exactCodeMatch = codelist.values.find(v => v.code.toLowerCase() === valueLower);
      if (exactCodeMatch) {
        // console.log(`[ML Apply All] ✅ Exact code match for ${atlasPath}: "${predictionValue}" → "${exactCodeMatch.code}"`);
        return exactCodeMatch.code;
      }

      // Try to match by description
      const descriptionMatch = codelist.values.find(v =>
        v.description.toLowerCase().includes(valueLower) ||
        valueLower.includes(v.description.toLowerCase())
      );
      if (descriptionMatch) {
        // console.log(`[ML Apply All] ✅ Description match for ${atlasPath}: "${predictionValue}" → "${descriptionMatch.code}"`);
        return descriptionMatch.code;
      }

      // Try partial code match (e.g., "0" matches "0", "00000100" etc.)
      const partialCodeMatch = codelist.values.find(v =>
        v.code.startsWith(predictionValue) || predictionValue.startsWith(v.code)
      );
      if (partialCodeMatch) {
        // console.log(`[ML Apply All] ✅ Partial code match for ${atlasPath}: "${predictionValue}" → "${partialCodeMatch.code}"`);
        return partialCodeMatch.code;
      }

      // Use the convertValueToCode method from codelistService
      const convertedValue = codelistService.convertValueToCode(predictionValue, codelistId);
      if (convertedValue !== predictionValue) {
        // Verify the converted value exists in codelist
        const verifyMatch = codelist.values.find(v => v.code === convertedValue);
        if (verifyMatch) {
          // console.log(`[ML Apply All] ✅ Converted value for ${atlasPath}: "${predictionValue}" → "${convertedValue}"`);
          return convertedValue;
        }
      }

      // If no match found, log and return original value
      // console.log(`[ML Apply All] ⚠️ No codelist match for ${atlasPath}: "${predictionValue}" (codelist: ${codelistId})`);
      return predictionValue;

    } catch (error) {
      console.error(`[ML Apply All] Error converting value for ${atlasPath}:`, error);
      return predictionValue;
    }
  }, []);

  const handleApplyAllMLPredictions = useCallback(async (confidence: ConfidenceFilter) => {
    // console.log('[ML Apply All] 🎯 Button clicked, confidence filter:', confidence);

    if (!mlPredictions) {
      // console.log('[ML Apply All] ❌ No ML predictions available - button should not be visible');
      return;
    }

    // console.log('[ML Apply All] 📊 ML Predictions structure:', {
    //   hasHeader: !!mlPredictions.header,
    //   headerFieldCount: Object.keys(mlPredictions.header || {}).length,
    //   hasPosition: !!mlPredictions.position,
    //   positionFieldCount: Object.keys(mlPredictions.position || {}).length,
    //   hasPositions: !!mlPredictions.positions,
    //   positionsCount: mlPredictions.positions?.length || 0,
    //   selectedPositionIndex
    // });

    // Log first 5 header predictions to verify structure
    if (mlPredictions.header) {
      const headerEntries = Object.entries(mlPredictions.header).slice(0, 5);
      // console.log('[ML Apply All] 📋 Sample header predictions:', headerEntries.map(([field, pred]) => ({
      //   mlField: field,
      //   atlasPath: mlToAtlasPath(field, false),
      //   value: pred.value,
      //   confidence: pred.confidence
      // })));
    }

    // SCROLL FIX: Preserve scroll position before state update to prevent jumping
    const scrollTop = window.scrollY || 0;

    // ============================================================================
    // NUCLEAR OPTION: 5-second complete ML blackout to prevent all ML activity
    // ============================================================================
    const BLACKOUT_DURATION_MS = 5000; // 5 seconds

    // Set ALL blocking mechanisms IMMEDIATELY
    setApplyingPredictions(true);
    startMlBlackout(BLACKOUT_DURATION_MS);

    // console.log('[ML Apply All] 🔒 BLACKOUT STARTED - 5 seconds');

    try {

    // ============================================================================
    // STEP 0: Pre-convert all prediction values to codelist codes (async)
    // This ensures dropdown fields get the correct codes, not descriptions
    // ============================================================================
    // console.log('[ML Apply All] 🔄 Pre-converting prediction values to codelist codes...');

    interface ConvertedPrediction {
      mlField: string;
      atlasPath: string;
      originalValue: any;
      convertedValue: any;
      confidence: number;
      isPosition: boolean;
    }

    const convertedPredictions: ConvertedPrediction[] = [];

    // Convert header predictions
    if (mlPredictions.header) {
      for (const [field, prediction] of Object.entries(mlPredictions.header)) {
        const passesFilter = confidence === 'all' ||
          (confidence === 'high' && prediction.confidence >= HIGH_CONFIDENCE) ||
          (confidence === 'medium' && prediction.confidence >= MEDIUM_CONFIDENCE && prediction.confidence < HIGH_CONFIDENCE) ||
          (confidence === 'low' && prediction.confidence < MEDIUM_CONFIDENCE);

        // Skip raw ML float artifacts ("0", "0.0") for Niederlassungsnummer.
        // "0000" is the valid ATLAS main-branch code and must be allowed through.
        const isZeroNiederlassung = field.toLowerCase().includes('niederlassung') &&
          ['0', '0.0'].includes(String(prediction.value).trim());

        if (passesFilter && prediction.value && prediction.value !== '[NULL]' && !isZeroNiederlassung) {
          let atlasPath = mlToAtlasPath(field, false);
          if (!atlasPath.startsWith('kopf.') && !atlasPath.startsWith('position')) {
            atlasPath = `kopf.${atlasPath}`;
          }

          const convertedValue = await convertPredictionToCodelistValue(atlasPath, prediction.value);
          convertedPredictions.push({
            mlField: field,
            atlasPath,
            originalValue: prediction.value,
            convertedValue,
            confidence: prediction.confidence,
            isPosition: false,
          });
        }
      }
    }

    // Convert position predictions
    const positionPredictions = mlPredictions.positions?.[selectedPositionIndex] || mlPredictions.position;
    if (positionPredictions) {
      for (const [field, prediction] of Object.entries(positionPredictions)) {
        const passesFilter = confidence === 'all' ||
          (confidence === 'high' && prediction.confidence >= HIGH_CONFIDENCE) ||
          (confidence === 'medium' && prediction.confidence >= MEDIUM_CONFIDENCE && prediction.confidence < HIGH_CONFIDENCE) ||
          (confidence === 'low' && prediction.confidence < MEDIUM_CONFIDENCE);

        if (passesFilter && prediction.value && prediction.value !== '[NULL]') {
          const atlasPath = mlToAtlasPath(field, true);
          const fullPath = `position[${selectedPositionIndex}].${atlasPath}`;

          const convertedValue = await convertPredictionToCodelistValue(fullPath, prediction.value);
          convertedPredictions.push({
            mlField: field,
            atlasPath: fullPath,
            originalValue: prediction.value,
            convertedValue,
            confidence: prediction.confidence,
            isPosition: true,
          });
        }
      }
    }

    // console.log('[ML Apply All] ✅ Pre-converted', convertedPredictions.length, 'predictions');

    // Track which fields we're applying for the applied set
    const fieldsToApply: string[] = [];
    const appliedPaths: string[] = [];

    // CRITICAL FIX: BATCH all changes into a SINGLE setProcessing() call
    // This prevents multiple state updates that each trigger useEffects
    setProcessing(prev => {
      // ========================================================================
      // STEP 1: Capture ALL existing non-empty values BEFORE deep clone
      // This protects against accidental value clearing during state updates
      // ========================================================================
      const existingValues = new Map<string, any>();
      const captureValues = (obj: any, prefix = '') => {
        if (!obj || typeof obj !== 'object') return;
        Object.entries(obj).forEach(([key, value]) => {
          const path = prefix ? `${prefix}.${key}` : key;
          if (Array.isArray(value)) {
            value.forEach((item, index) => {
              if (typeof item === 'object' && item !== null) {
                captureValues(item, `${path}[${index}]`);
              } else if (item !== null && item !== undefined && item !== '') {
                existingValues.set(`${path}[${index}]`, item);
              }
            });
          } else if (typeof value === 'object' && value !== null) {
            captureValues(value, path);
          } else if (value !== null && value !== undefined && value !== '') {
            existingValues.set(path, value);
          }
        });
      };
      captureValues(prev.extractedData);
      // console.log('[ML Apply All] Captured', existingValues.size, 'existing values');

      // Log specific values we want to protect
      const protectedFields = ['kopf.artderAusfuhranmeldung', 'kopf.beteiligtenKonstellation', 'kopf.sicherheit'];
      protectedFields.forEach(field => {
        const value = existingValues.get(field);
        if (value !== undefined) {
          // console.log(`[ML Apply All] Protected field: ${field} = "${value}"`);
        }
      });

      // ========================================================================
      // STEP 2: Deep clone and apply pre-converted predictions
      // ========================================================================
      const newData = JSON.parse(JSON.stringify(prev.extractedData || {}));

      // Apply all pre-converted predictions
      for (const pred of convertedPredictions) {
        const { mlField, atlasPath, originalValue, convertedValue, isPosition } = pred;

        // Handle object values - they need to be flattened to individual fields
        if (typeof convertedValue === 'object' && convertedValue !== null && !Array.isArray(convertedValue)) {
          // Object value like empfanger: {name: '...', land: '...'}
          // Flatten to individual paths: kopf.empfanger.name, kopf.empfanger.land
          const flattenObject = (obj: Record<string, any>, basePath: string, baseField: string) => {
            Object.entries(obj).forEach(([key, val]) => {
              const fullPath = `${basePath}.${key}`;
              if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
                flattenObject(val, fullPath, `${baseField}.${key}`);
              } else if (val !== null && val !== undefined && val !== '' && val !== '[NULL]') {
                const currentValue = getNestedValue(newData, fullPath);
                if (!currentValue || currentValue === '' || currentValue === '[NULL]') {
                  // console.log('[ML Apply All] ✅ Applying field (flattened):', { mlField: baseField, atlasPath: fullPath, value: val, previousValue: currentValue || '(empty)' });
                  setNestedValue(newData, fullPath, val);
                  appliedPaths.push(fullPath);
                  fieldsToApply.push(`${baseField}.${key}`);
                } else {
                  // console.log('[ML Apply All] ⏭️ SKIPPING field (already has value):', { mlField: `${baseField}.${key}`, atlasPath: fullPath, existingValue: currentValue });
                }
              }
            });
          };
          flattenObject(convertedValue, atlasPath, mlField);
        } else {
          // Simple scalar value
          const currentValue = getNestedValue(newData, atlasPath);
          if (!currentValue || currentValue === '' || currentValue === '[NULL]') {
            const valueToApply = convertedValue;
            // console.log('[ML Apply All] ✅ Applying field:', {
            //   mlField,
            //   atlasPath,
            //   originalValue,
            //   convertedValue: valueToApply,
            //   wasConverted: originalValue !== valueToApply,
            //   previousValue: currentValue || '(empty)'
            // });
            setNestedValue(newData, atlasPath, valueToApply);
            appliedPaths.push(atlasPath);
            // Verify the value was actually set
            const verifyValue = getNestedValue(newData, atlasPath);
            // console.log('[ML Apply All] ✔️ Verified value set:', { atlasPath, setValue: valueToApply, actualValue: verifyValue });
            fieldsToApply.push(mlField);
          } else {
            // console.log('[ML Apply All] ⏭️ SKIPPING field (already has value):', { mlField, atlasPath, existingValue: currentValue });
          }
        }
      }

      // ========================================================================
      // STEP 3: Restore any accidentally cleared values
      // This is a safety net - if any value was cleared during the process,
      // we restore it from our captured snapshot
      // ========================================================================
      let restoredCount = 0;
      existingValues.forEach((originalValue, path) => {
        const currentValue = getNestedValue(newData, path);
        if ((currentValue === null || currentValue === undefined || currentValue === '') &&
          originalValue !== null && originalValue !== undefined && originalValue !== '') {
          // console.log('[ML Apply All] ⚠️ RESTORING accidentally cleared value:', path, '=', originalValue);
          setNestedValue(newData, path, originalValue);
          restoredCount++;
        }
      });
      if (restoredCount > 0) {
        // console.log('[ML Apply All] Restored', restoredCount, 'values that were accidentally cleared');
      }

      // console.log('[ML Apply All] Batch update complete:', {
      //   fieldsApplied: fieldsToApply.length,
      //   valuesPreserved: existingValues.size,
      //   valuesRestored: restoredCount
      // });

      // DEBUG: Log sample values from final newData to verify state
      // console.log('[ML Apply All] 📦 Final newData sample:', {
      //   'kopf.empfanger.name': newData?.kopf?.empfanger?.name || '(not set)',
      //   'kopf.empfanger.land': newData?.kopf?.empfanger?.land || '(not set)',
      //   'kopf.ausfuhrer.tin': newData?.kopf?.ausfuhrer?.tin || '(not set)',
      //   'kopf.bestimmungsLand': newData?.kopf?.bestimmungsLand || '(not set)',
      //   'kopf.rechnungswahrung': newData?.kopf?.rechnungswahrung || '(not set)',
      // });

      return { ...prev, extractedData: newData };
    });

    // Update applied predictions set
    setAppliedPredictions(prev => {
      const newSet = new Set(prev);
      fieldsToApply.forEach(field => newSet.add(field));
      return newSet;
    });

    if (appliedPaths.length > 0) {
      setAiAppliedFields(prev => {
        const next = new Set(prev);
        appliedPaths.forEach(p => next.add(p));
        return next;
      });
    }

    // SCROLL FIX: Restore scroll position after state update using requestAnimationFrame
    // to ensure DOM has been updated before restoring scroll
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollTop);
    });

    // The 5s blackout timestamp self-expires and keeps ML fetch suppressed; we
    // only need to clear the applying flag, which the finally guarantees even on throw.
    toast.success(`Applied ${confidence === 'all' ? 'all' : confidence + ' confidence'} ML predictions (${fieldsToApply.length} fields)`);

    // DEBUG: Log applied fields for verification
    // console.log('[ML Apply All] 📝 Fields that were applied:', fieldsToApply);
    } finally {
      setApplyingPredictions(false);
    }
  }, [mlPredictions, selectedPositionIndex, convertPredictionToCodelistValue]);

  // Load from History: fetch most frequent field values from past submissions and fill empty fields
  const handleLoadFromHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const response = await api.get('/api/v1/transactions/history-suggestions');
      const { suggestions, transaction_count, field_count } = response.data;

      if (!suggestions || field_count === 0) {
        toast.info('No previous submission history found.');
        setShowHistoryConfirmDialog(false);
        return;
      }

      // Persist suggestions so per-field chips stay available
      setHistorySuggestions(suggestions);

      const scrollTop = window.scrollY || 0;
      let filledCount = 0;

      setProcessing(prev => {
        const newData = JSON.parse(JSON.stringify(prev.extractedData || {}));

        // Pre-process: group simple scalar array-indexed paths (e.g. ausgewahlteLander[0],
        // ausgewahlteLander[1]) into their base path as a comma-joined string.
        // This matches how the form stores simple list fields (e.g. transit countries).
        // Paths with a dot AFTER the index (e.g. bewilligung[0].art) are object-array
        // fields and must be kept as-is.
        const scalarArrayGroups: Record<string, string[]> = {};
        const processedSuggestions: Record<string, string> = {};

        for (const [path, value] of Object.entries(suggestions) as [string, string][]) {
          // Match paths that END with an array index: somePath[N] (no further dot)
          const scalarMatch = path.match(/^(.+)\[(\d+)\]$/);
          if (scalarMatch) {
            const basePath = scalarMatch[1];
            const idx = parseInt(scalarMatch[2], 10);
            if (!scalarArrayGroups[basePath]) scalarArrayGroups[basePath] = [];
            scalarArrayGroups[basePath][idx] = value;
          } else {
            processedSuggestions[path] = value;
          }
        }

        // Merge grouped scalar arrays as comma-joined strings
        for (const [basePath, values] of Object.entries(scalarArrayGroups)) {
          processedSuggestions[basePath] = values.filter(Boolean).join(',');
        }

        for (const [path, value] of Object.entries(processedSuggestions)) {
          const existing = getNestedValue(newData, path);
          if (!existing || existing === '' || existing === '[NULL]') {
            setNestedValue(newData, path, value);
            filledCount++;
          }
        }

        return { ...prev, extractedData: newData };
      });

      requestAnimationFrame(() => window.scrollTo(0, scrollTop));

      setTimeout(() => {
        toast.success(
          `Filled ${filledCount} field${filledCount !== 1 ? 's' : ''} from your last ${transaction_count} submission${transaction_count !== 1 ? 's' : ''}.`
        );
      }, 100);
    } catch (error) {
      toast.error('Failed to load history data. Please try again.');
    } finally {
      setIsLoadingHistory(false);
      setShowHistoryConfirmDialog(false);
    }
  }, []);

  const getClassifierContext = (fieldPath: string): {
    productDescription?: string;
    materials?: string;
    originCountry?: string;
    intendedUse?: string;
  } => {
    // Extract position index from field path: "position[0].wareWarennummerKN8" -> 0
    const match = fieldPath.match(/position\[(\d+)\]/);
    if (!match) {
      console.warn('[Classifier Context] No position index found in field path');
      return {};
    }

    const posIndex = parseInt(match[1]);
    const position = processing.extractedData?.position?.[posIndex];

    if (!position) {
      console.warn(`[Classifier Context] Position ${posIndex} not found in extracted data`);
      return {};
    }

    // Extract comprehensive context from ATLAS position data
    const context = {
      productDescription: position.wareWarenbezeichnung || position.description || '',
      materials: position.materials || '',
      originCountry: position.ursprungsland || position.origin || '',
      intendedUse: position.intended_use || position.verwendungszweck || '',
    };

    // Warn if key fields are missing
    if (!context.productDescription) {
      console.warn('[Classifier Context] ⚠️ Missing product description!');
    }
    if (!context.materials && !context.productDescription.match(/(plastic|steel|aluminum|cotton|wood)/i)) {
      console.warn('[Classifier Context] ⚠️ No materials specified and none detected in description');
    }

    return context;
  };

  const getAuthStatusColor = (status?: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'authenticated': return 'success';
      case 'expired': return 'warning';
      default: return 'error';
    }
  };

  const getAuthStatusText = (status?: string, username?: string) => {
    switch (status) {
      case 'authenticated': {
        const label = '✓ ' + tString('documentProcessing.module.authenticated');
        return username ? `${label} (${username})` : label;
      }
      case 'expired': return '⚠ ' + tString('documentProcessing.module.tokenExpired');
      default: return '🔒 ' + tString('documentProcessing.module.notAuthenticated');
    }
  };

  const getAuthStatusIcon = (status?: string) => {
    switch (status) {
      case 'authenticated': return <SuccessIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      case 'expired': return <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      default: return <Security sx={{ fontSize: 16, mr: 0.5 }} />;
    }
  };

  return (
    <>
      <style>{spinKeyframes}</style>
      {/* AppShell provides the outer 100vh box and AppBar — this fills the Outlet */}
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff', overflow: 'hidden' }}>


        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* MAIN CONTENT AREA — left nav and module drawer live in AppShell */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* ═══ PRIMARY CONTENT AREA ═══ */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 1, sm: 1.5, md: 2 } }}>

          {/* ── Export Status link ── */}
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end', pr: 2 }}>
            <Typography
              component="span"
              variant="body2"
              onClick={() => navigate('/process/export-status')}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Export Status
            </Typography>
          </Box>

          {/* ── StepBar ── */}
          <StepBar
            step={activeWorkflowStep}
            labels={{
              upload: tString('workflow.uploadDocument') || 'Upload Document',
              analyse: tString('workflow.analyse') || 'Analyse',
              review: tString('workflow.review') || 'Review',
            }}
            isStepAccessible={(target) => target === 'upload' || !!processing.extractedData}
            onStepClick={(target) => setActiveWorkflowStep(target)}
          />

          {/* ── UPLOAD STEP ── */}
          {activeWorkflowStep === 'upload' && (
            <>
          {/* Prompt to select module if none selected - only show after modules have loaded */}
          {modulesLoaded && !processing.selectedModule && (
            <Paper elevation={1} sx={{ p: 3, mb: 2, borderRadius: 2, textAlign: 'center', border: '2px dashed', borderColor: 'divider' }}>
              <AppsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                Select a Module to Begin
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                Open the module selector to choose a processing module before uploading documents.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AppsIcon />}
                onClick={() => setModuleDrawerOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Open Module Selector
              </Button>
            </Paper>
          )}

          {/* Compliance Polling Status */}
          {isPolling && (
            <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 1.5 }}>
              <Typography variant="body2">
                Running compliance check in background... (Status: {pollingStatus})
              </Typography>
            </Alert>
          )}

          {/* Compliance Report Cards */}
          {allComplianceReports.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              {allComplianceReports.map((item, index) => (
                <Box key={item.fileId} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                    Compliance Report for: {item.fileName}
                  </Typography>
                  <ComplianceReportCard
                    report={item.report}
                    onViewDetails={() => {
                      setActiveComplianceReport(item.report);
                      setShowComplianceReport(true);
                    }}
                    defaultExpanded={index === allComplianceReports.length - 1}
                  />
                </Box>
              ))}
            </Box>
          )}

          {pollingError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              Failed to get compliance results: {pollingError.message}
            </Alert>
          )}

          {/* ═══ Upload Documents Card ═══ */}
          <Paper elevation={1} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', backgroundColor: '#fbfcf9', minHeight: 'calc(100vh - 220px)' }}>
            <Box sx={{ p: 2 }}>
                  {/* Drag-drop zone */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box
                      {...getRootProps()}
                      sx={{
                        backgroundColor: brand.dropZoneBg,
                        border: `2px dashed ${isDragActive ? brand.orange : brand.borderSubtle}`,
                        borderRadius: 1,
                        py: 6,
                        px: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        width: '100%',
                        maxWidth: 800,
                        '&:hover': { borderColor: brand.orange },
                      }}
                    >
                      <input {...getInputProps()} />
                      <NavIcon src={stepperIcons.dragDrop} size={48} sx={{ mx: 'auto', mb: 1 }} />
                      <Typography variant="body1" gutterBottom sx={{
                        color: brand.textPrimary,
                        fontWeight: 500
                      }}>
                        {isDragActive ? t('documentProcessing.upload.dropHere') : t('documentProcessing.upload.dragDrop')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: brand.textSecondary }}>
                        {t('documentProcessing.upload.clickToBrowse')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* ── Uploaded files: horizontal slider ── */}
                  {uploadedFiles.length > 0 && (
                    <Box sx={{ maxWidth: 800, mx: 'auto', mb: 1 }}>
                      {/* Slider row */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 1.5,
                          overflowX: 'auto',
                          pb: 1,
                          '&::-webkit-scrollbar': { height: 4 },
                          '&::-webkit-scrollbar-track': { bgcolor: 'grey.100', borderRadius: 2 },
                          '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 2 },
                        }}
                      >
                        {uploadedFiles.map((file) => {
                          const isSelected = previewFileId === file.id;
                          return (
                            <Box
                              key={file.id}
                              onClick={() => setPreviewFileId(isSelected ? null : file.id)}
                              sx={{
                                position: 'relative',
                                flexShrink: 0,
                                width: 90,
                                cursor: 'pointer',
                                borderRadius: 1,
                                border: `1px solid ${isSelected ? brand.orange : brand.borderSubtle}`,
                                backgroundColor: '#fff',
                                overflow: 'hidden',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: brand.orange,
                                },
                              }}
                            >
                              {/* Thumbnail area */}
                              <Box
                                sx={{
                                  height: 120,
                                  bgcolor: brand.dropZoneBg,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  overflow: 'hidden',
                                  position: 'relative',
                                }}
                              >
                                {file.type.startsWith('image/') ? (
                                  <Box
                                    component="img"
                                    src={file.url}
                                    alt={file.name}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : file.type === 'application/pdf' ? (
                                  <iframe
                                    src={`${file.url}#toolbar=0&navpanes=0&scrollbar=0&page=1&zoom=50`}
                                    style={{ position: 'absolute', width: '200%', height: '200%', border: 'none', pointerEvents: 'none', top: '-50%', left: '-50%', transform: 'scale(0.5)', transformOrigin: 'center center' }}
                                    title={`Thumb - ${file.name}`}
                                  />
                                ) : (
                                  <Typography sx={{ fontSize: '2rem', userSelect: 'none' }}>📎</Typography>
                                )}
                              </Box>

                              {/* Remove button — top right */}
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFile(file.id);
                                  if (previewFileId === file.id) setPreviewFileId(null);
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: 2,
                                  right: 2,
                                  width: 20,
                                  height: 20,
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  p: 0,
                                  '&:hover': { backgroundColor: '#fff' },
                                }}
                              >
                                <NavIcon src={uiIcons.cross} size={14} />
                              </IconButton>

                              {/* Expand / preview indicator — bottom center */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 4,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(255,255,255,0.9)',
                                  border: '1px solid',
                                  borderColor: isSelected ? theme.palette.text.primary : 'grey.400',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {isSelected
                                  ? <RemoveIcon sx={{ fontSize: 12 }} />
                                  : <AddIcon sx={{ fontSize: 12 }} />
                                }
                              </Box>

                              {/* Filename below thumbnail */}
                              <Box sx={{ px: 0.75, py: 0.5, bgcolor: 'background.paper' }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.65rem',
                                    color: 'text.secondary',
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {file.name}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>

                      {/* Orange progress underline + count summary */}
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ height: 3, backgroundColor: brand.orange, width: '100%', borderRadius: 999 }} />
                        <Box sx={{ textAlign: 'center', fontSize: 13, color: brand.textSecondary, mt: 0.5 }}>
                          {uploadedFiles.length} document{uploadedFiles.length !== 1 ? 's' : ''} uploaded
                        </Box>
                      </Box>

                      {/* ── Inline preview panel (opens below slider on click) ── */}
                      {previewFileId && (() => {
                        const previewFile = uploadedFiles.find(f => f.id === previewFileId);
                        if (!previewFile) return null;
                        return (
                          <Box
                            sx={{
                              mt: 1.5,
                              border: '1px solid',
                              borderColor: theme.palette.text.primary,
                              borderRadius: 2,
                              overflow: 'hidden',
                              bgcolor: 'background.paper',
                            }}
                          >
                            {/* Preview header */}
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              px: 2,
                              py: 1,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              bgcolor: theme.palette.grey[50],
                            }}>
                              <Typography variant="body2" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                {previewFile.name}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, ml: 1, flexShrink: 0 }}>
                                {(previewFile.type === 'application/pdf' || previewFile.type.startsWith('image/')) && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => window.open(previewFile.url, '_blank')}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem', height: 28, px: 1.5 }}
                                  >
                                    Open
                                  </Button>
                                )}
                                <IconButton size="small" onClick={() => setPreviewFileId(null)} sx={{ width: 28, height: 28 }}>
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </Box>

                            {/* Preview content */}
                            {previewFile.type.startsWith('image/') ? (
                              <Box
                                component="img"
                                src={previewFile.url}
                                alt={previewFile.name}
                                sx={{ width: '100%', maxHeight: 400, objectFit: 'contain', bgcolor: theme.palette.grey[50], display: 'block' }}
                              />
                            ) : previewFile.type === 'application/pdf' ? (
                              <iframe
                                src={`${previewFile.url}#toolbar=0&navpanes=0&scrollbar=1`}
                                style={{ width: '100%', height: 420, border: 'none', display: 'block' }}
                                title={`Preview - ${previewFile.name}`}
                              />
                            ) : (
                              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>📎</Typography>
                                <Typography variant="body2">{previewFile.type || 'Unknown file type'}</Typography>
                                <Typography variant="caption">{(previewFile.size / 1024 / 1024).toFixed(2)} MB — ready for processing</Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  )}

                  {/* Compliance Check Toggle */}
                  <Paper sx={{
                    p: 2,
                    mb: 2,
                    mt: 2,
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}12 0%, ${theme.palette.primary.main}12 100%)`,
                    border: '1px solid',
                    borderColor: theme.palette.primary.light,
                    display: 'none'
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={processing.runComplianceCheck || false}
                          onChange={(e) => setProcessing(prev => ({
                            ...prev,
                            runComplianceCheck: e.target.checked
                          }))}
                          disabled={uploadedFiles.length === 0}
                          sx={{
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ShieldIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Run Compliance Check
                          </Typography>
                          <Chip
                            label={tString('documentProcessing.fieldStatus.aiPowered')}
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.primary.main,
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                        </Box>
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                      Automatically check entities against sanctions lists and export controls
                    </Typography>
                  </Paper>

                  {/* Compliance Report Button */}
                  {processing.complianceReport && (
                    <Button
                      variant="outlined"
                      startIcon={<ComplianceIcon />}
                      onClick={() => setShowComplianceReport(true)}
                      size="medium"
                      sx={{
                        mb: 2,
                        borderColor: processing.complianceReport.overall_status === 'compliant' ? theme.palette.success.main : theme.palette.warning.main,
                        color: processing.complianceReport.overall_status === 'compliant' ? theme.palette.success.main : theme.palette.warning.main,
                        '&:hover': {
                          borderColor: processing.complianceReport.overall_status === 'compliant' ? theme.palette.success.dark : theme.palette.warning.dark,
                          backgroundColor: processing.complianceReport.overall_status === 'compliant' ? `${theme.palette.success.main}12` : `${theme.palette.warning.main}12`,
                        }
                      }}
                    >
                      View Compliance Report ({processing.complianceReport.overall_status})
                    </Button>
                  )}

                  {/* Correlation Validation Section */}
                  {uploadedFiles.length === 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <Alert severity="info" sx={themedInfoAlertSx}>
                        Single document uploaded. Upload 2 or more documents to validate correlation for ATLAS submission.
                      </Alert>
                    </Box>
                  )}

                  {uploadedFiles.length >= 2 && actuallyUploadedFileIds.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <Alert severity="info" sx={themedInfoAlertSx}>
                        {uploadedFiles.length} documents ready. Click "Start Processing" to extract data and validate correlation.
                      </Alert>
                    </Box>
                  )}

                  {actuallyUploadedFileIds.length >= 2 && documentSetId && !processing.isProcessing && (
                    <>
                      <CorrelationValidator
                        documentSetId={documentSetId}
                        documentCount={actuallyUploadedFileIds.length}
                        declarationType="export"
                        onValidationComplete={handleCorrelationValidationComplete}
                        onValidationError={handleCorrelationValidationError}
                        autoValidate={true}
                      />

                      {/* Expandable Correlation Details */}
                      {correlationResult && (
                        <Box sx={{ mb: 2 }}>
                          <Button
                            variant="text"
                            onClick={() => setShowCorrelationDetails(!showCorrelationDetails)}
                            startIcon={showCorrelationDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{ mb: 1 }}
                          >
                            {showCorrelationDetails ? 'Hide' : 'Show'} Detailed Correlation Analysis
                          </Button>

                          {showCorrelationDetails && (
                            <>
                              <CorrelationFeedbackPanel validationResult={correlationResult} />
                              <ATLASValidationPanel validationResult={correlationResult} />
                            </>
                          )}
                        </Box>
                      )}

                      {/* Correlation Status Alert */}
                      {correlationResult && !isCorrelationValid && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          Documents are not properly correlated. Please review the issues above before submitting to ATLAS.
                          {correlationResult.atlas_validation.recommendations.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold">Quick Recommendations:</Typography>
                              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                {correlationResult.atlas_validation.recommendations.slice(0, 3).map((rec, index) => (
                                  <li key={index}>
                                    <Typography variant="body2">{rec}</Typography>
                                  </li>
                                ))}
                              </ul>
                            </Box>
                          )}
                        </Alert>
                      )}
                    </>
                  )}

                  {/* Start Processing Button */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 2 }}>
                    {processing.isProcessing ? (() => {
                      // The button becomes the progress surface: identical
                      // footprint (maxWidth 800 / py 1.5 / borderRadius 1) so
                      // nothing below it shifts when processing starts.
                      // Restart the band clock whenever the step label changes.
                      const now = procElapsed || Date.now();
                      if (procBandStepRef.current !== processing.currentStep) {
                        procBandStepRef.current = processing.currentStep;
                        procBandStartRef.current = now;
                      }
                      const rawPct = deriveProcessingProgress(
                        processing.currentStep,
                        now - procBandStartRef.current,
                        Math.max(1, uploadedFiles.length),
                      );
                      // Clamp monotonically: easing inside a band can already be
                      // ahead of the next band's floor, and the bar must not
                      // visibly retreat when the step label changes.
                      let pct: number | null = rawPct;
                      if (rawPct !== null) {
                        pct = Math.max(rawPct, procPeakRef.current);
                        procPeakRef.current = pct;
                      }
                      const determinate = pct !== null;
                      const label = processing.currentStep || tString('documentProcessing.processing.inProgress');
                      return (
                        <Box
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={determinate ? Math.round(pct as number) : undefined}
                          aria-valuetext={label}
                          aria-busy
                          sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            width: '100%',
                            maxWidth: 800,
                            py: 1.5,
                            borderRadius: 1,
                            backgroundColor: brand.black,
                            userSelect: 'none',
                          }}
                        >
                          {/* Completed region — a lift in the dark surface, not a
                              bright fill: white text over brand.yellow would be
                              unreadable. */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              bottom: 0,
                              width: determinate ? `${pct}%` : '100%',
                              backgroundColor: determinate ? 'rgba(255,255,255,0.09)' : 'transparent',
                              transition: 'width 260ms linear',
                            }}
                          />
                          {/* Indeterminate sweep — shown only when the stage is
                              unknown, so it never pretends to be a position. */}
                          {!determinate && (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.10) 50%, transparent 100%)`,
                                animation: 'procSweep 1.6s ease-in-out infinite',
                              }}
                            />
                          )}
                          {/* Brand accent tracking the edge of progress. */}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              height: 3,
                              width: determinate ? `${pct}%` : '100%',
                              background: `linear-gradient(90deg, ${brand.orange} 0%, ${brand.yellow} 100%)`,
                              transition: 'width 260ms linear',
                              animation: determinate ? 'none' : 'procGlow 1.6s ease-in-out infinite',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1.25,
                              px: 2,
                              minWidth: 0,
                            }}
                          >
                            <CircularProgress size={16} color="inherit" thickness={5} sx={{ color: '#fff', flexShrink: 0 }} />
                            <Box
                              component="span"
                              sx={{
                                color: '#fff',
                                fontSize: 16,
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {label}
                            </Box>
                            {determinate && (
                              <Box
                                component="span"
                                sx={{
                                  color: 'rgba(255,255,255,0.6)',
                                  fontSize: 13,
                                  fontWeight: 600,
                                  fontVariantNumeric: 'tabular-nums',
                                  flexShrink: 0,
                                }}
                              >
                                {Math.round(pct as number)}%
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })() : (
                      <Button
                        ref={startProcessingBtnRef}
                        variant="contained"
                        fullWidth
                        startIcon={<ProcessIcon />}
                        onClick={handleStartProcessing}
                        disabled={
                          !processing.selectedModule ||
                          uploadedFiles.length === 0 ||
                          (actuallyUploadedFileIds.length >= 2 && !isCorrelationValid)
                        }
                        size="large"
                        sx={{
                          backgroundColor: brand.black,
                          color: '#fff',
                          width: '100%',
                          maxWidth: 800,
                          py: 1.5,
                          fontSize: 16,
                          fontWeight: 600,
                          borderRadius: 1,
                          '&:hover': { backgroundColor: '#000' },
                          '&.Mui-disabled': { backgroundColor: '#3A3A3A', color: '#888' },
                        }}
                      >
                        {t('documentProcessing.processing.start')}
                      </Button>
                    )}
                    {processing.isProcessing && abortController && (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={handleCancelProcessing}
                        sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                      >
                        Cancel
                      </Button>
                    )}
                    {/* Helper hint — sits inside the upload card,
                        left-aligned within the same 800px column as
                        the Start Processing button above it. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: brand.textSecondary, fontSize: 13, alignSelf: 'flex-start' }}>
                      <NavIcon src={stepperIcons.uploadExclamation} size={16} />
                      <Box component="span">{tString('documentProcessing.upload.minDocumentsHint') || 'Upload 2 or more documents to validate correlation for ATLAS submission.'}</Box>
                    </Box>
                  </Box>
                </Box>
            </Paper>
            </>
          )}

          {/* ── ANALYSE STEP ── */}
          {activeWorkflowStep === 'analyse' && processing.extractedData && (
            <>
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  {/* Color Legend - Only show for ATLAS module */}
                  {processing.selectedModule?.name?.toLowerCase().includes('atlas') && (
                    <>
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        {t('documentProcessing.atlasStatus.title')}
                      </Typography>
                      <Chip
                        size="small"
                        label={tString('documentProcessing.atlasStatus.complete', fieldStatusCounts.complete)}
                        sx={{
                          bgcolor: `${theme.palette.success.main}18`,
                          color: theme.palette.success.main,
                          fontWeight: 600,
                          border: `1px solid ${theme.palette.success.main}40`,
                          height: 20,
                          fontSize: '0.7rem',
                          '& .MuiChip-icon': { fontSize: 14 },
                          '& .MuiChip-label': { px: 0.75 }
                        }}
                        icon={<CheckCircleIcon style={{ color: 'inherit' }} />}
                      />
                    </>
                  )}
                </Box>
                    {/* ATLAS Structured Form View — default for all modules */}
                    {viewMode === 'form' ? (
                      /* ATLAS Structured Form View with gRPC validation */
                      <>
                        {/* General Validation Errors Alert - for fields not mapped in form */}
                        {generalValidationErrors.length > 0 && (
                          <Alert
                            id="general-validation-errors"
                            severity="error"
                            icon={false}
                            sx={{
                              mb: 1,
                              py: 0.5,
                              px: 1.5,
                              '& .MuiAlert-message': { width: '100%', py: 0 },
                              border: '1px solid',
                              borderColor: 'error.main',
                              borderRadius: '6px',
                              bgcolor: 'rgba(211, 47, 47, 0.04)',
                            }}
                          >
                            <Typography variant="caption" fontWeight="bold" color="error.dark">
                              {tString('documentProcessing.errors.generalErrorsTitle', generalValidationErrors.length)}
                            </Typography>
                            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 1.5, listStyle: 'none' }}>
                              {generalValidationErrors.map((error, index) => (
                                <li key={index} style={{ marginBottom: '2px' }}>
                                  <Typography
                                    variant="caption"
                                    color="error.dark"
                                    sx={{
                                      display: 'block',
                                      py: 0.25,
                                      px: 0.75,
                                      fontSize: '0.7rem',
                                      lineHeight: 1.3,
                                      borderLeft: '2px solid',
                                      borderColor: 'error.main',
                                    }}
                                  >
                                    {error}
                                  </Typography>
                                </li>
                              ))}
                            </Box>
                          </Alert>
                        )}

                        {(mergeResult || mergeFallback) && (
                          <>
                            <MergeStatusBanner
                              documentsProcessed={mergeResult?.documents_processed ?? 0}
                              autoResolvedConflicts={mergeResult?.auto_resolved_conflicts ?? 0}
                              mergeFallback={mergeFallback}
                              correlationResult={mergeResult?.correlation_result ?? null}
                            />
                            {mergeResult && documentSetId && (
                              <MergeConflicts
                                setId={documentSetId}
                                conflicts={mergeResult.conflicts ?? []}
                                onResolved={(fieldPath, value) => handleFieldValueChange(fieldPath, value)}
                              />
                            )}
                          </>
                        )}

                        <AtlasStructuredForm
                          extractedData={processing.extractedData}
                          onFieldChange={handleFieldValueChange}
                          fieldSources={mergeResult ? fieldSources : null}
                          fieldValidationErrors={fieldValidationErrors}
                          mlPredictions={mlPredictions}
                          aiAppliedFields={aiAppliedFields}
                          documentSnapshot={documentSnapshot}
                          onPredictionApplied={(path) => setAiAppliedFields(prev => {
                            if (prev.has(path)) return prev;
                            const next = new Set(prev);
                            next.add(path);
                            return next;
                          })}
                          historySuggestions={historySuggestions}
                          onPredictionShown={handlePredictionShown}
                          aiClassifiedFields={aiClassifiedHSCodes}
                          headerActions={(
                            <>
                              {/* Load from History button */}
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={isLoadingHistory
                                  ? <CircularProgress size={14} color="inherit" />
                                  : <NavIcon src={actionIcons.loadFromHistory} size={16} />}
                                onClick={() => setShowHistoryConfirmDialog(true)}
                                disabled={processing.isProcessing || isLoadingHistory}
                                sx={{ px: 2, py: 0.75 }}
                              >
                                {tString('atlasFields.loadFromHistory')}
                              </Button>

                              {/* Apply All Predictions - visible when ML predictions available */}
                              {mlPredictions && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  startIcon={<NavIcon src={actionIcons.applyAllPredictions} size={16} />}
                                  onClick={() => handleApplyAllMLPredictions('all')}
                                  disabled={processing.isProcessing}
                                  sx={{ px: 2, py: 0.75 }}
                                >
                                  {tString('atlasFields.applyAllPredictions')}
                                </Button>
                              )}
                            </>
                          )}
                          onClearFieldError={(fieldPath) => {
                            // Clear all error paths that could match this field via fuzzy matching
                            setFieldValidationErrors(prev => {
                              const updated = { ...prev };

                              // Generate all path variations to clear
                              const pathsToCheck = [
                                fieldPath,
                                // Without kopf prefix
                                fieldPath.replace(/^kopf\./, ''),
                                // With kopf prefix
                                fieldPath.startsWith('kopf.') ? fieldPath : `kopf.${fieldPath}`,
                                // Without position array index prefix
                                fieldPath.replace(/^position\[\d+\]\./, ''),
                              ];

                              // Extract last part for partial matching
                              const lastPart = fieldPath.split('.').pop() || '';
                              const fieldContext = fieldPath.includes('empfanger') ? 'empfanger' :
                                fieldPath.includes('ausfuhrer') ? 'ausfuhrer' :
                                  fieldPath.includes('anmelder') ? 'anmelder' :
                                    fieldPath.includes('vertreter') ? 'vertreter' :
                                      fieldPath.includes('position') ? 'position' : '';

                              // Delete all matching error paths
                              Object.keys(updated).forEach(errorPath => {
                                // Check exact matches and variations
                                if (pathsToCheck.some(p => errorPath === p || errorPath.endsWith(p) || p.endsWith(errorPath))) {
                                  delete updated[errorPath];
                                  return;
                                }

                                // Check if this error matches by context and field name
                                const errorLastPart = errorPath.split('.').pop() || '';
                                const errorContext = errorPath.includes('empfanger') ? 'empfanger' :
                                  errorPath.includes('ausfuhrer') ? 'ausfuhrer' :
                                    errorPath.includes('anmelder') ? 'anmelder' :
                                      errorPath.includes('vertreter') ? 'vertreter' :
                                        errorPath.includes('position') ? 'position' : '';

                                if (lastPart === errorLastPart && fieldContext && fieldContext === errorContext) {
                                  delete updated[errorPath];
                                }
                              });

                              // console.log(`Cleared validation errors for: ${fieldPath}`);
                              // console.log(`   Remaining errors: ${Object.keys(updated).length}`);

                              return updated;
                            });
                          }}
                          getFieldError={getFieldError}
                          useWideLayout={true}
                        />

                        {/* ATLAS Validation Preview - HIDDEN */}

                        {/* Action Buttons for ATLAS Form View - sticky footer */}
                        <Box sx={{
                          position: 'sticky',
                          bottom: 0,
                          backgroundColor: '#fff',
                          borderTop: `1px solid ${brand.borderSubtle}`,
                          px: 3,
                          py: 2,
                          pr: 10,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          zIndex: 10,
                          flexWrap: { xs: 'wrap', md: 'nowrap' },
                        }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sendPartialData}
                                onChange={(e) => setSendPartialData(e.target.checked)}
                                disabled={processing.isProcessing}
                                sx={{
                                  color: brand.orange,
                                  '&.Mui-checked': { color: brand.orange },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Box sx={{ fontSize: 13, fontWeight: 600, color: brand.textPrimary }}>
                                  {tString('documentProcessing.sendPartial.label')}
                                </Box>
                                <Box sx={{ fontSize: 11, color: brand.textSecondary, lineHeight: 1.3 }}>
                                  {t('documentProcessing.sendPartial.description')}
                                </Box>
                              </Box>
                            }
                            sx={{ m: 0, alignItems: 'flex-start' }}
                          />
                          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

                          {/* Action buttons: stretch to fill rows on small screens,
                              natural width on desktop. */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 2,
                              width: { xs: '100%', md: 'auto' },
                              '& > .MuiButton-root': {
                                flex: { xs: '1 1 140px', md: '0 0 auto' },
                                whiteSpace: 'nowrap',
                              },
                            }}
                          >
                          <Button
                            onClick={handleResetForm}
                            disabled={processing.isProcessing}
                            startIcon={<NavIcon src={actionIcons.reset} size={16} />}
                            variant="outlined"
                            color="primary"
                          >
                            {t('documentProcessing.actions.reset')}
                          </Button>

                          <Button
                            onClick={() => {
                              if (uploadedFiles.length > 0 && uploadedFiles[0].fileId) {
                                setSelectedFileForAnnotation(uploadedFiles[0]);
                              }
                              setShowTeachAI(true);
                            }}
                            disabled={processing.isProcessing || !uploadedFiles.some(f => f.fileId)}
                            startIcon={<NavIcon src={actionIcons.teachAi} size={16} />}
                            variant="outlined"
                            color="primary"
                          >
                            {tString('atlasFields.teachAI')}
                          </Button>

                          <Button
                            onClick={handleRunAudit}
                            disabled={processing.isProcessing || isAuditing || !processing.extractedData}
                            startIcon={isAuditing
                              ? <CircularProgress size={16} color="inherit" />
                              : <NavIcon src={actionIcons.auditDeclaration} size={16} />}
                            variant="outlined"
                            color="primary"
                          >
                            {isAuditing ? `${tString('atlasFields.auditDeclaration')}...` : tString('atlasFields.auditDeclaration')}
                          </Button>

                          <Button
                            onClick={handleSendData}
                            disabled={
                              isSending ||
                              processing.isProcessing ||
                              !Object.values(processing.fieldSelections).some(Boolean)
                            }
                            startIcon={<NavIcon src={actionIcons.validate} size={16} />}
                            variant="contained"
                            color="primary"
                          >
                            {t('documentProcessing.actions.sendData')}
                          </Button>
                          </Box>
                        </Box>

                        {/* Audit Results Panel */}
                        {showAuditResults && auditReport && (
                          <>
                            <AuditDashboard
                              report={auditReport}
                              appliedCount={appliedFixes.size}
                              fixableCount={auditReport.area_results.reduce((sum, ar) =>
                                sum + ar.findings.filter(f => f.suggested_fix && f.field).length, 0)}
                              onClose={() => setShowAuditResults(false)}
                              onDownloadPdf={handleDownloadAuditPdf}
                              onAreaClick={(area) => setExpandedAuditArea(area)}
                            />

                            {auditReport.area_results.some(ar => ar.findings.some(f =>
                              f.suggested_fix && f.field && (f.fix_confidence || 0) >= 0.6 && !appliedFixes.has(f.field)
                            )) && (
                              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                                <Button variant="outlined" color="primary" onClick={() => setShowApplyDialog(true)}>
                                  Apply All Fixes
                                </Button>
                              </Box>
                            )}

                            <AuditFindings
                              areaResults={auditReport.area_results}
                              appliedFixes={appliedFixes}
                              onApplyFix={handleAuditApplyFix}
                              onUndoFix={handleAuditUndoFix}
                              onViewField={handleAuditViewField}
                              expandedArea={expandedAuditArea}
                            />

                            <AuditHistory
                              lrn={processing.extractedData?.kopf?.lrn}
                              currentScore={auditReport.overall_risk_score}
                            />

                            <AuditApplyDialog
                              open={showApplyDialog}
                              report={auditReport}
                              appliedFixes={appliedFixes}
                              onConfirm={handleApplyAllAuditFixes}
                              onCancel={() => setShowApplyDialog(false)}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      /* Traditional Table View */
                      <>
                        {(mergeResult || mergeFallback) && (
                          <>
                            <MergeStatusBanner
                              documentsProcessed={mergeResult?.documents_processed ?? 0}
                              autoResolvedConflicts={mergeResult?.auto_resolved_conflicts ?? 0}
                              mergeFallback={mergeFallback}
                              correlationResult={mergeResult?.correlation_result ?? null}
                            />
                            {mergeResult && documentSetId && (
                              <MergeConflicts
                                setId={documentSetId}
                                conflicts={mergeResult.conflicts ?? []}
                                onResolved={(fieldPath, value) => handleFieldValueChange(fieldPath, value)}
                              />
                            )}
                          </>
                        )}

                        {/* Data Preview Table */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                          <TableContainer
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              maxHeight: { xs: 300, sm: 400 },
                              overflowY: 'auto',
                              overflowX: 'auto',
                              width: '100%'
                            }}
                          >
                            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 800 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell padding="checkbox" sx={{ width: 48, bgcolor: 'background.paper' }}>
                                    <Checkbox
                                      indeterminate={
                                        Object.values(processing.fieldSelections).some(Boolean) &&
                                        !Object.values(processing.fieldSelections).every(Boolean)
                                      }
                                      checked={Object.values(processing.fieldSelections).every(Boolean)}
                                      onChange={(e) => {
                                        const allSelections: Record<string, boolean> = {};
                                        Object.keys(flattenObject(processing.extractedData)).forEach(key => {
                                          allSelections[key] = e.target.checked;
                                        });
                                        setProcessing(prev => ({ ...prev, fieldSelections: allSelections }));
                                      }}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell sx={{
                                    fontWeight: 'bold',
                                    bgcolor: 'background.paper',
                                    width: '22%',
                                    minWidth: 180,
                                    maxWidth: 250,
                                  }}>
                                    {tString('documentProcessing.tableView.fieldName')}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper', width: '45%' }}>
                                    {tString('documentProcessing.tableView.extractedValue')}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper', width: '33%', minWidth: 200 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {tString('documentProcessing.tableView.mlPrediction')}
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="primary"
                                          startIcon={isLoadingHistory
                                            ? <CircularProgress size={12} color="inherit" />
                                            : <NavIcon src={actionIcons.loadFromHistory} size={16} />}
                                          onClick={() => setShowHistoryConfirmDialog(true)}
                                          disabled={processing.isProcessing || isLoadingHistory}
                                        >
                                          {tString('atlasFields.loadFromHistory')}
                                        </Button>
                                        {mlPredictions && (
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            startIcon={<NavIcon src={actionIcons.applyAllPredictions} size={16} />}
                                            onClick={() => handleApplyAllMLPredictions('all')}
                                            disabled={processing.isProcessing}
                                          >
                                            {tString('atlasFields.applyAllPredictions')}
                                          </Button>
                                        )}
                                      </Box>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(() => {
                                  const allItems = addMissingRequiredFields(flattenDataForTable(processing.extractedData));

                                  // Separate items into: non-position fields, required position fields, optional position fields
                                  const nonPositionItems = allItems.filter(item => getPositionIndex(item.fieldPath) === -1);
                                  const positionItems = allItems.filter(item => getPositionIndex(item.fieldPath) !== -1);

                                  // Group position items by index
                                  const positionGroups: Record<number, { required: typeof allItems; optional: typeof allItems }> = {};
                                  positionItems.forEach(item => {
                                    const idx = getPositionIndex(item.fieldPath);
                                    if (!positionGroups[idx]) {
                                      positionGroups[idx] = { required: [], optional: [] };
                                    }
                                    if (isOptionalPositionField(item.fieldPath)) {
                                      positionGroups[idx].optional.push(item);
                                    } else {
                                      positionGroups[idx].required.push(item);
                                    }
                                  });

                                  const sortedPositionIndices = Object.keys(positionGroups).map(Number).sort((a, b) => a - b);

                                  // Helper to render a single field row
                                  const renderFieldRow = (item: typeof allItems[0], index: number) => {
                                    const fieldStatus = getFieldStatus(item.fieldPath, processing.extractedData);
                                    const getRowColor = () => {
                                      switch (fieldStatus) {
                                        case 'missing': return `${theme.palette.error.main}12`;
                                        case 'verify': return `${theme.palette.warning.main}12`;
                                        case 'complete': return `${theme.palette.success.main}12`;
                                        default: return 'background.paper';
                                      }
                                    };

                                    return (
                                      <TableRow
                                        key={item.fieldPath}
                                        hover
                                        data-field-status={fieldStatus}
                                        data-field-path={item.fieldPath}
                                        sx={{
                                          bgcolor: getRowColor(),
                                          '&:hover': {
                                            bgcolor: fieldStatus === 'missing' ? `${theme.palette.error.main}20` :
                                              fieldStatus === 'verify' ? `${theme.palette.warning.main}20` :
                                                fieldStatus === 'complete' ? `${theme.palette.success.main}20` :
                                                  'action.hover',
                                          }
                                        }}
                                      >
                                        <TableCell padding="checkbox">
                                          <Checkbox
                                            checked={processing.fieldSelections[item.fieldPath] || false}
                                            onChange={() => handleFieldToggle(item.fieldPath)}
                                            size="small"
                                          />
                                        </TableCell>
                                        <TableCell sx={{
                                          fontWeight: 'medium',
                                          color: 'text.primary',
                                          fontSize: '0.875rem',
                                          width: '22%',
                                          minWidth: 180,
                                          maxWidth: 250,
                                          overflow: 'hidden',
                                        }}>
                                          <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                          }}>
                                            <Tooltip title={getFieldLabel(item.fieldPath || item.key)} placement="top-start">
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 500,
                                                  wordBreak: 'break-word',
                                                  lineHeight: 1.3,
                                                }}
                                              >
                                                {getFieldLabel(item.fieldPath || item.key)}
                                              </Typography>
                                            </Tooltip>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                              {fieldStatus === 'missing' && (
                                                <Chip
                                                  label={tString('documentProcessing.fieldStatus.required')}
                                                  size="small"
                                                  color="error"
                                                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                                                />
                                              )}
                                              {fieldStatus === 'verify' && (
                                                <Chip
                                                  label={tString('documentProcessing.fieldStatus.verify')}
                                                  size="small"
                                                  color="warning"
                                                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                                                />
                                              )}
                                            </Box>
                                          </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '45%' }}>
                                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }} data-field-path={item.fieldPath}>
                                            {(() => {
                                              const fieldError = getFieldError(item.fieldPath);
                                              const hasValidationError = !!fieldError;
                                              return (
                                                <TextField
                                                  fullWidth
                                                  size="small"
                                                  name={item.fieldPath}
                                                  value={convertToDisplayValue(item.fieldPath, item.value) || ''}
                                                  onChange={(e) => {
                                                    handleFieldValueChange(item.fieldPath, e.target.value);
                                                    if (fieldValidationErrors[item.fieldPath] || hasValidationError) {
                                                      setFieldValidationErrors(prev => {
                                                        const updated = { ...prev };
                                                        const fieldPath = item.fieldPath;

                                                        const pathsToCheck = [
                                                          fieldPath,
                                                          fieldPath.replace(/^kopf\./, ''),
                                                          fieldPath.startsWith('kopf.') ? fieldPath : `kopf.${fieldPath}`,
                                                          fieldPath.replace(/^position\[\d+\]\./, ''),
                                                        ];

                                                        const lastPart = fieldPath.split('.').pop() || '';
                                                        const fieldContext = fieldPath.includes('empfanger') ? 'empfanger' :
                                                          fieldPath.includes('ausfuhrer') ? 'ausfuhrer' :
                                                            fieldPath.includes('anmelder') ? 'anmelder' :
                                                              fieldPath.includes('vertreter') ? 'vertreter' :
                                                                fieldPath.includes('position') ? 'position' : '';

                                                        Object.keys(updated).forEach(errorPath => {
                                                          if (pathsToCheck.some(p => errorPath === p || errorPath.endsWith(p) || p.endsWith(errorPath))) {
                                                            delete updated[errorPath];
                                                            return;
                                                          }
                                                          const errorLastPart = errorPath.split('.').pop() || '';
                                                          const errorContext = errorPath.includes('empfanger') ? 'empfanger' :
                                                            errorPath.includes('ausfuhrer') ? 'ausfuhrer' :
                                                              errorPath.includes('anmelder') ? 'anmelder' :
                                                                errorPath.includes('vertreter') ? 'vertreter' :
                                                                  errorPath.includes('position') ? 'position' : '';
                                                          if (lastPart === errorLastPart && fieldContext && fieldContext === errorContext) {
                                                            delete updated[errorPath];
                                                          }
                                                        });

                                                        return updated;
                                                      });
                                                    }
                                                  }}
                                                  onBlur={(e) => {
                                                    // Convert display value back to backend format on blur
                                                    const backendVal = convertToBackendValue(item.fieldPath, e.target.value);
                                                    if (backendVal !== e.target.value) {
                                                      handleFieldValueChange(item.fieldPath, backendVal);
                                                    }
                                                  }}
                                                  variant="outlined"
                                                  placeholder={fieldStatus === 'missing' ? tString('documentProcessing.fieldStatus.enterRequired') : ''}
                                                  inputProps={{
                                                    'data-field-path': item.fieldPath,
                                                  }}
                                                  sx={{
                                                    '& .MuiInputBase-input': {
                                                      fontSize: '0.875rem',
                                                      py: 1,
                                                      wordBreak: 'break-word',
                                                      whiteSpace: 'pre-wrap',
                                                    },
                                                    '& .MuiOutlinedInput-root': {
                                                      '&.Mui-focused fieldset': {
                                                        borderColor: (fieldStatus === 'missing' || hasValidationError) ? theme.palette.error.main : undefined,
                                                        borderWidth: (fieldStatus === 'missing' || hasValidationError) ? 2 : 1
                                                      }
                                                    },
                                                    '& .MuiFormHelperText-root': {
                                                      mx: 0,
                                                      mt: 0.5
                                                    }
                                                  }}
                                                  multiline={item.value && item.value.length > 50}
                                                  minRows={1}
                                                  maxRows={3}
                                                  error={fieldStatus === 'missing' || hasValidationError}
                                                  helperText={fieldError || ''}
                                                />
                                              );
                                            })()}

                                            {processing.selectedModule?.name?.toLowerCase().includes('atlas') && (
                                              <FieldValidator
                                                fieldPath={item.fieldPath}
                                                value={item.value || ''}
                                                showHelperText={false}
                                              />
                                            )}

                                            {/* Legacy per-field "Open classifier dialog" button removed —
                                                replaced by the standalone Tariff Classifier page +
                                                inline `autoFillMissingHSCodes` batch fill. */}
                                          </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '33%', minWidth: 200 }}>
                                          <MLPredictionCell
                                            fieldPath={item.fieldPath}
                                            fieldValue={item.value}
                                            extractedData={processing.extractedData}
                                            onApplyPrediction={(prediction) => {
                                              setProcessing(prev => {
                                                const newData = JSON.parse(JSON.stringify(prev.extractedData || {}));
                                                setNestedValue(newData, item.fieldPath, prediction);
                                                return { ...prev, extractedData: newData };
                                              });
                                              // Record provenance so this value is classified AI-Prefilled,
                                              // not Extracted, after a switch to form view.
                                              setAiAppliedFields(prev => {
                                                const next = new Set(prev);
                                                next.add(item.fieldPath);
                                                return next;
                                              });
                                            }}
                                            onPredictionShown={handlePredictionShown}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  };

                                  return (
                                    <>
                                      {/* Non-position (header) fields */}
                                      {nonPositionItems.map((item, index) => renderFieldRow(item, index))}

                                      {/* Position (line item) fields - grouped by item with optional fields separated */}
                                      {sortedPositionIndices.map(posIdx => {
                                        const group = positionGroups[posIdx];
                                        const isExpanded = showOptionalPositionFields[posIdx] || false;
                                        const optionalCount = group.optional.length;

                                        return (
                                          <React.Fragment key={`position-group-${posIdx}`}>
                                            {/* Required fields for this line item */}
                                            {group.required.map((item, index) => renderFieldRow(item, index))}

                                            {/* Toggle row for optional fields */}
                                            {optionalCount > 0 && (
                                              <TableRow
                                                key={`optional-toggle-${posIdx}`}
                                                sx={{
                                                  bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                                                  cursor: 'pointer',
                                                  '&:hover': {
                                                    bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.100',
                                                  },
                                                }}
                                                onClick={() => setShowOptionalPositionFields(prev => ({
                                                  ...prev,
                                                  [posIdx]: !prev[posIdx],
                                                }))}
                                              >
                                                <TableCell colSpan={4} sx={{ py: 0.75 }}>
                                                  <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                  }}>
                                                    <IconButton size="small" sx={{ p: 0.25 }}>
                                                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                    </IconButton>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                                      {isExpanded ? 'Hide' : 'Show'} Optional Fields
                                                      {sortedPositionIndices.length > 1 ? ` (Item ${posIdx + 1})` : ''}
                                                    </Typography>
                                                    <Chip
                                                      label={`${optionalCount} fields`}
                                                      size="small"
                                                      variant="outlined"
                                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                  </Box>
                                                </TableCell>
                                              </TableRow>
                                            )}

                                            {/* Optional fields (conditionally shown) */}
                                            {isExpanded && group.optional.map((item, index) => renderFieldRow(item, index))}
                                          </React.Fragment>
                                        );
                                      })}
                                    </>
                                  );
                                })()}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>

                        {/* ATLAS Validation Preview - HIDDEN */}

                        {/* Action Buttons - Always Visible (sticky footer) */}
                        <Box sx={{
                          position: 'sticky',
                          bottom: 0,
                          backgroundColor: '#fff',
                          borderTop: `1px solid ${brand.borderSubtle}`,
                          px: 3,
                          py: 2,
                          pr: 10,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          zIndex: 10,
                          flexWrap: { xs: 'wrap', md: 'nowrap' },
                        }}>
                          {/* Auto-Classify HS Codes Button - HIDDEN */}
                          {false && processing.selectedModule?.name?.toLowerCase().includes('atlas') &&
                            processing.extractedData?.position &&
                            Array.isArray(processing.extractedData.position) &&
                            processing.extractedData.position.length > 0 && (
                              <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                startIcon={classifyingAll ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                                onClick={handleAutoClassifyAll}
                                disabled={processing.isProcessing || classifyingAll}
                              >
                                {classifyingAll ? tString('documentProcessing.classifying') : tString('documentProcessing.autoClassifyHsCodes')}
                              </Button>
                            )}

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sendPartialData}
                                onChange={(e) => setSendPartialData(e.target.checked)}
                                disabled={processing.isProcessing}
                                sx={{
                                  color: brand.orange,
                                  '&.Mui-checked': { color: brand.orange },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Box sx={{ fontSize: 13, fontWeight: 600, color: brand.textPrimary }}>
                                  {tString('documentProcessing.sendPartial.label')}
                                </Box>
                                <Box sx={{ fontSize: 11, color: brand.textSecondary, lineHeight: 1.3 }}>
                                  {t('documentProcessing.sendPartial.description')}
                                </Box>
                              </Box>
                            }
                            sx={{ m: 0, alignItems: 'flex-start' }}
                          />
                          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

                          {/* Action buttons: stretch to fill rows on small screens,
                              natural width on desktop. */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 2,
                              width: { xs: '100%', md: 'auto' },
                              '& > .MuiButton-root': {
                                flex: { xs: '1 1 140px', md: '0 0 auto' },
                                whiteSpace: 'nowrap',
                              },
                            }}
                          >
                          <Button
                            onClick={handleResetForm}
                            disabled={processing.isProcessing}
                            startIcon={<NavIcon src={actionIcons.reset} size={16} />}
                            variant="outlined"
                            color="primary"
                          >
                            {t('documentProcessing.actions.reset')}
                          </Button>

                          <Button
                            onClick={() => {
                              if (uploadedFiles.length > 0 && uploadedFiles[0].fileId) {
                                setSelectedFileForAnnotation(uploadedFiles[0]);
                              }
                              setShowTeachAI(true);
                            }}
                            disabled={processing.isProcessing || !uploadedFiles.some(f => f.fileId)}
                            startIcon={<NavIcon src={actionIcons.teachAi} size={16} />}
                            variant="outlined"
                            color="primary"
                          >
                            {tString('atlasFields.teachAI')}
                          </Button>

                          <Button
                            onClick={handleRunAudit}
                            disabled={processing.isProcessing || isAuditing || !processing.extractedData}
                            startIcon={isAuditing
                              ? <CircularProgress size={16} color="inherit" />
                              : <NavIcon src={actionIcons.auditDeclaration} size={16} />}
                            variant="outlined"
                            color="primary"
                          >
                            {isAuditing ? `${tString('atlasFields.auditDeclaration')}...` : tString('atlasFields.auditDeclaration')}
                          </Button>

                          <Button
                            onClick={handleSendData}
                            disabled={
                              isSending ||
                              processing.isProcessing ||
                              !Object.values(processing.fieldSelections).some(Boolean)
                            }
                            startIcon={<NavIcon src={actionIcons.validate} size={16} />}
                            variant="contained"
                            color="primary"
                          >
                            {t('documentProcessing.actions.sendData')}
                          </Button>
                          </Box>
                        </Box>

                        {/* Audit Results Panel (Table View) */}
                        {showAuditResults && auditReport && (
                          <>
                            <AuditDashboard
                              report={auditReport}
                              appliedCount={appliedFixes.size}
                              fixableCount={auditReport.area_results.reduce((sum, ar) =>
                                sum + ar.findings.filter(f => f.suggested_fix && f.field).length, 0)}
                              onClose={() => setShowAuditResults(false)}
                              onDownloadPdf={handleDownloadAuditPdf}
                              onAreaClick={(area) => setExpandedAuditArea(area)}
                            />

                            {auditReport.area_results.some(ar => ar.findings.some(f =>
                              f.suggested_fix && f.field && (f.fix_confidence || 0) >= 0.6 && !appliedFixes.has(f.field)
                            )) && (
                              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                                <Button variant="outlined" color="primary" onClick={() => setShowApplyDialog(true)}>
                                  Apply All Fixes
                                </Button>
                              </Box>
                            )}

                            <AuditFindings
                              areaResults={auditReport.area_results}
                              appliedFixes={appliedFixes}
                              onApplyFix={handleAuditApplyFix}
                              onUndoFix={handleAuditUndoFix}
                              onViewField={handleAuditViewField}
                              expandedArea={expandedAuditArea}
                            />

                            <AuditHistory
                              lrn={processing.extractedData?.kopf?.lrn}
                              currentScore={auditReport.overall_risk_score}
                            />

                            <AuditApplyDialog
                              open={showApplyDialog}
                              report={auditReport}
                              appliedFixes={appliedFixes}
                              onConfirm={handleApplyAllAuditFixes}
                              onCancel={() => setShowApplyDialog(false)}
                            />
                          </>
                        )}
                      </>
                    )}
            </>
          )}

          {/* ── REVIEW STEP ── */}
          {activeWorkflowStep === 'review' && processing.extractedData && (() => {
            // Use validated data if available (after Validate & Send), else build from current form state
            const reviewData = previewSubmissionData
              ?? filterSelectedFields(processing.extractedData, processing.fieldSelections);
            const isReadyToSubmit = !!previewSubmissionData;
            return (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden' }}>
                <SubmissionPreviewDialog
                  panel
                  data={reviewData}
                  onProceed={handleProceedFromPreview}
                  onEdit={() => { setPreviewSubmissionData(null); setActiveWorkflowStep('analyse'); }}
                  proceedLabel="Submit"
                  editLabel="← Back to Edit"
                  hideProceed={!isReadyToSubmit}
                />
              </Box>
            );
          })()}


            </Box>{/* End primary content scroll area */}
          </Box>{/* End PRIMARY CONTENT AREA */}


        </Box>{/* End MAIN CONTENT AREA */}

        {/* Module Authentication Dialog */}
        <ModuleAuthModal
          open={authDialog.open && authDialog.module !== null}
          onClose={(authenticated) => {
            const moduleToKeepSelected = authDialog.module;
            const wasSwitchUser = isSwitchUserRef.current;
            isSwitchUserRef.current = false;
            setAuthDialog({ open: false, module: null, credentials: { username: '', password: '' } });

            if (moduleToKeepSelected && authenticated) {
              // Always update token + module status immediately
              storeModuleAuth(moduleToKeepSelected.id);
              setModules(prev => prev.map(m =>
                m.id === moduleToKeepSelected.id
                  ? { ...m, auth_status: 'authenticated' as const }
                  : m
              ));

              // Notify the drawer (and any other listeners) so its local
              // module list reflects the new auth_status without a re-open.
              window.dispatchEvent(new CustomEvent('module-auth-changed', {
                detail: { moduleId: moduleToKeepSelected.id, authStatus: 'authenticated' as const },
              }));

              const updatedModule: Module = { ...moduleToKeepSelected, auth_status: 'authenticated' as const };
              const isComplianceModule = updatedModule.name?.toLowerCase().includes('sanctions') ||
                updatedModule.name?.toLowerCase().includes('compliance');

              if (!wasSwitchUser) {
                // Normal auth flow — apply immediately (original behavior)
                if (isComplianceModule && moduleSelectionLocked) {
                  setProcessing(prev => ({ ...prev, complianceModule: updatedModule, selectedModule: prev.selectedModule }));
                } else {
                  setProcessing(prev => ({ ...prev, selectedModule: updatedModule }));
                  setSelectedModule(updatedModule as any);
                }
                authService.getModuleUserDetails(moduleToKeepSelected.id).then(userDetails => {
                  if (userDetails?.found && userDetails.user_details) {
                    const d = userDetails.user_details;
                    const eoriNumber = d.ausfuhr_eori || '';
                    setModuleUserDetails({
                      atlas_user_id: d.atlas_user_id, atlas_mandant_id: d.atlas_mandant_id,
                      atlas_kundennummer: d.atlas_kundennummer, email: d.email,
                      modules_data: d.modules_data, ausfuhr_eori: eoriNumber,
                      eoriNiederlassungsnummer: eoriNumber,
                    });
                  }
                }).catch(err => console.error('Failed to fetch module user details:', err));
              } else {
                // Switch User flow — update module state first, then decide on user details
                if (isComplianceModule && moduleSelectionLocked) {
                  setProcessing(prev => ({ ...prev, complianceModule: updatedModule, selectedModule: prev.selectedModule }));
                } else {
                  setProcessing(prev => ({ ...prev, selectedModule: updatedModule }));
                  setSelectedModule(updatedModule as any);
                }
                // Fetch new user details, then decide based on whether predictions are loaded
                authService.getModuleUserDetails(moduleToKeepSelected.id).then(userDetails => {
                  const pendingDetails = userDetails?.found && userDetails.user_details ? (() => {
                    const d = userDetails.user_details;
                    const eoriNumber = d.ausfuhr_eori || '';
                    return {
                      atlas_user_id: d.atlas_user_id, atlas_mandant_id: d.atlas_mandant_id,
                      atlas_kundennummer: d.atlas_kundennummer, email: d.email,
                      modules_data: d.modules_data, ausfuhr_eori: eoriNumber,
                      eoriNiederlassungsnummer: eoriNumber,
                    };
                  })() : null;

                  if (mlPredictions) {
                    // Prediction data loaded — ask user what to do
                    setSwitchUserConfirmDialog({ open: true, pendingModule: updatedModule, pendingUserDetails: pendingDetails });
                  } else {
                    // No prediction data — switch silently
                    if (pendingDetails) setModuleUserDetails(pendingDetails);
                    toast.success(`Switched user for "${moduleToKeepSelected.name}".`);
                  }
                }).catch(err => {
                  console.error('Failed to fetch module user details after switch:', err);
                  toast.success(`Switched user for "${moduleToKeepSelected.name}".`);
                });
              }
            } else if (moduleToKeepSelected && !authenticated) {
              setProcessing(prev => ({ ...prev, selectedModule: moduleToKeepSelected }));
            }
          }}
          moduleId={authDialog.module?.id || ''}
          moduleName={authDialog.module?.name || ''}
        />

        {/* Switch User Confirmation Dialog */}
        <Dialog
          open={switchUserConfirmDialog.open}
          onClose={() => setSwitchUserConfirmDialog({ open: false, pendingModule: null, pendingUserDetails: null })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <ManageAccountsIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {tString('documentProcessing.switchUser.title')}
            </Typography>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {tString('documentProcessing.switchUser.message')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2, gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => setSwitchUserConfirmDialog({ open: false, pendingModule: null, pendingUserDetails: null })}
            >
              {tString('documentProcessing.switchUser.cancel')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                // Keep Form — apply new user details without changing form data
                if (switchUserConfirmDialog.pendingUserDetails) {
                  setModuleUserDetails(switchUserConfirmDialog.pendingUserDetails);
                }
                toast.success(`${tString('documentProcessing.switchUser.title')} — ${tString('documentProcessing.switchUser.keepFormDone')}`);
                setSwitchUserConfirmDialog({ open: false, pendingModule: null, pendingUserDetails: null });
              }}
            >
              {tString('documentProcessing.switchUser.keepForm')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                // Reset Form — restore original extracted data, then apply new user details
                if (originalExtractedDataRef.current) {
                  setProcessing(prev => ({
                    ...prev,
                    extractedData: JSON.parse(JSON.stringify(originalExtractedDataRef.current)),
                  }));
                }
                if (switchUserConfirmDialog.pendingUserDetails) {
                  setModuleUserDetails(switchUserConfirmDialog.pendingUserDetails);
                }
                setMlPredictions(null);
                setAiAppliedFields(new Set());
                setDocumentSnapshot(null);
                toast.success(`${tString('documentProcessing.switchUser.title')} — ${tString('documentProcessing.switchUser.resetFormDone')}`);
                setSwitchUserConfirmDialog({ open: false, pendingModule: null, pendingUserDetails: null });
              }}
            >
              {tString('documentProcessing.switchUser.resetForm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Magic Marker Annotation Dialog */}
        <Dialog
          open={showMagicMarker}
          onClose={() => setShowMagicMarker(false)}
          fullScreen
          TransitionProps={{
            onExited: () => setSelectedFileForAnnotation(null)
          }}
        >
          <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'white' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <HighlightIcon />
                <Typography variant="h6">
                  {t('documentProcessing.dialogs.magicMarker')}
                </Typography>
                {selectedFileForAnnotation && (
                  <Chip
                    label={selectedFileForAnnotation.name}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                )}
              </Box>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setShowMagicMarker(false)}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {selectedFileForAnnotation && selectedFileForAnnotation.fileId && (
              <DocumentAnnotator
                documentId={selectedFileForAnnotation.fileId}
                documentUrl={selectedFileForAnnotation.url}
                documentContent={selectedFileForAnnotation.extractedData}
                enableCollaboration={true}
                onAnnotationsChange={(annotations) => {
                  // console.log('Annotations updated:', annotations);
                }}
                onRoutingDecision={(decision) => {
                  // console.log('Routing decision:', decision);
                  toast.info(`Document routed to: ${decision.next_stage}`);
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Teach AI Modal */}
        {showTeachAI && selectedFileForAnnotation && selectedFileForAnnotation.fileId && (
          <TeachAIModal
            open={showTeachAI}
            onClose={() => setShowTeachAI(false)}
            pdfUrl={`/api/v1/files/${selectedFileForAnnotation.fileId}/view`}
            fileName={selectedFileForAnnotation.name}
            fileId={selectedFileForAnnotation.fileId}
            availableFiles={uploadedFiles
              .filter(f => !!f.fileId)
              .map(f => ({ fileId: f.fileId as string, name: f.name }))}
            onFileChange={(newFileId) => {
              const newFile = uploadedFiles.find(f => f.fileId === newFileId);
              if (!newFile) return;
              // Persist any in-form edits made on the CURRENT file back to its
              // uploadedFiles entry before swapping the active extractedData,
              // so the corrections aren't lost when the user switches back.
              const currentId = selectedFileForAnnotation?.fileId;
              if (currentId && currentId !== newFileId && processing.extractedData) {
                setUploadedFiles(prev =>
                  prev.map(f =>
                    f.fileId === currentId
                      ? { ...f, extractedData: processing.extractedData ?? f.extractedData }
                      : f
                  )
                );
              }
              setSelectedFileForAnnotation(newFile);
              if (newFile.extractedData !== undefined) {
                setProcessing(prev => ({ ...prev, extractedData: newFile.extractedData }));
              }
            }}
            onFieldMapped={(fieldPath, value) => {
              // console.log('Field mapped via Teach AI:', fieldPath, value);
              // Immediately update the form field with the extracted value
              handleFieldValueChange(fieldPath, value);
              // Show a success toast so the user sees live feedback
              toast.success(
                `Field "${fieldPath.split('.').pop()}" updated to "${value.substring(0, 40)}${value.length > 40 ? '…' : ''}"`,
                { position: 'bottom-right', autoClose: 3000 }
              );
            }}
            onFieldDeleted={(fieldPath) => {
              // console.log('Rule deleted via Teach AI, clearing field:', fieldPath);
              // Clear the field value in the live form
              handleFieldValueChange(fieldPath, '');
              // Also un-check the field's selection checkbox
              setProcessing(prev => ({
                ...prev,
                fieldSelections: {
                  ...prev.fieldSelections,
                  [fieldPath]: false,
                },
              }));
              toast.warning(
                `Rule deleted — field "${fieldPath.split('.').pop()}" has been cleared`,
                { position: 'bottom-right', autoClose: 4000 }
              );
            }}
            onFieldRestored={(fieldPath, value) => {
              // Inverse of onFieldDeleted: re-populate the live form value and
              // re-check the field's selection so it's included again.
              handleFieldValueChange(fieldPath, value);
              setProcessing(prev => ({
                ...prev,
                fieldSelections: {
                  ...prev.fieldSelections,
                  [fieldPath]: true,
                },
              }));
              toast.success(
                `Field "${fieldPath.split('.').pop()}" restored`,
                { position: 'bottom-right', autoClose: 3000 }
              );
            }}
            extractedData={processing.extractedData || undefined}
          />
        )}

        {/* Compliance Report Dialog */}
        {(() => {
          const displayedReport = activeComplianceReport || processing.complianceReport;
          return (
            <Dialog
              open={showComplianceReport}
              onClose={() => { setShowComplianceReport(false); setActiveComplianceReport(null); }}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  height: '85vh',
                  maxHeight: '85vh',
                  borderRadius: 2,
                }
              }}
            >
              <DialogTitle sx={{
                background: `linear-gradient(135deg, ${brand.orange} 0%, ${brand.yellow} 100%)`,
                color: '#fff',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ShieldIcon />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('documentProcessing.dialogs.complianceReport')}
                    </Typography>
                    {displayedReport && (
                      <Chip
                        size="small"
                        label={displayedReport.overall_status}
                        sx={{
                          bgcolor: displayedReport.overall_status === 'compliant' ? brand.success :
                            displayedReport.overall_status === 'non_compliant' ? brand.error : brand.warning,
                          color: '#fff',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                  <IconButton
                    edge="end"
                    sx={{ color: '#fff' }}
                    onClick={() => { setShowComplianceReport(false); setActiveComplianceReport(null); }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                {displayedReport && (
                  <ComplianceReport report={displayedReport} />
                )}
              </DialogContent>
              <DialogActions sx={{ borderTop: `1px solid ${brand.borderSubtle}`, p: 2 }}>
                <Button
                  onClick={() => { setShowComplianceReport(false); setActiveComplianceReport(null); }}
                  sx={{ color: brand.textSecondary }}
                >
                  Close
                </Button>
                {displayedReport && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      const reportData = JSON.stringify(displayedReport, null, 2);
                      const blob = new Blob([reportData], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `compliance-report-${new Date().toISOString()}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Compliance report exported');
                    }}
                    sx={{
                      bgcolor: brand.orange,
                      '&:hover': { bgcolor: brand.orange, filter: 'brightness(0.92)' },
                    }}
                  >
                    Export Report
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          );
        })()}

        {/* ─── Floating "Compliance results" button (bottom-left) ───
            Shown once any per-file compliance report has come back, regardless
            of which workflow step is active. Click opens the list dialog. */}
        {allComplianceReports.length > 0 && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              left: 24,
              zIndex: (t) => t.zIndex.fab,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
            }}
          >
            <Chip
              icon={<ShieldIcon sx={{ color: `${brand.successText} !important` }} />}
              label={`Compliance check complete · ${allComplianceReports.length} ${allComplianceReports.length === 1 ? 'file' : 'files'}`}
              sx={{
                bgcolor: '#E8F5E9',
                color: brand.successText,
                fontWeight: 600,
                border: `1px solid ${brand.success}55`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                height: 32,
              }}
            />
            <Fab
              variant="extended"
              size="medium"
              onClick={() => setShowAllComplianceDialog(true)}
              aria-label="Open compliance results"
              sx={{
                bgcolor: brand.orange,
                color: '#fff',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(242,111,33,0.35)',
                '&:hover': { bgcolor: brand.orange, filter: 'brightness(0.92)' },
              }}
            >
              <Badge
                badgeContent={allComplianceReports.length}
                color="error"
                sx={{ mr: 1.5, '& .MuiBadge-badge': { right: -4, top: 2 } }}
              >
                <ComplianceIcon sx={{ mr: 0.5 }} />
              </Badge>
              View Results
            </Fab>
          </Box>
        )}

        {/* ─── All Compliance Reports Dialog ───
            Lists every per-file compliance report so the user can still reach
            them after leaving the upload step. Each card opens the detail
            dialog above via setActiveComplianceReport. */}
        <Dialog
          open={showAllComplianceDialog}
          onClose={() => setShowAllComplianceDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
        >
          <DialogTitle sx={{
            background: `linear-gradient(135deg, ${brand.orange} 0%, ${brand.yellow} 100%)`,
            color: '#fff',
            py: 2,
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ShieldIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Compliance Results
                </Typography>
                <Chip
                  size="small"
                  label={allComplianceReports.length}
                  sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700 }}
                />
              </Box>
              <IconButton
                edge="end"
                sx={{ color: '#fff' }}
                onClick={() => setShowAllComplianceDialog(false)}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 2.5, bgcolor: brand.dropZoneBg }}>
            {allComplianceReports.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: brand.textMuted }}>
                <Typography variant="body2">No compliance results yet.</Typography>
              </Box>
            ) : (
              allComplianceReports.map((item) => (
                <Box key={item.fileId} sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600, color: brand.textPrimary }}
                  >
                    {item.fileName}
                  </Typography>
                  <ComplianceReportCard
                    report={item.report}
                    onViewDetails={() => {
                      setActiveComplianceReport(item.report);
                      setShowAllComplianceDialog(false);
                      setShowComplianceReport(true);
                    }}
                    defaultExpanded={false}
                  />
                </Box>
              ))
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: `1px solid ${brand.borderSubtle}`, p: 2 }}>
            <Button
              onClick={() => setShowAllComplianceDialog(false)}
              sx={{ color: brand.textSecondary }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Confirmation Dialog */}
        <Dialog
          open={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <SuccessIcon />
            <Typography variant="h6">{tString('documentProcessing.successDialog.title')}</Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" gutterBottom sx={{ fontSize: '1.1rem', mb: 3, textAlign: 'center' }}>
                {tString('documentProcessing.successDialog.message')}
              </Typography>

              {lastTransaction && (
                <Box sx={{ mb: 3, p: 2, bgcolor: `${theme.palette.success.main}18`, borderRadius: 2, border: `1px solid ${theme.palette.success.main}50` }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.success.dark }}>
                    {tString('documentProcessing.successDialog.transactionDetails')}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                        <Typography variant="body2" color="textSecondary">{tString('documentProcessing.successDialog.module')}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{lastTransaction.moduleName}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                        <Typography variant="body2" color="textSecondary">{tString('documentProcessing.successDialog.filesProcessed')}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{lastTransaction.filesProcessed}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                        <Typography variant="body2" color="textSecondary">{tString('documentProcessing.successDialog.transactionId')}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {lastTransaction.transactionId}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
                {tString('documentProcessing.successDialog.processAnother')}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0, gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowSuccessDialog(false);
                navigate('/process');
              }}
              startIcon={<HomeIcon />}
              sx={{ minWidth: 150 }}
            >
              {tString('documentProcessing.successDialog.goToDashboard')}
            </Button>
            <Button
              variant="outlined"
              color="success"
              onClick={() => {
                setShowSuccessDialog(false);
                navigate(`/process/export-status?moduleId=${processing.selectedModule?.id || ''}`);
              }}
              startIcon={<OpenInNewIcon />}
              sx={{ minWidth: 150 }}
            >
              Export Status
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setShowSuccessDialog(false);
                // Reset the processing page back to the upload step.
                handleResetForm();
                toast.info(tString('documentProcessing.toasts.readyForNext'));
              }}
              startIcon={<RefreshIcon />}
              sx={{
                minWidth: 150,
                background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
              }}
            >
              {tString('documentProcessing.successDialog.processAnotherButton')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Auth Required Dialog for BEO Sanctions */}
        <Dialog
          open={authRequiredDialog.open}
          onClose={() => setAuthRequiredDialog({ ...authRequiredDialog, open: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: theme.palette.warning.light, color: '#fff' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <WarningIcon />
              <Typography variant="h6" component="span">
                {tString('documentProcessing.errors.authenticationRequired')}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {authRequiredDialog.message}
            </Alert>
            <Typography variant="body2" color="textSecondary">
              {tString('documentProcessing.errors.complianceAuthRequired')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setAuthRequiredDialog({ ...authRequiredDialog, open: false });
                // Continue without compliance check
                toast.info('Continuing without compliance screening');
              }}
              color="inherit"
            >
              {tString('documentProcessing.errors.continueWithout')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Security />}
              onClick={() => {
                // Close this dialog
                setAuthRequiredDialog({ ...authRequiredDialog, open: false });

                // Navigate to profile page
                navigate('/profile');
                toast.info('Please authenticate with BEO Sanctions in your Profile page');
              }}
            >
              {tString('documentProcessing.errors.authenticateNow')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Load from History confirmation dialog */}
        <Dialog
          open={showHistoryConfirmDialog}
          onClose={() => !isLoadingHistory && setShowHistoryConfirmDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            {tString('atlasFields.loadFromHistory')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Do you want to fill the form with values from your previous submissions?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Only empty fields will be filled. Fields already populated from the document will not be changed.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowHistoryConfirmDialog(false)}
              disabled={isLoadingHistory}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleLoadFromHistory}
              disabled={isLoadingHistory}
              startIcon={isLoadingHistory ? <CircularProgress size={16} color="inherit" /> : <HistoryIcon />}
            >
              {isLoadingHistory ? 'Loading...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ML Predictions FAB - DISABLED (using inline MLPredictionCell instead) */}
        {/* {processing.extractedData && processing.selectedModule?.name?.toLowerCase().includes('atlas') && (
        <Fab
          color="primary"
          onClick={() => setShowMLPredictionsSidebar(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
            },
          }}
        >
          <Badge
            badgeContent={mlPredictions ?
              Object.keys(mlPredictions.header || {}).length +
              Object.keys(mlPredictions.position || mlPredictions.positions?.[0] || {}).length
              : 0
            }
            color="error"
            max={999}
          >
            <PsychologyIcon />
          </Badge>
        </Fab>
      )} */}

        {/* ML Predictions Sidebar - DISABLED (using inline MLPredictionCell instead) */}
        {/* <MLPredictionsSidebar
        open={showMLPredictionsSidebar}
        onClose={() => setShowMLPredictionsSidebar(false)}
        predictions={mlPredictions}
        headerData={processing.extractedData?.kopf || {}}
        positionsData={processing.extractedData?.position || []}
        onApplyPrediction={handleApplyMLPrediction}
        onApplyAll={handleApplyAllMLPredictions}
        appliedPredictions={appliedPredictions}
        selectedPositionIndex={selectedPositionIndex}
        onPositionChange={setSelectedPositionIndex}
      /> */}
      </Box>
    </>
  );
};

export default DocumentProcessing;
